import { useState, useEffect, useRef, Suspense, lazy } from "react";
import { toast } from "react-toastify";
import "./UserDashboard.css";
import {
  supabase,
  getSafeSession,
  getCachedSession,
  getCachedProfile,
  setCachedProfile,
  clearSupabaseAuthStorage,
} from "../supabaseClient";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  Spinner,
  Modal,
  Button,
  ListGroup,
  Row,
  Col,
  Dropdown,
} from "react-bootstrap";
import AIReadinessGauge from "./Components/AIReadinessGauge";
import AnalyticsHeader from "./Components/AnalyticsHeader";
import SearchConsoleHeader from "./Components/SearchConsoleHeader";
import DashboardSkeleton from "./Components/DashboardSkeleton";
import TabSkeleton from "./Components/TabSkeleton";
import { safeCopyToClipboard } from "../clipboardUtils";
import {
  FiGrid,
  FiFileText,
  FiAlertTriangle,
  FiCalendar,
  FiPlus,
  FiChevronRight,
  FiChevronLeft,
  FiChevronDown,
  FiInfo,
  FiClock,
  FiMenu,
  FiDatabase,
  FiLayers,
  FiSearch,
  FiZap,
  FiLink,
  FiUser,
  FiGlobe,
  FiUserCheck,
  FiLogOut,
  FiCopy,
  FiDownload,
  FiCheck,
  FiSun,
  FiMoon,
  FiLock,
  FiCheckCircle,
  FiX,
  FiShield,
  FiHelpCircle,
} from "react-icons/fi";
import { LuSparkle } from "react-icons/lu";

const AIKnowledgeEngine = lazy(() => import("./Components/AIKnowledgeEngine"));
const GoogleSearchConsole = lazy(() => import("./Components/GoogleSearchConsole"));
const GoogleAnalytics = lazy(() => import("./Components/GoogleAnalytics"));
const LegacyGA4 = lazy(() => import("./Components/LegacyGA4"));
const LegacyGSC = lazy(() => import("./Components/LegacyGSC"));
const HelpSupport = lazy(() => import("./HelpSupport"));
const MonthlyReportHub = lazy(() => import("./Components/MonthlyReport/MonthlyReportHub"));
const InternalLinkingHub = lazy(() => import("./Components/InternalLinkingHub"));

let stripePromiseCache = null;
const getStripe = async () => {
  if (stripePromiseCache) {
    return stripePromiseCache;
  }
  let pubKey = null;
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/stripe-publishable-key`,
      { timeout: 10000 },
    );
    pubKey = response.data?.publishableKey;
  } catch (err) {
    console.warn(
      "Failed to load Stripe key dynamically from backend, falling back to env:",
      err,
    );
  }
  if (!pubKey) {
    pubKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  }
  if (pubKey) {
    stripePromiseCache = loadStripe(pubKey);
    return stripePromiseCache;
  }
  return null;
};

const PLAN_NAMES = {
  core: "SeenByAI Core",
  growth: "SeenByAI Growth",
  expansion: "SeenByAI Expansion",
  agency: "SeenByAI Growth Agency",
};

export const DATE_RANGE_PRESETS = [
  { id: "7d", label: "Last 7 Days", days: 7, trendKey: "7 Days" },
  { id: "30d", label: "Last 30 Days", days: 30, trendKey: "30 Days" },
  { id: "90d", label: "Last 90 Days", days: 90, trendKey: "90 Days" },
  { id: "all", label: "All Time", days: null, trendKey: "All Time" },
];

export const formatPresetDateRange = (days) => {
  if (!days) return "All historical data";
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));

  const startMonth = start.toLocaleDateString("en-US", { month: "short" });
  const startDay = start.getDate();
  const startYear = start.getFullYear();

  const endMonth = end.toLocaleDateString("en-US", { month: "short" });
  const endDay = end.getDate();
  const endYear = end.getFullYear();

  if (startYear !== endYear) {
    return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
  }
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`;
};

const CATEGORY_EFFECTIVE_WEIGHTS = {
  "Schema Markup (JSON-LD)": 26.1,
  "Open Graph Tags": 8.1,
  "llms.txt": 5.85,
  "Core Web Vitals (LCP + INP + CLS, combined)": 5.0,
  "Twitter Cards": 3.6,
  "Freshness indicators present": 3.13,
  "AI-friendly summaries per service": 3.13,
  "Content depth sufficient (300+ words/page)": 3.13,
  "Proper heading structure (H1/H2/H3)": 3.13,
  "Dedicated individual service pages": 3.13,
  "FAQ content present (Content Quality)": 3.13,
  "Question-based content present": 3.13,
  "Multilingual content (if applicable)": 3.13,
  "Lazy loading images": 2.0,
  "Contextual links present": 2.0,
  "Descriptive anchor text used": 2.0,
  "Hub-spoke structure detectable": 2.0,
  "Breadcrumbs present": 2.0,
  "WebP image format": 1.5,
  "AI Meta Tags (ai:*)": 1.35,
  "JS / CSS minification": 1.0,
  HTTPS: 1.4,
  "Core Web Vitals detectability (Technical SEO check)": 1.4,
  "Alt text on images": 1.4,
  "Canonical tags": 1.4,
  "Robots.txt + Sitemap present": 1.4,
  "CTA clearly visible": 1.0,
  "Trust signals present": 1.0,
  "Testimonials displayed": 1.0,
  "Contact info prominent": 1.0,
  "FAQ section present (User Engagement)": 1.0,
  "Caching headers": 0.5,
};

const SUBCHECK_MAPPINGS = [
  // --- CRITICAL (effective weight >= 5.0%) ---
  {
    key: "graph_multiple_nodes",
    pattern:
      /Nested @graph structure with multiple|nested @graph|graph_multiple_nodes/i,
    name: "Schema nested @graph structure",
    category: "Schema Markup (JSON-LD)",
    type: "Critical",
    impact: "High",
    scoreImpact: "-5.22%",
  },
  {
    key: "specific_industry_type",
    pattern:
      /No specific industry type|specific_industry_type|generic types were found/i,
    name: "Schema specific industry type",
    category: "Schema Markup (JSON-LD)",
    type: "Critical",
    impact: "High",
    scoreImpact: "-5.22%",
  },
  {
    key: "has_offer_catalog_with_urls",
    pattern: /hasOfferCatalog|offerCatalog/i,
    name: "Schema hasOfferCatalog",
    category: "Schema Markup (JSON-LD)",
    type: "Critical",
    impact: "High",
    scoreImpact: "-5.22%",
  },
  {
    key: "individual_offices_with_parent",
    pattern:
      /individual office|individual_offices_with_parent|parentOrganization/i,
    name: "Schema individual office nodes",
    category: "Schema Markup (JSON-LD)",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-3.92%",
  },
  {
    key: "main_entity_of_page",
    pattern: /mainEntityOfPage/i,
    name: "Schema mainEntityOfPage",
    category: "Schema Markup (JSON-LD)",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-2.61%",
  },
  {
    key: "same_as_min_3_urls",
    pattern: /sameAs/i,
    name: "Schema sameAs URLs",
    category: "Schema Markup (JSON-LD)",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-2.61%",
  },
  {
    key: "price_range",
    pattern: /priceRange/i,
    name: "Schema priceRange",
    category: "Schema Markup (JSON-LD)",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-1.31%",
  },
  {
    key: "og_tags",
    pattern: /Open Graph|og:/i,
    name: "Open Graph Tags",
    category: "Open Graph Tags",
    type: "Critical",
    impact: "High",
    scoreImpact: "-8.1%",
  },
  {
    key: "llms_txt",
    pattern: /llms\.txt|llms/i,
    name: "llms.txt",
    category: "llms.txt",
    type: "Critical",
    impact: "High",
    scoreImpact: "-5.85%",
  },
  {
    key: "core_web_vitals",
    pattern: /Core Web Vitals metrics are not optimized|LCP|INP|CLS/i,
    name: "Core Web Vitals (LCP + INP + CLS, combined)",
    category: "Core Web Vitals (LCP + INP + CLS, combined)",
    type: "Critical",
    impact: "High",
    scoreImpact: "-5.0%",
  },

  // --- WARNING (effective weight 1.5% - 4.99%) ---
  {
    key: "twitter_cards",
    pattern: /Twitter Card/i,
    name: "Twitter Cards",
    category: "Twitter Cards",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-3.6%",
  },
  {
    key: "freshness_indicators",
    pattern: /freshness|updated dates|modified times/i,
    name: "Freshness indicators present",
    category: "Freshness indicators present",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-3.13%",
  },
  {
    key: "ai_friendly_summaries",
    pattern: /AI-friendly summaries|self-contained summaries/i,
    name: "AI-friendly summaries per service",
    category: "AI-friendly summaries per service",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-3.13%",
  },
  {
    key: "content_depth",
    pattern: /content depth|300\+ words/i,
    name: "Content depth sufficient (300+ words/page)",
    category: "Content depth sufficient (300+ words/page)",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-3.13%",
  },
  {
    key: "heading_structure",
    pattern: /heading structure|heading hierarchy|H1|H2|H3/i,
    name: "Proper heading structure (H1/H2/H3)",
    category: "Proper heading structure (H1/H2/H3)",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-3.13%",
  },
  {
    key: "dedicated_service_pages",
    pattern: /dedicated service pages/i,
    name: "Dedicated individual service pages",
    category: "Dedicated individual service pages",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-3.13%",
  },
  {
    key: "faq_content",
    pattern: /FAQ page schema|FAQ headings/i,
    name: "FAQ content present (Content Quality)",
    category: "FAQ content present (Content Quality)",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-3.13%",
  },
  {
    key: "question_based_content",
    pattern: /question-based content/i,
    name: "Question-based content present",
    category: "Question-based content present",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-3.13%",
  },
  {
    key: "multilingual_content",
    pattern: /multilingual|HTML lang|hreflang/i,
    name: "Multilingual content (if applicable)",
    category: "Multilingual content (if applicable)",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-3.13%",
  },
  {
    key: "lazy_loading",
    pattern: /lazy loading|lazy-loading/i,
    name: "Lazy loading images",
    category: "Lazy loading images",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-2.5%",
  },
  {
    key: "contextual_links_present",
    pattern: /contextual interlinks|contextual links/i,
    name: "Contextual links present",
    category: "Contextual links present",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-2.0%",
  },
  {
    key: "descriptive_anchor_text_used",
    pattern: /anchor text/i,
    name: "Descriptive anchor text used",
    category: "Descriptive anchor text used",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-2.0%",
  },
  {
    key: "hub_spoke_structure_detectable",
    pattern: /hub-spoke|hub-and-spoke/i,
    name: "Hub-spoke structure detectable",
    category: "Hub-spoke structure detectable",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-2.0%",
  },
  {
    key: "breadcrumbs_present",
    pattern: /breadcrumbs/i,
    name: "Breadcrumbs present",
    category: "Breadcrumbs present",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-2.0%",
  },
  {
    key: "webp_images",
    pattern: /WebP|modern format images/i,
    name: "WebP image format",
    category: "WebP image format",
    type: "Warning",
    impact: "Medium",
    scoreImpact: "-2.5%",
  },

  // --- NOTICE (effective weight < 1.5%) ---
  {
    key: "ai_meta_tags",
    pattern: /AI Meta Tags|ai:agent|ai:\*/i,
    name: "AI Meta Tags (ai:*)",
    category: "AI Meta Tags (ai:*)",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-1.35%",
  },
  {
    key: "minification_of_js_css",
    pattern: /minification|minify|minified/i,
    name: "JS / CSS minification",
    category: "JS / CSS minification",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-2.5%",
  },
  {
    key: "https",
    pattern: /HTTPS|securely over HTTPS/i,
    name: "HTTPS",
    category: "HTTPS",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-1.4%",
  },
  {
    key: "core_web_vitals",
    pattern: /Core Web Vitals detectability|Technical SEO check/i,
    name: "Core Web Vitals detectability (Technical SEO check)",
    category: "Core Web Vitals detectability (Technical SEO check)",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-1.4%",
  },
  {
    key: "alt_text",
    pattern: /Alt text|alt attributes/i,
    name: "Alt text on images",
    category: "Alt text on images",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-1.4%",
  },
  {
    key: "canonical_tags",
    pattern: /Canonical tags/i,
    name: "Canonical tags",
    category: "Canonical tags",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-1.4%",
  },
  {
    key: "robots_txt_sitemap",
    pattern: /Robots\.txt|Sitemap/i,
    name: "Robots.txt + Sitemap present",
    category: "Robots.txt + Sitemap present",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-1.4%",
  },
  {
    key: "cta_clearly_visible",
    pattern: /CTA|call-to-action/i,
    name: "CTA clearly visible",
    category: "CTA clearly visible",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-1.0%",
  },
  {
    key: "trust_signals_present",
    pattern: /Trust signals/i,
    name: "Trust signals present",
    category: "Trust signals present",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-1.0%",
  },
  {
    key: "testimonials_displayed",
    pattern: /testimonials|reviews/i,
    name: "Testimonials displayed",
    category: "Testimonials displayed",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-1.0%",
  },
  {
    key: "contact_info_prominent",
    pattern: /Contact info|Contact information/i,
    name: "Contact info prominent",
    category: "Contact info prominent",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-1.0%",
  },
  {
    key: "faq_section_present",
    pattern: /FAQ section is missing/i,
    name: "FAQ section present (User Engagement)",
    category: "FAQ section present (User Engagement)",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-1.0%",
  },
  {
    key: "caching_headers",
    pattern: /Caching headers|Cache-Control/i,
    name: "Caching headers",
    category: "Caching headers",
    type: "Notice",
    impact: "Low",
    scoreImpact: "-2.5%",
  },
];

const getDynamicPoints = (mapped) => {
  if (!mapped) return 0;
  const category = mapped.category;
  if (!category) return Math.abs(parseFloat(mapped.scoreImpact || 0));
  const weight = CATEGORY_EFFECTIVE_WEIGHTS[category];
  if (weight === undefined)
    return Math.abs(parseFloat(mapped.scoreImpact || 0));
  const count = SUBCHECK_MAPPINGS.filter((m) => m.category === category).length;
  if (count === 0) return 0;
  return parseFloat((weight / count).toFixed(2));
};

export default function UserDashboard() {
  const getCrawledPagesList = (scan) => {
    if (!scan) return [];

    if (
      scan.scan_data?.crawled_urls &&
      Array.isArray(scan.scan_data.crawled_urls) &&
      scan.scan_data.crawled_urls.length > 0
    ) {
      return scan.scan_data.crawled_urls;
    }
    if (
      scan.scan_data?.raw?.crawled_urls &&
      Array.isArray(scan.scan_data.raw.crawled_urls) &&
      scan.scan_data.raw.crawled_urls.length > 0
    ) {
      return scan.scan_data.raw.crawled_urls;
    }
    if (
      scan.crawled_urls &&
      Array.isArray(scan.crawled_urls) &&
      scan.crawled_urls.length > 0
    ) {
      return scan.crawled_urls;
    }
    if (
      scan.scan_data?.crawled_pages &&
      Array.isArray(scan.scan_data.crawled_pages) &&
      scan.scan_data.crawled_pages.length > 0
    ) {
      return scan.scan_data.crawled_pages;
    }
    if (
      scan.scan_data?.raw?.crawled_pages &&
      Array.isArray(scan.scan_data.raw.crawled_pages) &&
      scan.scan_data.raw.crawled_pages.length > 0
    ) {
      return scan.scan_data.raw.crawled_pages;
    }
    if (
      scan.crawled_pages &&
      Array.isArray(scan.crawled_pages) &&
      scan.crawled_pages.length > 0
    ) {
      return scan.crawled_pages;
    }

    if (
      scan.website &&
      typeof scan.website === "string" &&
      scan.website.trim().length > 0
    ) {
      const formatted = scan.website.startsWith("http")
        ? scan.website
        : `https://${scan.website}`;
      return [formatted];
    }

    return [];
  };

  const cleanUrlJS = (url) => {
    if (!url) return "";
    let cleaned = url.trim().toLowerCase();
    if (cleaned.startsWith("http://")) cleaned = cleaned.slice(7);
    else if (cleaned.startsWith("https://")) cleaned = cleaned.slice(8);
    if (cleaned.startsWith("www.")) cleaned = cleaned.slice(4);
    if (cleaned.endsWith("/")) cleaned = cleaned.slice(0, -1);
    return cleaned;
  };

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [, setLoading] = useState(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [userProfile, setUserProfile] = useState(() => {
    const s = getCachedSession();
    return s?.user?.id ? getCachedProfile(s.user.id) : null;
  });
  const userPlanKey = (userProfile?.plan || "").toLowerCase();
  const hasPaidPlan = ["core", "growth", "expansion", "agency"].includes(
    userPlanKey,
  );
  const currentPlanLabel = hasPaidPlan ? PLAN_NAMES[userPlanKey] : "No plan";
  const [userData, setUserData] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    confirmText: "Disconnect",
    confirmVariant: "danger",
    onConfirm: null,
  });

  const triggerConfirmation = (
    title,
    message,
    onConfirm,
    confirmText = "Disconnect",
    confirmVariant = "danger",
  ) => {
    setConfirmModal({
      show: true,
      title,
      message,
      confirmText,
      confirmVariant,
      onConfirm,
    });
  };
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [timeRange, setTimeRange] = useState("30 Days");
  const [selectedWebsite, setSelectedWebsite] = useState("all");
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Custom Header Dropdowns State
  const [websiteDropdownOpen, setWebsiteDropdownOpen] = useState(false);
  const [websiteSearchQuery, setWebsiteSearchQuery] = useState("");
  const websiteDropdownRef = useRef(null);

  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("30d");
  const dateDropdownRef = useRef(null);

  // Close dropdowns on outside click or Escape key
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        websiteDropdownRef.current &&
        !websiteDropdownRef.current.contains(e.target)
      ) {
        setWebsiteDropdownOpen(false);
      }
      if (
        dateDropdownRef.current &&
        !dateDropdownRef.current.contains(e.target)
      ) {
        setDateDropdownOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setWebsiteDropdownOpen(false);
        setDateDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Agency Plan States
  const [agencyClients, setAgencyClients] = useState([]);
  const [agencyAllowance, setAgencyAllowance] = useState(null);
  const [agencyLoading, setAgencyLoading] = useState(false);
  const [newClientUrl, setNewClientUrl] = useState("");

  // ── Light/Dark Theme State ──
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    if (theme === "light") {
      document.body.classList.add("light-mode");
      document.body.classList.remove("dark-mode");
    } else {
      document.body.classList.add("dark-mode");
      document.body.classList.remove("light-mode");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  // ── Audit Report View States ──
  const [activeTab, setActiveTab] = useState("dashboard"); // "dashboard" | "report"
  const [issueFilter, setIssueFilter] = useState("all");
  const [issueSearch, setIssueSearch] = useState("");
  const [selectedRecIndex, setSelectedRecIndex] = useState(0);
  const [codeTab, setCodeTab] = useState("json-ld"); // "json-ld" | "raw" "
  const [copiedCode, setCopiedCode] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [propertiesList, setPropertiesList] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showPropertySelect, setShowPropertySelect] = useState(false);
  const [breakdownData, setBreakdownData] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  // ── Google Search Console & Analytics States & Handlers ──
  const [activeIntegrationTab, setActiveIntegrationTab] = useState("analytics"); // "analytics" | "search-console"
  const [gscSitesList, setGscSitesList] = useState([]);
  const [gscData, setGscData] = useState(null);
  const [gscLoading, setGscLoading] = useState(false);
  const [gscError, setGscError] = useState(null);
  const [gscPeriodDays, setGscPeriodDays] = useState(28);
  const [gaPeriodDays, setGaPeriodDays] = useState(30);
  const [showGscSiteSelect, setShowGscSiteSelect] = useState(false);
  const [gscSiteUrl, setGscSiteUrl] = useState(null);
  const [ga4Property, setGa4Property] = useState(null);
  const [ga4PropertyId, setGa4PropertyId] = useState(null);

  // ── GA & GSC View Modes (AI vs Legacy) ──
  const [gaMode, setGaMode] = useState(() => {
    return localStorage.getItem("seenbyai_ga_mode") || "legacy";
  });
  const [gscMode, setGscMode] = useState(() => {
    return localStorage.getItem("seenbyai_gsc_mode") || "legacy";
  });

  const handleSetGaMode = (mode) => {
    setGaMode(mode);
    localStorage.setItem("seenbyai_ga_mode", mode);
    if (mode === "legacy" && !analyticsData && googleAnalyticsConnected) {
      fetchGoogleAnalyticsData(getModifiedProfileForClient());
    }
  };

  const handleSetGscMode = (mode) => {
    setGscMode(mode);
    localStorage.setItem("seenbyai_gsc_mode", mode);
    if (mode === "legacy" && !gscData && gscActive) {
      fetchGoogleSearchConsoleData(getModifiedProfileForClient());
    }
  };

  // Handle selected client website or active tab change (Agency only)
  useEffect(() => {
    setSelectedScan(null);
    if (
      userPlanKey === "agency" &&
      selectedWebsite &&
      selectedWebsite !== "all"
    ) {
      const client = agencyClients.find(
        (c) => cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite),
      );
      const effectiveRefreshToken =
        client?.client_google_refresh_token ||
        null;
      const effectivePropId = client?.property_id || null;
      const effectivePropName = client?.ga4_property || null;
      const effectiveGscSite = client?.gsc_site_url || null;

      const modifiedProfile = {
        ...userProfile,
        website: client?.website || selectedWebsite,
        property_id: effectivePropId,
        ga4_property: effectivePropName,
        gsc_site_url: effectiveGscSite,
        google_search_console_connected: !!effectiveGscSite,
        google_refresh_token: effectiveRefreshToken,
        google_analytics_connected: !!(effectiveRefreshToken && effectivePropId),
      };
      setGscSiteUrl(effectiveGscSite);
      setGa4Property(effectivePropName);
      setGa4PropertyId(effectivePropId);
      setShowPropertySelect(false);
      setShowGscSiteSelect(false);

      // Immediately clear stale analytics so the previous website's data never lingers
      setAnalyticsData(null);
      setBreakdownData(null);

      if (effectivePropId && effectiveRefreshToken) {
        setAnalyticsLoading(true);
        setBreakdownLoading(true);
      }

      const targetWeb = client?.website || selectedWebsite;
      if (activeTab === "analytics") {
        fetchGoogleAnalyticsData(modifiedProfile, null, gaPeriodDays, targetWeb, effectivePropId);
      } else if (activeTab === "search-console") {
        fetchGoogleSearchConsoleData(modifiedProfile, null, null);
      } else {
        // Fetch both to keep metrics ready
        fetchGoogleAnalyticsData(modifiedProfile, null, gaPeriodDays, targetWeb, effectivePropId);
        fetchGoogleSearchConsoleData(modifiedProfile, null, null);
      }
    } else if (selectedWebsite === "all" || !selectedWebsite) {
      setGa4Property(userProfile?.ga4_property || null);
      setGa4PropertyId(userProfile?.property_id || null);
      setGscSiteUrl(userProfile?.gsc_site_url || null);
      setAnalyticsData(null);
      setBreakdownData(null);
      if (userProfile?.property_id && (userProfile?.google_refresh_token || userProfile?.google_analytics_connected)) {
        fetchGoogleAnalyticsData(userProfile, null, gaPeriodDays, null, userProfile.property_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedWebsite,
    activeTab,
    agencyClients,
    userPlanKey,
    userProfile,
    gaPeriodDays,
  ]);

  const formatFriendlyGscError = (rawDetail, siteUrl) => {
    if (!rawDetail) {
      return "Unable to load Google Search Console metrics. Please try again.";
    }

    const cleanSite = siteUrl || "this site";
    let msg = typeof rawDetail === "string" ? rawDetail : JSON.stringify(rawDetail);
    const lower = msg.toLowerCase();

    // 1. Permission / 403 / Forbidden
    if (
      lower.includes("sufficient permission") ||
      lower.includes("forbidden") ||
      lower.includes("code: 403") ||
      lower.includes('"code": 403') ||
      lower.includes("permission denied")
    ) {
      return `You do not have permission to access Search Console data for "${cleanSite}". Please select a verified property in Google Search Console.`;
    }

    // 2. Token / Session / Invalid Grant / 401
    if (
      lower.includes("invalid_grant") ||
      lower.includes("token has expired") ||
      lower.includes("session has expired") ||
      lower.includes("invalid credentials") ||
      lower.includes("code: 401") ||
      lower.includes('"code": 401')
    ) {
      return "Your Google Search Console connection has expired. Please reconnect your Google account.";
    }

    // 3. Not Found / 404
    if (
      lower.includes("not found") ||
      lower.includes("code: 404") ||
      lower.includes('"code": 404')
    ) {
      return `Property "${cleanSite}" was not found in your Google Search Console account. Please choose a verified property.`;
    }

    // 4. Rate Limit / Quota / 429
    if (
      lower.includes("quota") ||
      lower.includes("rate limit") ||
      lower.includes("code: 429") ||
      lower.includes('"code": 429')
    ) {
      return "Google Search Console request limit reached. Please wait a few moments before trying again.";
    }

    // 5. Clean Google API string prefixes
    if (msg.startsWith("Google API error:")) {
      msg = msg.replace("Google API error:", "").trim();
    }
    if (msg.startsWith("Google Search Console API error:")) {
      msg = msg.replace("Google Search Console API error:", "").trim();
    }

    // If JSON object or stringified JSON, parse message
    if (msg.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(msg);
        if (parsed?.error?.message) {
          msg = parsed.error.message;
        }
      } catch {
        // ignore
      }
    }

    // Strip technical URLs
    if (msg.includes("See also:")) {
      msg = msg.split("See also:")[0].trim();
    }

    // Filter out any lingering raw JSON code brackets
    if (msg.includes("{") || msg.includes("}")) {
      return `Unable to access Search Console metrics for "${cleanSite}". Please verify your property permissions.`;
    }

    return msg.trim() || "Failed to load Google Search Console metrics.";
  };

  const fetchGoogleSearchConsoleData = async (
    profile,
    daysParam,
    sessionPassed = null,
  ) => {
    const googleConnected = !!(
      profile?.google_analytics_connected ||
      profile?.google_refresh_token ||
      profile?.google_search_console_connected
    );
    const resolvedSiteUrl = profile?.gsc_site_url || gscSiteUrl;
    const targetDays = daysParam !== undefined && daysParam !== null ? daysParam : gscPeriodDays;

    if (!googleConnected) {
      setGscData(null);
      setGscError(null);
      return;
    }

    setGscLoading(true);
    setGscError(null);
    let metricsErrorOccurred = false;

    try {
      let session = sessionPassed;
      if (!session) {
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();
        session = currentSession;
      }
      if (!session) return;

      const headers = { Authorization: `Bearer ${session.access_token}` };

      if (resolvedSiteUrl) {
        try {
          let url = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/auth/google/gsc/metrics?days=${targetDays}`;
          if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
            url += `&website=${encodeURIComponent(selectedWebsite)}`;
          }
          const res = await axios.get(url, { headers });
          setGscData(res.data);
          setGscError(null);
        } catch (mErr) {
          console.error("Error loading GSC metrics:", mErr);
          metricsErrorOccurred = true;
          const rawDetail =
            mErr.response?.data?.detail ||
            mErr.message ||
            "Failed to load Google Search Console metrics.";
          const friendlyMsg = formatFriendlyGscError(rawDetail, resolvedSiteUrl);
          toast.error(friendlyMsg);
          setGscError({
            message: friendlyMsg,
            status: mErr.response?.status,
            isPermissionError:
              mErr.response?.status === 403 ||
              friendlyMsg.toLowerCase().includes("permission"),
            siteUrl: resolvedSiteUrl,
          });
          setGscData(null);
          if (mErr.response?.status === 401 || rawDetail.toLowerCase().includes("reconnect") || rawDetail.toLowerCase().includes("expired")) {
            if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
              setAgencyClients((prev) =>
                prev.map((c) =>
                  cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite)
                    ? { ...c, client_google_refresh_token: null, client_google_access_token: null }
                    : c
                )
              );
            } else {
              setUserProfile((prev) =>
                prev ? { ...prev, google_search_console_connected: false, google_refresh_token: null } : prev
              );
            }
          }
        }
      }

      try {
        let url = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/auth/google/gsc/sites`;
        if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
          url += `?website=${encodeURIComponent(selectedWebsite)}`;
        }
        const res = await axios.get(url, { headers });
        setGscSitesList(res.data?.sites || []);
      } catch (sErr) {
        console.error("Error loading GSC sites:", sErr);
        const rawDetail =
          sErr.response?.data?.detail ||
          sErr.message ||
          "Failed to load Google Search Console sites.";
        // Avoid double toasting if metrics already reported an auth or permission error
        if (!metricsErrorOccurred) {
          const friendlyMsg = formatFriendlyGscError(rawDetail, resolvedSiteUrl);
          toast.error(friendlyMsg);
        }
      }
    } catch (err) {
      console.error("Error fetching Google Search Console data:", err);
    } finally {
      setGscLoading(false);
    }
  };

  const fetchAgencyClients = async (sessionPassed = null) => {
    try {
      let session = sessionPassed;
      if (!session) {
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();
        session = currentSession;
      }
      if (!session) return;

      const headers = { Authorization: `Bearer ${session.access_token}` };
      const [clientsRes, allowanceRes] = await Promise.allSettled([
        axios.get(
          `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/agency/clients`,
          { headers },
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/agency/allowance`,
          { headers },
        ),
      ]);

      if (clientsRes.status === "fulfilled") {
        const rawClients = clientsRes.value.data || [];
        const cleanClients = rawClients.filter(
          (c) => c && c.website && !c.website.trim().startsWith("{")
        );
        setAgencyClients(cleanClients);

        // Default selectedWebsite if not set or equal to all or corrupted
        if (
          cleanClients.length > 0 &&
          (!selectedWebsite || selectedWebsite === "all" || selectedWebsite.trim().startsWith("{"))
        ) {
          setSelectedWebsite(cleanClients[0].website);
        }
      }

      if (allowanceRes.status === "fulfilled" && allowanceRes.value.data) {
        setAgencyAllowance(allowanceRes.value.data);
        setUserProfile((prev) => {
          if (!prev) return prev;
          const updated = {
            ...prev,
            extra_clients: allowanceRes.value.data.extra_clients,
          };
          setCachedProfile(session.user.id, updated);
          return updated;
        });
      }
    } catch (err) {
      console.error("Failed to fetch agency clients:", err);
    }
  };

  const executeAddAgencyClient = async (websiteVal) => {
    setAgencyLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const headers = { Authorization: `Bearer ${session.access_token}` };
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/agency/clients/checkout-session`,
        { website: websiteVal },
        { headers },
      );

      if (response.data.checkout_required) {
        toast.info("Redirecting to Stripe checkout...");
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          const stripe = await getStripe();
          if (stripe && response.data.id) {
            await stripe.redirectToCheckout({
              sessionId: response.data.id,
            });
          } else {
            toast.error("Failed to initialize checkout session.");
          }
        }
      } else {
        if (response.data.is_first_client) {
          toast.success(
            "Successfully added your 1st client website (included in base plan)!",
          );
        } else {
          toast.success("Successfully added client website (Included without paying)!");
        }
        setNewClientUrl("");
        setSelectedWebsite(websiteVal);
        await fetchAgencyClients(session);
      }
    } catch (err) {
      console.error("Error adding client:", err);
      toast.error(
        err.response?.data?.detail || "Failed to add client website.",
      );
    } finally {
      setAgencyLoading(false);
    }
  };

  const handleAddAgencyClient = async () => {
    let websiteVal = newClientUrl.trim();
    if (!websiteVal) {
      toast.error("Please enter a website URL.");
      return;
    }

    const complimentaryLimit = agencyAllowance
      ? agencyAllowance.complimentary_limit
      : 1 + (userProfile?.extra_clients || 0);
    const isExtraClient = agencyClients.length >= complimentaryLimit;
    if (isExtraClient) {
      triggerConfirmation(
        "Add Additional Client Website",
        `Adding '${websiteVal}' will add an additional company at $9.99/month (20 scans, 10 Schema, 10 Robots.txt, 10 LLMs.txt per 30-day billing cycle). Would you like to proceed to checkout?`,
        () => executeAddAgencyClient(websiteVal),
        "Proceed to Checkout",
        "primary",
      );
      return;
    }

    await executeAddAgencyClient(websiteVal);
  };

  const handleDeleteAgencyClient = (website) => {
    const compLimit = agencyAllowance
      ? agencyAllowance.complimentary_limit
      : 1 + (userProfile?.extra_clients || 0);
    const isPaid = agencyClients.length > compLimit;

    triggerConfirmation(
      "Remove Client Website",
      `Are you sure you want to remove the client website '${website}'? ${
        isPaid
          ? "This will cancel its $9.99/month recurring subscription immediately and remove its GA/GSC linked settings."
          : "This will free up its complimentary slot and remove its GA/GSC linked settings."
      }`,
      async () => {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session) {
            navigate("/auth");
            return;
          }

          const headers = { Authorization: `Bearer ${session.access_token}` };
          await axios.delete(
            `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/agency/clients?website=${encodeURIComponent(website)}`,
            { headers },
          );

          toast.success(
            isPaid
              ? "Client website removed and $9.99/mo subscription cancelled."
              : "Client website removed and complimentary slot freed up.",
          );

          // If we deleted the active client website, select another one or reset
          if (selectedWebsite === website) {
            setSelectedWebsite("");
          }

          // Refresh list
          await fetchAgencyClients(session);
        } catch (err) {
          console.error("Error deleting client:", err);
          toast.error(
            err.response?.data?.detail || "Failed to remove client website.",
          );
        }
      },
      "Remove Website",
      "danger",
    );
  };

  const handleSelectGSCSite = async (siteUrl) => {
    setActionLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const payload = { site_url: siteUrl };
      if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
        payload.website = selectedWebsite;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/auth/google/gsc/select-site`,
        payload,
        { headers: { Authorization: `Bearer ${session.access_token}` } },
      );

      if (res.data?.status === "success") {
        toast.success(`Successfully connected to GSC site: ${siteUrl}`);
        if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
          const updatedClients = agencyClients.map((c) => {
            if (cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite)) {
              return { ...c, gsc_site_url: siteUrl };
            }
            return c;
          });
          setAgencyClients(updatedClients);
          setGscSiteUrl(siteUrl);
          setShowGscSiteSelect(false);
          const client = updatedClients.find(
            (c) => cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite),
          );
          const modifiedProfile = {
            ...userProfile,
            property_id: client?.property_id || null,
            ga4_property: client?.ga4_property || null,
            gsc_site_url: siteUrl,
            google_search_console_connected: true,
            google_refresh_token: client?.client_google_refresh_token || null,
            google_analytics_connected: !!client?.client_google_refresh_token,
          };
          await fetchGoogleSearchConsoleData(modifiedProfile);
        } else {
          const updatedProfile = {
            ...userProfile,
            gsc_site_url: siteUrl,
            google_search_console_connected: true,
          };
          setUserProfile(updatedProfile);
          setGscSiteUrl(siteUrl);
          setShowGscSiteSelect(false);
          await fetchGoogleSearchConsoleData(updatedProfile);
        }
      } else {
        toast.error(res.data?.message || "Failed to select site.");
      }
    } catch (err) {
      console.error("Error selecting site:", err);
      toast.error(err.response?.data?.detail || "Failed to select site.");
    } finally {
      setActionLoading(false);
    }
  };

  const performDisconnectGSC = async () => {
    setActionLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const payload = { site_url: "" };
      if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
        payload.website = selectedWebsite;
      }

      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/auth/google/gsc/select-site`,
          payload,
          { headers: { Authorization: `Bearer ${session.access_token}` } },
        );
      } catch (err) {
        console.warn("Backend error clearing GSC site URL:", err);
      }

      if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
        const updatedClients = agencyClients.map((c) => {
          if (cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite)) {
            return { ...c, gsc_site_url: null };
          }
          return c;
        });
        setAgencyClients(updatedClients);
        setGscSiteUrl(null);
        setGscData(null);
        toast.success("Google Search Console disconnected successfully.");
        const client = updatedClients.find(
          (c) => cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite),
        );
        const modifiedProfile = {
          ...userProfile,
          property_id: client?.property_id || null,
          ga4_property: client?.ga4_property || null,
          gsc_site_url: null,
          google_search_console_connected: false,
          google_refresh_token: client?.client_google_refresh_token || null,
          google_analytics_connected: !!client?.client_google_refresh_token,
        };
        await fetchGoogleSearchConsoleData(modifiedProfile);
      } else {
        const updatedProfile = {
          ...userProfile,
          gsc_site_url: null,
          google_search_console_connected: false,
        };
        setUserProfile(updatedProfile);
        setGscSiteUrl(null);
        setGscData(null);
        toast.success("Google Search Console disconnected successfully.");
      }
    } catch (err) {
      console.error("GSC disconnect error:", err);
      toast.error("Failed to disconnect Google Search Console.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnectGSC = () => {
    triggerConfirmation(
      "Disconnect Google Search Console",
      "Are you sure you want to disconnect your Google Search Console integration?",
      performDisconnectGSC,
      "Disconnect",
      "danger",
    );
  };

  const performDisconnectGAProperty = async () => {
    setActionLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const payload = { property_id: "", property_name: "" };
      if (userPlanKey === "agency" && selectedWebsite) {
        payload.website = selectedWebsite;
      }

      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/auth/google/select-property`,
          payload,
          { headers: { Authorization: `Bearer ${session.access_token}` } },
        );
      } catch (err) {
        console.warn("Backend error clearing GA property:", err);
      }

      setGa4Property(null);
      setGa4PropertyId(null);

      if (userPlanKey === "agency" && selectedWebsite) {
        const updatedClients = agencyClients.map((c) => {
          if (c.website === selectedWebsite) {
            return { ...c, property_id: null, ga4_property: null };
          }
          return c;
        });
        setAgencyClients(updatedClients);
        setAnalyticsData(null);
        setBreakdownData(null);
        toast.success("Google Analytics property disconnected successfully.");
        const modifiedProfile = {
          ...userProfile,
          property_id: null,
          ga4_property: null,
        };
        await fetchGoogleAnalyticsData(modifiedProfile, null, gaPeriodDays);
      } else {
        const updatedProfile = {
          ...userProfile,
          property_id: null,
          ga4_property: null,
        };
        setUserProfile(updatedProfile);
        setAnalyticsData(null);
        setBreakdownData(null);
        toast.success("Google Analytics property disconnected successfully.");
      }
    } catch (err) {
      console.error("GA property disconnect error:", err);
      toast.error("Failed to disconnect Google Analytics property.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnectGAProperty = () => {
    triggerConfirmation(
      "Disconnect Google Analytics Property",
      "Are you sure you want to disconnect your Google Analytics GA4 property integration?",
      performDisconnectGAProperty,
      "Disconnect",
      "danger",
    );
  };

  const fetchGoogleAnalyticsData = async (
    profile,
    sessionPassed = null,
    daysParam = 30,
    explicitWebsite = null,
    explicitPropertyId = null,
  ) => {
    const targetPropertyId = explicitPropertyId || profile?.property_id || ga4PropertyId || null;
    const targetWebsite = explicitWebsite || profile?.website || (selectedWebsite && selectedWebsite !== "all" ? selectedWebsite : null);

    if (
      !profile?.google_analytics_connected &&
      !profile?.google_refresh_token
    ) {
      setAnalyticsData(null);
      setBreakdownData(null);
      return;
    }

    setAnalyticsLoading(true);
    try {
      let session = sessionPassed;
      if (!session) {
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();
        session = currentSession;
      }
      if (!session) return;

      const headers = { Authorization: `Bearer ${session.access_token}` };

      if (targetPropertyId) {
        setBreakdownLoading(true);

        const fetchMetrics = async () => {
          try {
            let url = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/auth/google/metrics?days=${daysParam}`;
            if (targetWebsite) {
              url += `&website=${encodeURIComponent(targetWebsite)}`;
            }
            if (targetPropertyId) {
              url += `&property_id=${encodeURIComponent(targetPropertyId)}`;
            }
            const res = await axios.get(url, { headers });
            setAnalyticsData(res.data);
          } catch (mErr) {
            console.error("Error loading GA metrics:", mErr);
            setAnalyticsData(null);
            const status = mErr.response?.status;
            const detail = mErr.response?.data?.detail || "";
            if (status === 401 || detail.toLowerCase().includes("reconnect") || detail.toLowerCase().includes("expired")) {
              if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
                setAgencyClients((prev) =>
                  prev.map((c) =>
                    cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite)
                      ? { ...c, client_google_refresh_token: null, client_google_access_token: null }
                      : c
                  )
                );
              } else {
                setUserProfile((prev) =>
                  prev ? { ...prev, google_analytics_connected: false, google_refresh_token: null } : prev
                );
              }
            }
          }
        };

        const fetchBreakdowns = async () => {
          try {
            let url = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/auth/google/breakdowns?days=${daysParam}`;
            if (targetWebsite) {
              url += `&website=${encodeURIComponent(targetWebsite)}`;
            }
            if (targetPropertyId) {
              url += `&property_id=${encodeURIComponent(targetPropertyId)}`;
            }
            const res = await axios.get(url, { headers });
            setBreakdownData(res.data);
          } catch (bErr) {
            console.error("Error loading GA breakdowns:", bErr);
            setBreakdownData(null);
          } finally {
            setBreakdownLoading(false);
          }
        };

        await Promise.all([fetchMetrics(), fetchBreakdowns()]);
      } else {
        setAnalyticsData(null);
        setBreakdownData(null);
      }

      // Always fetch the properties list if GA is connected (using prefixed variables to prevent conflict)
      try {
        let propUrl = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/auth/google/properties`;
        if (targetWebsite) {
          propUrl += `?website=${encodeURIComponent(targetWebsite)}`;
        }
        const propRes = await axios.get(propUrl, { headers });
        const fetchedProps = propRes.data?.properties || [];
        setPropertiesList(fetchedProps);

        // Auto-match if no property is currently linked to the active profile/client
        if (
          !targetPropertyId &&
          fetchedProps.length > 0 &&
          targetWebsite &&
          targetWebsite !== "all"
        ) {
          const cleanTarget = cleanUrlJS(targetWebsite);
          const matched = fetchedProps.find((p) => {
            const cleanProp = cleanUrlJS(p.display_name || "");
            return (
              cleanProp === cleanTarget ||
              (cleanTarget && cleanProp.includes(cleanTarget)) ||
              (cleanProp && cleanTarget.includes(cleanProp))
            );
          });
          if (matched) {
            console.log(
              `Auto-matching GA4 property ${matched.display_name} (${matched.property_id}) to ${targetWebsite}`,
            );
            handleSelectGAProperty(matched.property_id, matched.display_name);
          }
        }
      } catch (pErr) {
        console.error("Error fetching Google Analytics properties:", pErr);
        setPropertiesList([]);
      }
    } catch (err) {
      console.error("Error fetching Google Analytics data:", err);
      setAnalyticsData(null);
      setBreakdownData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    // Safety timer to prevent spinner hanging indefinitely (increased for heavy admin payloads)
    const safetyTimer = setTimeout(() => {
      setInitialDataLoaded(true);
      setLoading(false);
    }, 25000);

    try {
      setLoading(true);
      setError(null);

      let session = getCachedSession();
      const isCachedExpired = session?.expires_at
        ? session.expires_at < Math.floor(Date.now() / 1000)
        : true;

      let sessionError = null;

      // Only perform network getSafeSession if we don't have a cached session or it is expired
      if (!session || isCachedExpired) {
        try {
          const result = await getSafeSession(15000);
          if (result.data?.session) {
            session = result.data.session;
          }
          sessionError = result.error;
        } catch (err) {
          sessionError = err;
        }
      }

      if (!session) {
        setInitialDataLoaded(true);
        setLoading(false);
        clearTimeout(safetyTimer);
        const storageKey = Object.keys(localStorage).find(
          (key) => key.startsWith("sb-") && key.endsWith("-auth-token"),
        );
        if (!storageKey) {
          navigate("/auth");
        } else {
          console.warn(
            "UserDashboard: getSafeSession timed out/failed, and fallback failed.",
          );
          setError(
            "Session loading timed out. Please check your network and refresh.",
          );
        }
        return;
      }

      const isExpired = session.expires_at
        ? session.expires_at < Math.floor(Date.now() / 1000)
        : true;
      if (sessionError && isExpired) {
        console.warn(
          "UserDashboard: Session is expired and token refresh timed out. Redirecting to /auth.",
        );
        setInitialDataLoaded(true);
        setLoading(false);
        clearTimeout(safetyTimer);
        clearSupabaseAuthStorage();
        navigate("/auth");
        return;
      }

      setUserData(session.user);

      // Fast check: do we have a cached profile?
      const cached = getCachedProfile(session.user.id);
      let resolvedProfile = cached || userProfile;

      if (resolvedProfile && !userProfile) {
        setUserProfile(resolvedProfile);
      }

      // Fetch user profile immediately without blocking on scan history.
      // Direct Supabase query runs concurrently with backend /user/profile.
      const fetchProfilePromise = (async () => {
        // Direct fast query to Supabase profiles table (~50-100ms)
        const directSbFetch = supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single()
          .then(({ data: sbData, error: sbErr }) => {
            if (!sbErr && sbData) {
              setUserProfile((prev) => {
                const merged = { ...(prev || {}), ...sbData };
                setCachedProfile(session.user.id, merged);
                return merged;
              });
              return sbData;
            }
            return null;
          })
          .catch(() => null);

        // Backend profile fetch (adds backend flags: google tokens, reset checks, etc.)
        try {
          const profileRes = await axios.get(
            `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/user/profile`,
            {
              headers: { Authorization: `Bearer ${session.access_token}` },
              timeout: 15000,
            },
          );
          if (profileRes && profileRes.data) {
            setUserProfile(profileRes.data);
            setCachedProfile(session.user.id, profileRes.data);
            return profileRes.data;
          }
        } catch (profErr) {
          console.warn(
            "Backend profile fetch warning, checking direct Supabase data:",
            profErr,
          );
          const sbData = await directSbFetch;
          if (sbData) return sbData;
        }

        const sbData = await directSbFetch;
        if (sbData) return sbData;

        // Fallback only if both network calls fail and no cache exists: use authenticated user identity
        if (!resolvedProfile) {
          const fallback = {
            id: session.user.id,
            plan: session.user.user_metadata?.plan || "core",
            scans_used: 0,
            company_name: session.user.email
              ? session.user.email.split("@")[0]
              : "User",
          };
          setUserProfile(fallback);
          return fallback;
        }
        return resolvedProfile;
      })();

      // Await scan history concurrently with user profile so data is ready BEFORE dashboard renders
      const historyPromise = axios
        .get(
          `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/scan_history`,
          {
            headers: { Authorization: `Bearer ${session.access_token}` },
            timeout: 20000,
          },
        )
        .then((res) => {
          if (res && res.data) {
            setHistory(res.data || []);
            return res.data || [];
          }
          return [];
        })
        .catch((histErr) => {
          console.warn("Scan history fetch warning:", histErr);
          return [];
        });

      const [freshProfile] = await Promise.all([
        fetchProfilePromise,
        historyPromise,
      ]);
      if (freshProfile) {
        resolvedProfile = freshProfile;
      }

      // Claim guest scan if available
      const savedGuestScan = localStorage.getItem("seenbyai_last_guest_scan");
      if (savedGuestScan) {
        try {
          const parsedScan = JSON.parse(savedGuestScan);
          await axios.post(
            `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/claim_guest_scan`,
            { guest_scan: parsedScan },
            {
              headers: { Authorization: `Bearer ${session.access_token}` },
              timeout: 15000,
            },
          );
          localStorage.removeItem("seenbyai_last_guest_scan");
        } catch (claimErr) {
          console.warn("Failed to claim guest scan:", claimErr);
        }
      }

      // Fetch Google Analytics & Google Search Console data concurrently
      let profileForMetrics = resolvedProfile;
      if (
        (resolvedProfile.plan &&
        resolvedProfile.plan.toLowerCase() === "agency") ||
        (resolvedProfile.extra_clients > 0) ||
        resolvedProfile.is_admin
      ) {
        let fetchedClients = [];
        try {
          const headers = { Authorization: `Bearer ${session.access_token}` };
          const [clientsRes, allowanceRes] = await Promise.allSettled([
            axios.get(
              `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/agency/clients`,
              { headers },
            ),
            axios.get(
              `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/agency/allowance`,
              { headers },
            ),
          ]);

          if (clientsRes.status === "fulfilled") {
            fetchedClients = clientsRes.value.data || [];
            setAgencyClients(fetchedClients);
            if (
              fetchedClients.length > 0 &&
              (!selectedWebsite || selectedWebsite === "all")
            ) {
              setSelectedWebsite(fetchedClients[0].website);
            }
          }

          if (allowanceRes.status === "fulfilled" && allowanceRes.value.data) {
            setAgencyAllowance(allowanceRes.value.data);
            setUserProfile((prev) => {
              if (!prev) return prev;
              const updated = {
                ...prev,
                extra_clients: allowanceRes.value.data.extra_clients,
              };
              setCachedProfile(session.user.id, updated);
              return updated;
            });
          }
        } catch (agencyErr) {
          console.error("Failed to load agency clients on mount:", agencyErr);
        }

        const activeSite =
          selectedWebsite && selectedWebsite !== "all"
            ? selectedWebsite
            : fetchedClients.length > 0
              ? fetchedClients[0].website
              : null;

        if (activeSite) {
          const client = fetchedClients.find((c) => cleanUrlJS(c.website) === cleanUrlJS(activeSite));
          profileForMetrics = {
            ...resolvedProfile,
            property_id: client?.property_id || null,
            ga4_property: client?.ga4_property || null,
            gsc_site_url: client?.gsc_site_url || null,
            google_search_console_connected: !!client?.gsc_site_url,
            google_refresh_token: client?.client_google_refresh_token || null,
            google_analytics_connected: !!client?.client_google_refresh_token,
          };
          setGscSiteUrl(profileForMetrics.gsc_site_url);
          setGa4Property(profileForMetrics.ga4_property);
          setGa4PropertyId(profileForMetrics.property_id);
        } else {
          setGscSiteUrl(null);
          setGa4Property(null);
          setGa4PropertyId(null);
        }
      } else {
        if (resolvedProfile.gsc_site_url) {
          setGscSiteUrl(resolvedProfile.gsc_site_url);
        } else {
          setGscSiteUrl(null);
        }
        if (resolvedProfile.ga4_property || resolvedProfile.property_id) {
          setGa4Property(resolvedProfile.ga4_property);
          setGa4PropertyId(resolvedProfile.property_id);
        } else {
          setGa4Property(null);
          setGa4PropertyId(null);
        }
      }

      await Promise.all([
        fetchGoogleAnalyticsData(profileForMetrics, session, gaPeriodDays),
        fetchGoogleSearchConsoleData(profileForMetrics, null, session),
      ]);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError(err.message || "Failed to load dashboard data.");
    } finally {
      clearTimeout(safetyTimer);
      setInitialDataLoaded(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    const paramWebsite = searchParams.get("website");
    if (paramWebsite) {
      setSelectedWebsite(paramWebsite);
    }

    if (searchParams.get("google_connected") === "true") {
      toast.success("Google account connected successfully!");
      // Clear URL parameter
      setSearchParams({}, { replace: true });
    }

    const checkStripeSession = async () => {
      const sessionId = searchParams.get("session_id");
      if (sessionId) {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (session) {
            const baseUrl =
              process.env.REACT_APP_API_URL || "http://localhost:8000";
            const response = await axios.get(
              `${baseUrl}/confirm-subscription`,
              {
                params: { session_id: sessionId },
                headers: { Authorization: `Bearer ${session.access_token}` },
              },
            );
            if (response.data?.status === "success") {
              setSuccessMessage(
                `Subscription confirmed! Welcome to SeenByAI ${response.data.plan.toUpperCase()}.`,
              );
              toast.success(
                `Subscription confirmed! Plan upgraded to ${response.data.plan.toUpperCase()}.`,
              );
              fetchDashboardData();
            }
          }
        } catch (err) {
          console.error("Failed to confirm Stripe subscription:", err);
          toast.error(
            "Could not confirm subscription. Please contact support.",
          );
        } finally {
          setSearchParams({}, { replace: true });
        }
      }
    };
    checkStripeSession();

    const checkAdditionalSession = async () => {
      const additionalSessionId = searchParams.get("additional_session_id");
      if (additionalSessionId) {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (session) {
            const baseUrl =
              process.env.REACT_APP_API_URL || "http://localhost:8000";
            const response = await axios.get(
              `${baseUrl}/confirm-additional-business`,
              {
                params: { session_id: additionalSessionId },
                headers: { Authorization: `Bearer ${session.access_token}` },
              },
            );
            if (response.data?.website) {
              toast.success(
                `Successfully activated client website: ${response.data.website}`,
              );
              fetchDashboardData();
            }
          }
        } catch (err) {
          console.error("Failed to confirm additional business payment:", err);
          toast.error(
            err.response?.data?.detail || "Failed to confirm payment.",
          );
        } finally {
          setSearchParams({}, { replace: true });
        }
      }
    };
    checkAdditionalSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setTimeout(() => {
        if (event === "SIGNED_OUT") {
          navigate("/auth");
        } else if (event === "TOKEN_REFRESHED" && session) {
          setUserData(session.user);
        }
      }, 0);
    });

    return () => {
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams, setSearchParams]);

  const handleLogout = async () => {
    try {
      // Non-blocking signout call with 500ms timeout so API latency never freezes UI
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
    } catch (err) {
      console.warn("UserDashboard: Error during signOut:", err);
    } finally {
      clearSupabaseAuthStorage();
      navigate("/auth");
    }
  };

  const handleConnectGoogle = async () => {
    setActionLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      let url = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/auth/google/url`;
      if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
        url += `?website=${encodeURIComponent(selectedWebsite)}`;
      }

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Failed to retrieve Google connection URL.");
      }
    } catch (err) {
      console.error("Google Auth connection error:", err);
      toast.error(
        err.response?.data?.detail ||
          "Could not start Google Analytics connection.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const performDisconnectGoogle = async () => {
    setActionLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      let url = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/auth/google/disconnect`;
      if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
        url += `?website=${encodeURIComponent(selectedWebsite)}`;
      }

      const res = await axios.post(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );

      if (res.data?.status === "success") {
        toast.success("Google account disconnected successfully.");
        if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
          const updatedClients = agencyClients.map((c) => {
            if (cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite)) {
              return {
                ...c,
                property_id: null,
                ga4_property: null,
                gsc_site_url: null,
                client_google_access_token: null,
                client_google_refresh_token: null,
                client_google_token_expires_at: null,
              };
            }
            return c;
          });
          setAgencyClients(updatedClients);
          setGscSiteUrl(null);
          setAnalyticsData(null);
          setBreakdownData(null);
          setGscData(null);
        } else {
          if (userProfile) {
            setUserProfile({
              ...userProfile,
              google_analytics_connected: false,
              google_search_console_connected: false,
              google_refresh_token: null,
              property_id: null,
              ga4_property: null,
              gsc_site_url: null,
            });
          }
          setGscSiteUrl(null);
          setAnalyticsData(null);
          setBreakdownData(null);
          setGscData(null);
        }
      } else {
        toast.error(
          res.data?.message || "Failed to disconnect Google account.",
        );
      }
    } catch (err) {
      console.error("Google disconnect error:", err);
      toast.error(
        err.response?.data?.detail || "Failed to disconnect Google account.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnectGoogle = () => {
    triggerConfirmation(
      "Disconnect Google Analytics",
      "Are you sure you want to disconnect your Google Analytics account?",
      performDisconnectGoogle,
      "Disconnect",
      "danger",
    );
  };

  const handleSelectGAProperty = async (propertyId, displayName) => {
    setActionLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const payload = {
        property_id: propertyId,
        property_name: displayName,
      };
      if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
        payload.website = selectedWebsite;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/auth/google/select-property`,
        payload,
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );

      if (res.data?.status === "success") {
        toast.success(`Successfully connected to GA4 property: ${displayName}`);
        setGa4Property(displayName);
        setGa4PropertyId(propertyId);
        setShowPropertySelect(false);
        if (userPlanKey === "agency" && selectedWebsite && selectedWebsite !== "all") {
          const updatedClients = agencyClients.map((c) => {
            if (cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite)) {
              return {
                ...c,
                property_id: propertyId,
                ga4_property: displayName,
              };
            }
            return c;
          });
          setAgencyClients(updatedClients);
          const matchedClient = updatedClients.find((c) => cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite));
          const modifiedProfile = {
            ...userProfile,
            website: matchedClient?.website || selectedWebsite,
            property_id: propertyId,
            ga4_property: displayName,
            google_refresh_token: matchedClient?.client_google_refresh_token || null,
            google_analytics_connected: true,
          };
          await fetchGoogleAnalyticsData(modifiedProfile, null, gaPeriodDays, matchedClient?.website || selectedWebsite, propertyId);
        } else {
          const updatedProfile = {
            ...userProfile,
            property_id: propertyId,
            ga4_property: displayName,
          };
          setUserProfile(updatedProfile);
          await fetchGoogleAnalyticsData(updatedProfile, null, gaPeriodDays, null, propertyId);
        }
      } else {
        toast.error(res.data?.message || "Failed to select property.");
      }
    } catch (err) {
      console.error("Error selecting property:", err);
      toast.error(err.response?.data?.detail || "Failed to select property.");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Admin Check (100% Dynamic from Database Profile / Metadata) ──
  const isAdmin = !!(
    userProfile?.is_admin ||
    userData?.user_metadata?.is_admin ||
    userData?.app_metadata?.is_admin
  );

  const getClientRecord = () => {
    if (
      userPlanKey === "agency" &&
      selectedWebsite &&
      selectedWebsite !== "all"
    ) {
      return (
        agencyClients.find(
          (c) => cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite),
        ) || null
      );
    }
    return null;
  };

  const googleAnalyticsConnected =
    userPlanKey === "agency"
      ? !!(
          selectedWebsite &&
          selectedWebsite !== "all" &&
          getClientRecord()?.client_google_refresh_token
        )
      : !!(
          userProfile?.google_analytics_connected ||
          userProfile?.google_refresh_token
        );

  // ── Extract Unique Scanned Websites ──
  const uniqueWebsites = Array.from(
    new Set(
      history
        .map((item) => item.website)
        .filter((w) => w && typeof w === "string" && w.trim().length > 0),
    ),
  );

  // Filter history based on selected website dropdown
  const activeHistory =
    selectedWebsite === "all"
      ? history
      : history.filter(
          (item) => cleanUrlJS(item.website) === cleanUrlJS(selectedWebsite),
        );

  const latestScan =
    activeHistory.length > 0
      ? activeHistory[0]
      : selectedWebsite === "all" && history.length > 0
        ? history[0]
        : null;
  const rawScore = latestScan?.scan_data?.score ?? 0;

  // ── Dynamic Score Delta Calculation ──
  const hasHistory = activeHistory.length > 1;
  const currentScore = latestScan?.scan_data?.score ?? 0;
  const prevScore = hasHistory
    ? (activeHistory[1]?.scan_data?.score ?? currentScore)
    : currentScore;
  const scoreDiff = currentScore - prevScore;
  const scoreDeltaText = hasHistory
    ? `${scoreDiff > 0 ? `+${scoreDiff}` : scoreDiff} pts`
    : latestScan
      ? "Initial scan"
      : "—";
  const vsLabel = hasHistory
    ? `vs ${new Date(activeHistory[1].created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
    : latestScan
      ? "vs baseline"
      : "No scans yet";

  const scoreTier = !latestScan
    ? "No Scan Data"
    : rawScore >= 80
      ? "Excellent"
      : rawScore >= 60
        ? "Good"
        : rawScore > 0
          ? "Needs Work"
          : "No Scan Data";

  const getReadinessDescription = (tier) => {
    if (tier === "Excellent") {
      return (
        <>
          Your website has an <span className="highlight-green">Excellent</span>{" "}
          level of AI readiness. It is highly optimized for AI search engines,
          crawler access, and structured data.
        </>
      );
    } else if (tier === "Good") {
      return (
        <>
          Your website has a <span className="highlight-green">Good</span> level
          of AI readiness. There are a few areas you can improve to increase
          your AI Readiness Score.
        </>
      );
    } else if (tier === "Needs Work") {
      return (
        <>
          Your website has a <span className="highlight-amber">Needs Work</span>{" "}
          level of AI readiness. Key optimizations for AI search crawlers,
          technical SEO, and structured schema are missing.
        </>
      );
    } else {
      return "No scan data available. Run a scan to evaluate your website's AI Readiness Score.";
    }
  };

  const getGaugeStatusText = (tier) => {
    if (tier === "Excellent") return "Excellent progress";
    if (tier === "Good") return "You're on the right track";
    if (tier === "Needs Work") return "Needs optimization";
    return "Ready to scan";
  };

  const getDeltaClass = () => {
    if (!hasHistory) return "delta-neutral";
    if (scoreDiff > 0) return "delta-positive";
    if (scoreDiff < 0) return "delta-negative";
    return "delta-neutral";
  };

  // ── Helper to extract category scores directly from backend raw json output ──
  const getRawCategoryScore = (key) => {
    const rawObj = latestScan?.scan_data?.raw || latestScan?.scan_data;
    const scoreVal = rawObj?.[key];
    return scoreVal != null && !isNaN(scoreVal) ? Math.round(scoreVal) : 0;
  };

  // ── Dynamic 6 Readiness Breakdown Categories ──
  const categoriesBreakdown = [
    {
      name: "Technical Foundation",
      icon: <FiDatabase />,
      score: getRawCategoryScore("technical_seo_raw_score"),
    },
    {
      name: "Content & Entities",
      icon: <FiFileText />,
      score: getRawCategoryScore("content_raw_score"),
    },
    {
      name: "Schema & Structured Data",
      icon: <FiLayers />,
      score: getRawCategoryScore("raw_score"),
    },
    {
      name: "Accessibility & Indexability",
      icon: <FiSearch />,
      score: getRawCategoryScore("user_engagement_raw_score"),
    },
    {
      name: "Metadata & Optimization",
      icon: <FiZap />,
      score: getRawCategoryScore("optimization_raw_score"),
    },
    {
      name: "Internal Linking",
      icon: <FiLink />,
      score: getRawCategoryScore("link_structure_raw_score"),
    },
  ];

  // ── Dynamic Top Priority Actions ──
  const getDynamicPriorityActions = () => {
    if (!latestScan) return [];

    const actions = [];

    // Priority areas from LLM evaluation
    const rawPriorityAreas = latestScan?.scan_data?.raw?.priority_areas;
    if (Array.isArray(rawPriorityAreas) && rawPriorityAreas.length > 0) {
      rawPriorityAreas.slice(0, 5).forEach((item) => {
        actions.push({
          title: item.recommended_action || item.area || item.title || "",
          impact:
            item.impact && item.impact !== "No data available"
              ? item.impact
              : item.priority_level
                ? `${item.priority_level} Impact`
                : "",
          impactLevel: (item.impact || item.priority_level || "")
            .toLowerCase()
            .includes("high")
            ? "high"
            : "medium",
          boost:
            item.boost && item.boost !== "No data available"
              ? item.boost
              : item.priority_level
                ? `Priority: ${item.priority_level}`
                : "",
        });
      });
    }

    // Reasons from scan result (representing missing elements dynamically)
    const rawReasons = latestScan?.scan_data?.reasons;
    if (
      actions.length < 5 &&
      Array.isArray(rawReasons) &&
      rawReasons.length > 0
    ) {
      rawReasons.forEach((r) => {
        if (actions.length >= 5) return;
        const titleText =
          typeof r === "string" ? r : r.text || r.reason || r.issue || "";
        if (titleText) {
          let mapped = SUBCHECK_MAPPINGS.find((m) => m.pattern.test(titleText));
          actions.push({
            title: titleText,
            impact: mapped ? `${mapped.impact} Impact` : "Medium Impact",
            impactLevel: mapped
              ? mapped.type === "Critical"
                ? "high"
                : mapped.type === "Warning"
                  ? "medium"
                  : "low"
              : "medium",
            boost: mapped ? `Boost: +${getDynamicPoints(mapped)}%` : "",
          });
        }
      });
    }

    const order = { high: 1, medium: 2, low: 3 };
    actions.sort(
      (a, b) => (order[a.impactLevel] || 4) - (order[b.impactLevel] || 4),
    );

    return actions.slice(0, 5);
  };

  const topPriorityActions = getDynamicPriorityActions();

  // ── Dynamic Trend Chart Coordinates & Path Generation ──
  const getTrendChartData = () => {
    const now = new Date();
    const endDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999,
    );

    // 1. Filter valid scans (score > 0) strictly for the active website (or all if "all")
    let validScans = [];
    if (selectedWebsite && selectedWebsite !== "all") {
      validScans = (history || []).filter(
        (item) =>
          cleanUrlJS(item.website) === cleanUrlJS(selectedWebsite) &&
          item.scan_data?.score != null &&
          item.scan_data.score > 0,
      );
    } else {
      validScans = (history || []).filter(
        (item) => item.scan_data?.score != null && item.scan_data.score > 0,
      );
    }

    let numDays = 30;
    let startDate;
    if (timeRange === "7 Days") {
      numDays = 7;
      startDate = new Date(
        endDate.getTime() - (numDays - 1) * 24 * 60 * 60 * 1000,
      );
    } else if (timeRange === "90 Days") {
      numDays = 90;
      startDate = new Date(
        endDate.getTime() - (numDays - 1) * 24 * 60 * 60 * 1000,
      );
    } else if (timeRange === "All Time") {
      numDays = null;
      if (validScans.length > 0) {
        const earliestTime = Math.min(
          ...validScans.map((s) => new Date(s.created_at).getTime()),
        );
        startDate = new Date(earliestTime);
      } else {
        startDate = new Date(endDate.getTime() - 29 * 24 * 60 * 60 * 1000);
      }
    } else {
      numDays = 30;
      startDate = new Date(
        endDate.getTime() - (numDays - 1) * 24 * 60 * 60 * 1000,
      );
    }
    startDate.setHours(0, 0, 0, 0);

    let startTime = startDate.getTime();
    const endTime = endDate.getTime();
    if (startTime >= endTime) {
      startTime = endTime - 7 * 24 * 60 * 60 * 1000;
    }
    const timeSpan = endTime - startTime || 1;

    // Generate clean, evenly spaced X-axis date tick labels
    const xTicks = [];
    const tickCount = numDays === 7 ? 7 : 6;
    for (let i = 0; i < tickCount; i++) {
      const tickTime =
        startTime + (i / (tickCount - 1)) * (endTime - startTime);
      const tickDate = new Date(tickTime);
      const label = tickDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const pct = (i / (tickCount - 1)) * 100;
      xTicks.push({ label, pct });
    }

    if (!validScans || validScans.length === 0) {
      return {
        points: [],
        xTicks,
        pathD: "",
        areaD: "",
        totalScansInRange: 0,
        latestOverallScan: null,
        timeRange,
      };
    }

    // Sort all available scans descending to get overall latest scan for reference
    const sortedAll = [...validScans].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
    const latestOverallScan = sortedAll.length > 0 ? sortedAll[0] : null;

    // 2. Filter strictly by selected time range
    const scansInRange = validScans.filter((item) => {
      const t = new Date(item.created_at).getTime();
      return t >= startTime && t <= endTime;
    });

    // 3. Group by Date (YYYY-MM-DD) to keep at most 1 point per day (latest of that day)
    const groupedByDate = {};
    scansInRange.forEach((item) => {
      const d = new Date(item.created_at);
      const dStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (
        !groupedByDate[dStr] ||
        new Date(item.created_at) > new Date(groupedByDate[dStr].created_at)
      ) {
        groupedByDate[dStr] = item;
      }
    });

    const chronScans = Object.values(groupedByDate).sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );

    if (chronScans.length === 0) {
      return {
        points: [],
        xTicks,
        pathD: "",
        areaD: "",
        totalScansInRange: 0,
        latestOverallScan,
        timeRange,
      };
    }

    // Map chronScans to percentage and SVG (viewBox 0 0 1000 100) coordinates
    const points = chronScans.map((item) => {
      const itemTime = new Date(item.created_at).getTime();
      // Ensure points stay within padding bounds (2% to 98% horizontally)
      const rawPctX = ((itemTime - startTime) / timeSpan) * 100;
      const pctX = Math.max(2, Math.min(98, rawPctX));
      const score = Math.max(0, Math.min(100, item.scan_data?.score ?? 0));
      // Y is 0 at top (score 100) and 100 at bottom (score 0), with padding 8% to 92%
      const pctY = 92 - (score / 100) * 84;
      const svgX = (pctX / 100) * 1000;
      const svgY = pctY;
      const dateStr = new Date(item.created_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      return {
        pctX,
        pctY,
        svgX,
        svgY,
        score,
        dateStr,
        created_at: item.created_at,
      };
    });

    let pathD = "";
    let areaD = "";

    if (points.length === 1) {
      const p = points[0];
      pathD = `M ${Math.max(0, p.svgX - 25)} ${p.svgY} L ${Math.min(1000, p.svgX + 25)} ${p.svgY}`;
      areaD = "";
    } else {
      pathD = `M ${points[0].svgX} ${points[0].svgY}`;
      for (let i = 1; i < points.length; i++) {
        pathD += ` L ${points[i].svgX} ${points[i].svgY}`;
      }
      areaD = `${pathD} L ${points[points.length - 1].svgX} 100 L ${points[0].svgX} 100 Z`;
    }

    return {
      points,
      xTicks,
      pathD,
      areaD,
      totalScansInRange: points.length,
      latestOverallScan,
      timeRange,
    };
  };

  const trendData = getTrendChartData();

  // ── Calculate Score Distribution ──
  const getScoreDistribution = () => {
    const hasRealData = !!latestScan;

    // Collect the 6 category scores for the latest scan of the active website
    const categoryScores = hasRealData
      ? [
          getRawCategoryScore("technical_seo_raw_score"),
          getRawCategoryScore("content_raw_score"),
          getRawCategoryScore("raw_score"), // Schema & Structured Data score
          getRawCategoryScore("user_engagement_raw_score"),
          getRawCategoryScore("optimization_raw_score"),
          getRawCategoryScore("link_structure_raw_score"),
        ]
      : [];

    // Counts
    const excellent = hasRealData
      ? categoryScores.filter((s) => s >= 80 && s <= 100).length
      : 0;
    const good = hasRealData
      ? categoryScores.filter((s) => s >= 60 && s <= 79).length
      : 0;
    const average = hasRealData
      ? categoryScores.filter((s) => s >= 40 && s <= 59).length
      : 0;
    const poor = hasRealData
      ? categoryScores.filter((s) => s >= 0 && s <= 39).length
      : 0;

    const total = excellent + good + average + poor;

    // Percentages
    const excellentPct = total > 0 ? Math.round((excellent / total) * 100) : 0;
    const goodPct = total > 0 ? Math.round((good / total) * 100) : 0;
    const averagePct = total > 0 ? Math.round((average / total) * 100) : 0;
    // ensure it sums up to exactly 100
    const poorPct = total > 0 ? 100 - excellentPct - goodPct - averagePct : 0;

    return {
      excellent,
      good,
      average,
      poor,
      total,
      excellentPct,
      goodPct,
      averagePct,
      poorPct,
      hasRealData,
    };
  };

  const scoreDist = getScoreDistribution();
  const distRadius = 40;
  const distCirc = 2 * Math.PI * distRadius; // ~251.327

  const excellentDash = (scoreDist.excellentPct / 100) * distCirc;
  const goodDash = (scoreDist.goodPct / 100) * distCirc;
  const averageDash = (scoreDist.averagePct / 100) * distCirc;
  const poorDash = (scoreDist.poorPct / 100) * distCirc;

  const excellentOffset = 0;
  const goodOffset = -excellentDash;
  const averageOffset = -(excellentDash + goodDash);
  const poorOffset = -(excellentDash + goodDash + averageDash);

  const getMetricSparkline = (values) => {
    if (!values || values.length === 0) {
      return null;
    }
    const width = 120;
    const height = 30;
    const paddingX = 0;
    const paddingY = 4;

    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const valRange = maxVal - minVal;

    const points = values.map((val, idx) => {
      const x =
        values.length === 1
          ? width / 2
          : paddingX + (idx / (values.length - 1)) * (width - 2 * paddingX);

      let y;
      if (valRange === 0) {
        y = height / 2;
      } else {
        y =
          height -
          paddingY -
          ((val - minVal) / valRange) * (height - 2 * paddingY);
      }
      return { x, y };
    });

    if (points.length === 1) {
      const pt = points[0];
      return {
        pathD: `M 0 ${pt.y} L ${width} ${pt.y}`,
        areaD: `M 0 ${pt.y} L ${width} ${pt.y} L ${width} ${height} L 0 ${height} Z`,
      };
    }

    let pathD = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      pathD += ` L ${points[i].x} ${points[i].y}`;
    }

    const areaD = `${pathD} L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;

    return { pathD, areaD };
  };

  const getHistoricalMetrics = () => {
    const sorted = [...activeHistory].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );
    const lastScans = sorted.slice(-8);

    const pagesHistory = lastScans.map(
      (item) => getCrawledPagesList(item).length,
    );

    const issuesHistory = lastScans.map((item) => {
      const reasons = item.scan_data?.reasons || [];
      return reasons.length;
    });

    const warningsHistory = lastScans.map((item) => {
      const reasons = item.scan_data?.reasons || [];
      return reasons.filter((r) => {
        const text = typeof r === "string" ? r : r.text || "";
        const m = SUBCHECK_MAPPINGS.find((m) => m.pattern.test(text));
        return m && m.type === "Warning";
      }).length;
    });

    return {
      pages: pagesHistory,
      issues: issuesHistory,
      warnings: warningsHistory,
    };
  };

  const histMetrics = getHistoricalMetrics();
  const pagesSparkline = getMetricSparkline(histMetrics.pages);
  const issuesSparkline = getMetricSparkline(histMetrics.issues);
  const warningsSparkline = getMetricSparkline(histMetrics.warnings);

  const handleViewDetails = async (scanEntry) => {
    let finalScan = scanEntry || latestScan;

    // Check if the scan is stripped (i.e. older scan) and needs a full detail fetch
    if (
      finalScan &&
      finalScan.id &&
      (!finalScan.scan_data?.raw ||
        Object.keys(finalScan.scan_data.raw).length <= 3)
    ) {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/scan_history/${finalScan.id}`,
            {
              headers: {
                Authorization: `Bearer ${session.access_token}`,
              },
            },
          );
          if (response.data) {
            finalScan = response.data;
          }
        }
      } catch (err) {
        console.warn(
          "Failed to fetch full scan details, showing partial data:",
          err,
        );
      }
    }

    setSelectedScan(finalScan);
    setActiveTab("report");
  };

  const handleDownloadPDF = async () => {
    if (!latestScan || !latestScan.id) {
      toast.error("No scan data available to download.");
      return;
    }
    setPdfLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Authentication required. Please sign in again.");
        setPdfLoading(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/scan_history/${latestScan.id}/pdf`,
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${latestScan.company || "company"}_ai_readiness_report.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF Report downloaded successfully!");
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error("Failed to download PDF report. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  const companyDomain =
    selectedWebsite && selectedWebsite !== "all"
      ? selectedWebsite.replace(/^https?:\/\//, "").replace(/\/$/, "")
      : latestScan?.website
        ? latestScan.website.replace(/^https?:\/\//, "").replace(/\/$/, "")
        : "No website scanned yet";

  const [actionCenterResolutions, setActionCenterResolutions] = useState(() => {
    try {
      const saved = localStorage.getItem(`seenbyai_resolutions_${companyDomain}`);
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  // Sync resolutions whenever companyDomain changes or an update event fires
  useEffect(() => {
    const loadResolutions = () => {
      try {
        const saved = localStorage.getItem(`seenbyai_resolutions_${companyDomain}`);
        setActionCenterResolutions(saved ? JSON.parse(saved) : {});
      } catch (e) {
        setActionCenterResolutions({});
      }
    };

    loadResolutions();

    const handleResolutionEvent = (e) => {
      if (!e.detail || !e.detail.domain || e.detail.domain === companyDomain) {
        loadResolutions();
      }
    };

    window.addEventListener("seenbyai_resolution_toggled", handleResolutionEvent);
    window.addEventListener("storage", loadResolutions);

    return () => {
      window.removeEventListener("seenbyai_resolution_toggled", handleResolutionEvent);
      window.removeEventListener("storage", loadResolutions);
    };
  }, [companyDomain]);

  const getDynamicReportData = () => {
    if (!latestScan) {
      return { openIssues: [], fixedIssues: [], recommendations: [] };
    }

    const openIssues = [];
    const fixedIssues = [];
    const seenFixedKeys = new Set();

    // 1. Process current scan weaknesses
    const rawWeaknesses =
      latestScan?.scan_data?.raw?.weaknesses || latestScan?.scan_data?.reasons;
    if (Array.isArray(rawWeaknesses) && rawWeaknesses.length > 0) {
      rawWeaknesses.forEach((w, idx) => {
        const text =
          typeof w === "string" ? w : w.text || w.reason || w.issue || "";
        if (!text) return;

        let mapped = SUBCHECK_MAPPINGS.find((m) => m.pattern.test(text));
        const itemKey = mapped ? mapped.key : `issue_${idx}`;
        const issueName = mapped ? mapped.name : text;

        const issueObj = {
          name: text,
          canonicalName: issueName,
          key: itemKey,
          type: mapped
            ? mapped.type
            : idx === 0
              ? "Critical"
              : idx < 3
                ? "Warning"
                : "Notice",
          pagesAffected: w.pagesAffected || 1,
          impact: mapped
            ? mapped.impact
            : idx === 0
              ? "High"
              : idx < 3
                ? "Medium"
                : "Low",
          scoreImpact: mapped
            ? `-${getDynamicPoints(mapped)}%`
            : w.scoreImpact && w.scoreImpact !== "No data available"
              ? w.scoreImpact
              : "-",
          actionText: "",
        };

        // Check if this issue is marked as resolved in the Action Center
        const isActionCenterResolved = !!(actionCenterResolutions && actionCenterResolutions[itemKey]);

        if (isActionCenterResolved) {
          fixedIssues.push({
            ...issueObj,
            status: "fixed",
            source: "Action Center",
            isFixed: true,
          });
          seenFixedKeys.add(itemKey);
        } else {
          openIssues.push({
            ...issueObj,
            status: "open",
            isFixed: false,
          });
        }
      });
    }

    // 2. Process Scanner Comparison (2nd scan vs 1st scan)
    // When scanned the 2nd time, if an issue from the previous scan is resolved in the latest scan, show it in Fixed
    const prevScan = activeHistory.length > 1 ? activeHistory[1] : null;
    if (prevScan) {
      const prevWeaknesses =
        prevScan?.scan_data?.raw?.weaknesses || prevScan?.scan_data?.reasons;
      if (Array.isArray(prevWeaknesses) && prevWeaknesses.length > 0) {
        prevWeaknesses.forEach((pw, pIdx) => {
          const pText =
            typeof pw === "string" ? pw : pw.text || pw.reason || pw.issue || "";
          if (!pText) return;

          const pMapped = SUBCHECK_MAPPINGS.find((m) => m.pattern.test(pText));
          const pKey = pMapped ? pMapped.key : `prev_issue_${pIdx}`;

          // Check if this previous issue is still active in the current scan
          const stillActive = openIssues.some(
            (curr) => curr.key === pKey || (pMapped && pMapped.pattern.test(curr.name))
          );

          // If present in previous scan, but NOT active in the latest scan, it was fixed in the latest scan!
          if (!stillActive && !seenFixedKeys.has(pKey)) {
            fixedIssues.push({
              name: pText,
              canonicalName: pMapped ? pMapped.name : pText,
              key: pKey,
              type: pMapped ? pMapped.type : "Warning",
              pagesAffected: pw.pagesAffected || 1,
              impact: pMapped ? pMapped.impact : "Medium",
              scoreImpact: pMapped ? `-${getDynamicPoints(pMapped)}%` : "-",
              status: "fixed",
              source: "Fixed in latest scan",
              isFixed: true,
            });
            seenFixedKeys.add(pKey);
          }
        });
      }
    }

    // 3. Process any Action Center resolutions that weren't in rawWeaknesses
    if (actionCenterResolutions) {
      Object.entries(actionCenterResolutions).forEach(([resKey, isRes]) => {
        if (isRes && !seenFixedKeys.has(resKey)) {
          const mapped = SUBCHECK_MAPPINGS.find((m) => m.key === resKey);
          if (mapped) {
            fixedIssues.push({
              name: mapped.name,
              canonicalName: mapped.name,
              key: mapped.key,
              type: mapped.type,
              pagesAffected: 1,
              impact: mapped.impact,
              scoreImpact: `-${getDynamicPoints(mapped)}%`,
              status: "fixed",
              source: "Action Center",
              isFixed: true,
            });
            seenFixedKeys.add(resKey);
          }
        }
      });
    }

    const severityOrder = { Critical: 1, Warning: 2, Notice: 3 };
    openIssues.sort(
      (a, b) => (severityOrder[a.type] || 4) - (severityOrder[b.type] || 4),
    );
    fixedIssues.sort(
      (a, b) => (severityOrder[a.type] || 4) - (severityOrder[b.type] || 4),
    );

    const dynamicRecs = [];
    const recTitlesSeen = new Set();

    const addRec = (
      title,
      impact,
      boost,
      description,
      affectedPages = null,
    ) => {
      const cleanTitle = typeof title === "string" ? title.trim() : "";
      if (!cleanTitle || recTitlesSeen.has(cleanTitle.toLowerCase())) return;
      recTitlesSeen.add(cleanTitle.toLowerCase());
      dynamicRecs.push({
        title: cleanTitle,
        impact: impact && impact !== "No data available" ? impact : "",
        boost: boost && boost !== "No data available" ? boost : "",
        description:
          description && description !== "No data available" ? description : "",
        affectedPages: affectedPages,
      });
    };

    // 1. Process scan reasons from latest scan data (representing missing elements dynamically)
    const rawReasons = latestScan?.scan_data?.reasons;
    if (Array.isArray(rawReasons) && rawReasons.length > 0) {
      rawReasons.forEach((r) => {
        let titleText =
          typeof r === "string" ? r : r.text || r.reason || r.issue || "";
        if (titleText) {
          // Clean any previously appended backend text
          titleText = titleText
            .replace(/\s*\(If this is done, score will improve with.*?\)/g, "")
            .trim();

          let boostText = "";
          let mapped = SUBCHECK_MAPPINGS.find((m) => m.pattern.test(titleText));
          if (mapped) {
            const pts = getDynamicPoints(mapped);
            boostText = `+${pts} points`;
          }
          addRec(titleText, "", boostText, "");
        }
      });
    }

    // 2. Process priority areas from LLM evaluation if available
    const rawPriorityAreas = latestScan?.scan_data?.raw?.priority_areas;
    if (Array.isArray(rawPriorityAreas) && rawPriorityAreas.length > 0) {
      rawPriorityAreas.forEach((area) => {
        const titleText =
          typeof area === "string"
            ? area
            : area.recommended_action || area.area || "No data available";
        const descText =
          area && typeof area === "object"
            ? area.description || area.detail || ""
            : "";
        const impactVal = area?.priority_level
          ? `${area.priority_level} Impact`
          : area?.impact;
        addRec(titleText, impactVal, area?.boost, descText);
      });
    }

    return {
      openIssues,
      fixedIssues,
      recommendations: dynamicRecs.slice(0, 6),
    };
  };

  const getRawResponseJSON = (scanData) => {
    if (!scanData) return { message: "No data available" };
    const rawPayload = scanData.raw || scanData.raw_json || scanData;
    return rawPayload || { message: "No data available" };
  };

  const userName =
    userProfile?.company_name || userData?.email?.split("@")[0] || "User";

  const getModifiedProfileForClient = () => {
    if (
      userPlanKey === "agency" &&
      selectedWebsite &&
      selectedWebsite !== "all"
    ) {
      const client = agencyClients.find(
        (c) => cleanUrlJS(c.website) === cleanUrlJS(selectedWebsite),
      );
      const effectiveRefreshToken =
        client?.client_google_refresh_token || null;
      const effectivePropId = client?.property_id || null;
      const clientUsage = getSelectedClientUsage();
      const clientGscSite = client?.gsc_site_url || null;
      return {
        ...userProfile,
        website: client?.website || selectedWebsite,
        property_id: effectivePropId,
        ga4_property: client?.ga4_property || null,
        gsc_site_url: clientGscSite,
        google_search_console_connected: !!clientGscSite,
        google_refresh_token: effectiveRefreshToken,
        google_analytics_connected: !!(effectiveRefreshToken && effectivePropId),
        schema_generations_used: clientUsage.schema_used || 0,
        robots_generations_used: clientUsage.robots_used || 0,
        llms_generations_used: clientUsage.llms_used || 0,
      };
    }
    return {
      ...userProfile,
      property_id: userProfile?.property_id || ga4PropertyId || null,
      ga4_property: userProfile?.ga4_property || ga4Property || null,
      gsc_site_url: userProfile?.gsc_site_url || gscSiteUrl || null,
      google_analytics_connected: !!((userProfile?.google_refresh_token || userProfile?.google_analytics_connected) && (userProfile?.property_id || ga4PropertyId)),
    };
  };

  const handleSelectDateRange = (presetId) => {
    setSelectedPeriod(presetId);
    setDateDropdownOpen(false);

    const preset =
      DATE_RANGE_PRESETS.find((p) => p.id === presetId) ||
      DATE_RANGE_PRESETS[1];

    // 1. Sync AI Visibility Trend Chart timeRange
    setTimeRange(preset.trendKey);

    // 2. Sync GA and GSC period days
    const targetDays = preset.days || 30;
    const gscTargetDays = targetDays === 30 ? 28 : (targetDays || 28);
    setGaPeriodDays(targetDays);
    setGscPeriodDays(gscTargetDays);

    // 3. Trigger refetch for GA and GSC if connected
    const activeProfile = getModifiedProfileForClient();
    if (activeProfile?.google_analytics_connected || activeProfile?.property_id) {
      fetchGoogleAnalyticsData(activeProfile, null, targetDays);
    }
    if (activeProfile?.google_search_console_connected || activeProfile?.gsc_site_url) {
      fetchGoogleSearchConsoleData(activeProfile, gscTargetDays, null);
    }
  };

  const getDaysUntilReset = () => {
    try {
      const now = new Date();
      let referenceDate = null;

      if (
        userPlanKey === "agency" &&
        selectedWebsite &&
        selectedWebsite !== "all"
      ) {
        const cleanTarget = cleanUrlJS(selectedWebsite);
        const match = agencyClients.find(
          (c) => cleanUrlJS(c.website) === cleanTarget,
        );
        if (match?.created_at) {
          const clientAdded = new Date(match.created_at);
          let nextRenewal = new Date(clientAdded);
          let yearsDiff = now.getFullYear() - clientAdded.getFullYear();
          let monthsDiff = now.getMonth() - clientAdded.getMonth();
          let totalMonths = yearsDiff * 12 + monthsDiff;
          nextRenewal.setMonth(nextRenewal.getMonth() + totalMonths);
          if (nextRenewal <= now) {
            nextRenewal.setMonth(nextRenewal.getMonth() + 1);
          }
          const diffMs = nextRenewal - now;
          const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
          if (diffDays > 0) {
            return `Next scan in ${diffDays} day${diffDays !== 1 ? "s" : ""}`;
          }
        }
      }

      if (userProfile?.subscription_end) {
        referenceDate = new Date(userProfile.subscription_end);
      } else if (userProfile?.subscription_start) {
        const start = new Date(userProfile.subscription_start);
        referenceDate = new Date(
          start.getFullYear(),
          start.getMonth() + 1,
          start.getDate(),
        );
      } else if (userProfile?.created_at) {
        const created = new Date(userProfile.created_at);
        referenceDate = new Date(
          created.getFullYear(),
          created.getMonth() + 1,
          created.getDate(),
        );
      }

      if (referenceDate && !isNaN(referenceDate.getTime())) {
        const diffMs = referenceDate - now;
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays > 0) {
          return `Next scan in ${diffDays} day${diffDays !== 1 ? "s" : ""}`;
        }
      }
    } catch (e) {
      console.warn("Error calculating reset date:", e);
    }
    return "Next scan in 30 days";
  };

  const getPlanScansLimit = () => {
    const plan = (userProfile?.plan || "guest").toLowerCase();
    if (plan === "expansion" || plan === "business" || userProfile?.is_admin)
      return Infinity;
    if (plan === "agency") return 20; // 20 scans per client website
    const baseLimit = { core: 1, growth: 20, pro: 20, guest: 1 }[plan] || 0;
    const extra = userProfile?.extra_scans || 0;
    return baseLimit + extra;
  };

  const getBillingCycleStart = (specificWebsite) => {
    try {
      let refDateStr =
        userProfile?.subscription_start || userProfile?.created_at;
      const targetWeb =
        specificWebsite || (userPlanKey === "agency" ? selectedWebsite : null);
      if (userPlanKey === "agency" && targetWeb && targetWeb !== "all") {
        const cleanTarget = cleanUrlJS(targetWeb);
        const match = agencyClients.find(
          (c) => cleanUrlJS(c.website) === cleanTarget,
        );
        if (match?.created_at) {
          refDateStr = match.created_at;
        }
      }
      if (!refDateStr) return new Date(0);
      const refDate = new Date(refDateStr);
      const now = new Date();

      let yearsDiff = now.getFullYear() - refDate.getFullYear();
      let monthsDiff = now.getMonth() - refDate.getMonth();
      let totalMonths = yearsDiff * 12 + monthsDiff;

      let candidate = new Date(refDate);
      candidate.setMonth(candidate.getMonth() + totalMonths);
      if (candidate > now) {
        candidate = new Date(refDate);
        candidate.setMonth(candidate.getMonth() + totalMonths - 1);
      }
      return candidate;
    } catch (e) {
      console.warn("Error calculating cycle start:", e);
      return new Date(0);
    }
  };

  const getSelectedClientScansUsed = () => {
    if (
      userPlanKey !== "agency" ||
      !selectedWebsite ||
      selectedWebsite === "all"
    ) {
      return 0;
    }
    const cycleStart = getBillingCycleStart(selectedWebsite);
    const cleanSelected = cleanUrlJS(selectedWebsite);

    // Filter history
    const clientHistory = history.filter((item) => {
      if (cleanUrlJS(item.website) !== cleanSelected) return false;
      const scanDate = new Date(item.created_at);
      return scanDate >= cycleStart;
    });

    return clientHistory.length;
  };

  const getSelectedClientUsage = () => {
    const defaultUsage = { schema_used: 0, robots_used: 0, llms_used: 0 };
    if (
      userPlanKey !== "agency" ||
      !selectedWebsite ||
      selectedWebsite === "all"
    ) {
      return defaultUsage;
    }

    try {
      const companyWebsite = userProfile?.company_website || "";
      if (companyWebsite.startsWith("{")) {
        const limitsData = JSON.parse(companyWebsite);
        if (limitsData && limitsData.limits) {
          const cleanSelected = cleanUrlJS(selectedWebsite);
          const clientData = limitsData.limits[cleanSelected];
          if (clientData) {
            // Check if we need to reset the counters in frontend (lazy reset display logic)
            const cycleStart = getBillingCycleStart(selectedWebsite);
            const lastResetStr = clientData.last_reset;
            let needsReset = false;
            if (!lastResetStr) {
              needsReset = true;
            } else {
              const lastReset = new Date(lastResetStr);
              if (lastReset < cycleStart) {
                needsReset = true;
              }
            }

            if (needsReset) {
              return defaultUsage;
            }

            return {
              schema_used: clientData.schema_used || 0,
              robots_used: clientData.robots_used || 0,
              llms_used: clientData.llms_used || 0,
            };
          }
        }
      }
    } catch (e) {
      console.warn("Error parsing agency client usage:", e);
    }
    return defaultUsage;
  };

  const getScansPercent = () => {
    const limit = getPlanScansLimit();
    if (limit === Infinity || limit === 0) return 0;
    if (
      userPlanKey === "agency" &&
      selectedWebsite &&
      selectedWebsite !== "all"
    ) {
      const scansUsed = getSelectedClientScansUsed();
      return Math.min(100, Math.max(0, (scansUsed / limit) * 100));
    }
    const scansUsed = userProfile?.scans_used || 0;
    return Math.min(100, Math.max(0, (scansUsed / limit) * 100));
  };

  const getUserInitials = () => {
    const name =
      userData?.user_metadata?.full_name ||
      userProfile?.company_name ||
      userData?.email ||
      "User";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const {
    openIssues: allReportIssues,
    fixedIssues: fixedReportIssues,
    recommendations: dynamicRecommendations,
  } = getDynamicReportData();

  // Calculate Google Analytics variables for SVG rendering
  const gaDailyPoints = analyticsData?.daily || [];
  const gaMaxVal =
    gaDailyPoints.length > 0
      ? Math.max(
          ...gaDailyPoints.map((d) =>
            Math.max(d.users || 0, d.sessions || 0, d.views || 0),
          ),
          10,
        )
      : 10;

  const getGAPathD = (key) => {
    if (gaDailyPoints.length < 2) return "";
    return gaDailyPoints
      .map((dp, idx) => {
        const x = (idx / (gaDailyPoints.length - 1)) * 1000;
        const y = 92 - ((dp[key] || 0) * 84) / gaMaxVal;
        return `${idx === 0 ? "M" : "L"} ${x} ${y}`;
      })
      .join(" ");
  };


  if (error && !userData) {
    return (
      <div
        className="dashboard-error-container"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "var(--bg-body, #0a0e17)",
          color: "var(--text-light, #ffffff)",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            maxWidth: "450px",
            padding: "45px 30px",
            borderRadius: "16px",
            backgroundColor: "var(--dashboard-plan-card, #161d28)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
            border: "1px solid rgba(255, 255, 255, 0.05)",
          }}
        >
          <FiAlertTriangle size={48} color="#ff3366" className="mb-4" />
          <h3 className="mb-3" style={{ fontWeight: 600 }}>
            Failed to Load Dashboard
          </h3>
          <p
            className="text-muted mb-4"
            style={{ fontSize: "14px", lineHeight: "1.6" }}
          >
            {error}
          </p>
          <div className="d-flex flex-column gap-2">
            <Button variant="primary" onClick={() => fetchDashboardData()}>
              Retry Connection
            </Button>
            <Button variant="outline-light" onClick={() => navigate("/auth")}>
              Go to Login Page
            </Button>
          </div>
        </div>
      </div>
    );
  }
  // Calculate AI Traffic variables for SVG rendering
  const aiDailyPoints = analyticsData?.ai_traffic?.daily || [];
  const aiMaxVal =
    aiDailyPoints.length > 0
      ? Math.max(...aiDailyPoints.map((d) => d.sessions || 0), 10)
      : 10;

  const getAIPathD = () => {
    if (aiDailyPoints.length < 2) return "";
    return aiDailyPoints
      .map((dp, idx) => {
        const x = (idx / (aiDailyPoints.length - 1)) * 1000;
        const y = 92 - ((dp.sessions || 0) * 84) / aiMaxVal;
        return `${idx === 0 ? "M" : "L"} ${x} ${y}`;
      })
      .join(" ");
  };

  const getAIFillPathD = () => {
    if (aiDailyPoints.length < 2) return "";
    const pathD = getAIPathD();
    return `${pathD} L 1000 100 L 0 100 Z`;
  };

  const agentData = analyticsData?.ai_traffic?.top_agents || [];

  // Calculate Google Search Console variables for SVG rendering
  const gscDailyPoints = gscData?.daily || [];
  const gscMaxClicks =
    gscDailyPoints.length > 0
      ? Math.max(...gscDailyPoints.map((d) => d.clicks || 0), 10)
      : 10;
  const gscMaxImpressions =
    gscDailyPoints.length > 0
      ? Math.max(...gscDailyPoints.map((d) => d.impressions || 0), 10)
      : 10;

  const getGSCPathD = (key, maxVal) => {
    if (gscDailyPoints.length < 2) return "";
    return gscDailyPoints
      .map((dp, idx) => {
        const x = (idx / (gscDailyPoints.length - 1)) * 1000;
        const y = 92 - ((dp[key] || 0) * 84) / maxVal;
        return `${idx === 0 ? "M" : "L"} ${x} ${y}`;
      })
      .join(" ");
  };

  const activeProfile = getModifiedProfileForClient();
  const gaActive =
    googleAnalyticsConnected && activeProfile?.property_id && analyticsData;
  const gscActive =
    googleAnalyticsConnected && (activeProfile?.gsc_site_url || gscSiteUrl);
  const showGA = activeIntegrationTab === "analytics" && gaActive;
  const showGSC = activeIntegrationTab === "search-console" && gscActive;

  return (
    <div className="dashboard-layout">
      {/* ── Mobile & Desktop Sidebar Backdrop ── */}
      {!sidebarCollapsed && (
        <div
          className="dashboard-sidebar-backdrop"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* ── Left Navigation Sidebar Drawer ── */}
      <aside
        className={`dashboard-sidebar ${sidebarCollapsed ? "collapsed" : "expanded"}`}
      >
        <div className="sidebar-brand">
          <div className="brand-logo-wrap">
            <div className="brand-logo-icon">
              <img src="/faviconSeenbyai.jpeg" alt="Seenbyai" />
            </div>
            <span className="brand-title">SeenByAI</span>
          </div>
          <button
            className="sidebar-collapse-btn"
            onClick={() => setSidebarCollapsed(true)}
            title="Collapse Sidebar Drawer"
            aria-label="Collapse Sidebar Drawer"
          >
            <FiChevronLeft size={18} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-group">
            <div className="group-label">AI VISIBILITY</div>
            <a
              href="#dashboard"
              className={`nav-item ${activeTab === "dashboard" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("dashboard");
                if (window.innerWidth <= 860) setSidebarCollapsed(true);
              }}
            >
              <FiGrid className="item-icon" />
              <span>Dashboard</span>
            </a>
            <a
              href="#report"
              className={`nav-item ${activeTab === "report" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("report");
                if (window.innerWidth <= 860) setSidebarCollapsed(true);
              }}
            >
              <FiFileText className="item-icon" />
              <span>AI Readiness</span>
            </a>
            <a
              href="#monthly-report"
              className={`nav-item ${activeTab === "monthly-report" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("monthly-report");
                if (window.innerWidth <= 860) setSidebarCollapsed(true);
              }}
            >
              <FiCalendar className="item-icon" />
              <span>Monthly Report</span>
            </a>
            {/* <a
              href="#readiness"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiTrendingUp className="item-icon" />
              <span>AI Readiness</span>
            </a> */}
            {/* <a
              href="#authority"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiCheckCircle className="item-icon" />
              <span>AI Authority</span>
            </a> */}
            {/* <a
              href="#recommendation"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiZap className="item-icon" />
              <span>Recommendation</span>
              <span className="beta-badge">Beta</span>
            </a> */}
            {/* <a
              href="#comparisons"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiLayers className="item-icon" />
              <span>Comparisons</span>
            </a> */}
          </div>

          <div className="nav-group">
            <div className="group-label">CONTENT INTELLIGENCE</div>
            <a
              href="#content"
              className={`nav-item ${activeTab === "knowledge" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("knowledge");
                if (window.innerWidth <= 860) setSidebarCollapsed(true);
              }}
            >
              <FiFileText className="item-icon" />
              <span>AI Knowledge Engine</span>
            </a>
            {/* <a
              href="#linking"
              className={`nav-item ${activeTab === "linking" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("linking");
                if (window.innerWidth <= 860) setSidebarCollapsed(true);
              }}
            >
              <FiLink className="item-icon" />
              <span>Internal Linking</span>
            </a> */}
            <a
              href="#analytics"
              className={`nav-item ${activeTab === "analytics" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("analytics");
                if (window.innerWidth <= 860) setSidebarCollapsed(true);
              }}
            >
              <svg
                className="item-icon"
                viewBox="0 0 256 284"
                width="16"
                height="16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: "0px", flexShrink: 0 }}
              >
                <path
                  d="M256.003159,247.933017 C256.055907,258.030289 251.77298,267.664804 244.241349,274.390297 C236.709718,281.11579 226.653817,284.285366 216.626905,283.094249 C198.58347,280.424364 185.360959,264.722632 185.800619,246.488035 L185.800619,36.8452103 C185.364944,18.5907614 198.619678,2.88144681 216.687112,0.238996295 C226.704325,-0.933476157 236.743571,2.24455542 244.261279,8.9678962 C251.778988,15.691237 256.053811,25.3147619 256.003159,35.4002282 L256.003159,247.933017 Z"
                  fill="currentColor"
                ></path>
                <path
                  d="M35.1010243,213.193238 C54.4867848,213.193238 70.2020487,228.908502 70.2020487,248.294263 C70.2020487,267.680023 54.4867848,283.395287 35.1010243,283.395287 C15.7152639,283.395287 0,267.680023 0,248.294263 C0,228.908502 15.7152639,213.193238 35.1010243,213.193238 Z M127.459466,106.806429 C107.981896,107.874068 92.8698765,124.212107 93.3217628,143.713681 L93.3217628,237.998765 C93.3217628,263.58699 104.580582,279.120548 121.077461,282.431965 C131.434034,284.530959 142.185473,281.860819 150.356699,275.160414 C158.527925,268.460009 163.252393,258.439904 163.222912,247.872809 L163.222912,142.088076 C163.240039,132.641687 159.462041,123.584285 152.737293,116.950107 C146.012546,110.315928 136.904752,106.661084 127.459466,106.806429 L127.459466,106.806429 Z"
                  fill="currentColor"
                ></path>
              </svg>
              <span>Google Analytics</span>
            </a>
            <a
              href="#search-console"
              className={`nav-item ${activeTab === "search-console" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("search-console");
                if (window.innerWidth <= 860) setSidebarCollapsed(true);
              }}
            >
              <svg
                className="item-icon"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: "0px", flexShrink: 0 }}
              >
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              <span>Google Search Console</span>
            </a>
            <a
              href="#help-support"
              className={`nav-item ${activeTab === "help-support" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("help-support");
                if (window.innerWidth <= 860) setSidebarCollapsed(true);
              }}
            >
              <FiHelpCircle className="item-icon" />
              <span>Help & Support</span>
            </a>
          </div>

          {(userPlanKey === "agency" || (userProfile?.extra_clients > 0) || agencyClients.length > 0 || isAdmin) && (
            <div className="nav-group">
              <div className="group-label">AGENCY PANEL</div>
              <a
                href="#clients"
                className={`nav-item ${activeTab === "clients" ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("clients");
                  if (window.innerWidth <= 860) setSidebarCollapsed(true);
                }}
              >
                <FiUserCheck className="item-icon" />
                <span>Manage Clients</span>
              </a>
            </div>
          )}
          {/* 
          <div className="nav-group">
            <div className="group-label">TOOLS</div>
            <a
              href="#schema"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiTool className="item-icon" />
              <span>Schema Generator</span>
            </a>
            <a
              href="#llms"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiFileText className="item-icon" />
              <span>llms.txt Generator</span>
            </a>
            <a
              href="#robots"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiSearch className="item-icon" />
              <span>Robots.txt Analyzer</span>
            </a>
            <a
              href="#entity"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiDatabase className="item-icon" />
              <span>Entity Explorer</span>
            </a>
            <a
              href="#prompt"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiZap className="item-icon" />
              <span>AI Prompt Tester</span>
            </a>
          </div> */}

          {/* <div className="nav-group">
            <div className="group-label">REPORTS</div>
            <a
              href="#reports"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiFileText className="item-icon" />
              <span>Reports</span>
            </a>
            <a
              href="#whitelabel"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiLayers className="item-icon" />
              <span>White-Label Reports</span>
            </a>
          </div> */}

          {/* <div className="nav-group">
            <div className="group-label">SETTINGS</div>
            <a
              href="#settings"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiSettings className="item-icon" />
              <span>Settings</span>
            </a>
            <a
              href="#integrations"
              className="nav-item"
              onClick={(e) => e.preventDefault()}
            >
              <FiLayers className="item-icon" />
              <span>Integrations</span>
            </a>
          </div> */}
        </nav>

        <div className="sidebar-footer-wrapper">
          {/* Plan Card */}
          <div className="plan-widget-card">
            <div className="plan-widget-header">
              <span className="plan-widget-label">Your Plan</span>
              <span className="plan-widget-reset-timer">
                {getDaysUntilReset()}
              </span>
            </div>

            <div className="plan-widget-name">
              {userProfile?.is_admin ? "Administrator" : currentPlanLabel}
            </div>

            {userPlanKey === "agency" &&
            selectedWebsite &&
            selectedWebsite !== "all" ? (
              <div
                className="agency-client-usage-details mt-3"
                style={{ fontSize: "12px" }}
              >
                <div
                  className="text-white fw-semibold mb-2"
                  style={{
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    paddingBottom: "4px",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontSize: "14px" }}>🏢</span>
                  <span title={selectedWebsite}>{selectedWebsite}</span>
                </div>

                {/* Scans */}
                <div className="mb-2">
                  <div
                    className="d-flex justify-content-between mb-1"
                    style={{ fontSize: "11px" }}
                  >
                    <span style={{ color: "#94a3b8", fontWeight: 500 }}>Scans Used</span>
                    <span style={{ color: "#ffffff", fontWeight: 600 }}>
                      {getSelectedClientScansUsed()} / 20
                    </span>
                  </div>
                  <div
                    className="plan-widget-progress-container"
                    style={{
                      height: "6px",
                      background: "rgba(255,255,255,0.12)",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="plan-widget-progress-bar"
                      style={{
                        width: `${Math.min(100, (getSelectedClientScansUsed() / 20) * 100)}%`,
                        height: "100%",
                        borderRadius: "3px",
                        background: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
                      }}
                    />
                  </div>
                </div>

                {/* Schema */}
                <div className="mb-2">
                  <div
                    className="d-flex justify-content-between mb-1"
                    style={{ fontSize: "11px" }}
                  >
                    <span style={{ color: "#94a3b8", fontWeight: 500 }}>Schema Gens</span>
                    <span style={{ color: "#ffffff", fontWeight: 600 }}>
                      {getSelectedClientUsage().schema_used} / 10
                    </span>
                  </div>
                  <div
                    className="plan-widget-progress-container"
                    style={{
                      height: "6px",
                      background: "rgba(255,255,255,0.12)",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="plan-widget-progress-bar"
                      style={{
                        width: `${Math.min(100, (getSelectedClientUsage().schema_used / 10) * 100)}%`,
                        height: "100%",
                        borderRadius: "3px",
                        background: "linear-gradient(90deg, #10b981, #3b82f6)",
                      }}
                    />
                  </div>
                </div>

                {/* Robots.txt */}
                <div className="mb-2">
                  <div
                    className="d-flex justify-content-between mb-1"
                    style={{ fontSize: "11px" }}
                  >
                    <span style={{ color: "#94a3b8", fontWeight: 500 }}>robots.txt Gens</span>
                    <span style={{ color: "#ffffff", fontWeight: 600 }}>
                      {getSelectedClientUsage().robots_used} / 10
                    </span>
                  </div>
                  <div
                    className="plan-widget-progress-container"
                    style={{
                      height: "6px",
                      background: "rgba(255,255,255,0.12)",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="plan-widget-progress-bar"
                      style={{
                        width: `${Math.min(100, (getSelectedClientUsage().robots_used / 10) * 100)}%`,
                        height: "100%",
                        borderRadius: "3px",
                        background: "linear-gradient(90deg, #f59e0b, #10b981)",
                      }}
                    />
                  </div>
                </div>

                {/* LLMs.txt */}
                <div className="mb-3">
                  <div
                    className="d-flex justify-content-between mb-1"
                    style={{ fontSize: "11px" }}
                  >
                    <span style={{ color: "#94a3b8", fontWeight: 500 }}>llms.txt Gens</span>
                    <span style={{ color: "#ffffff", fontWeight: 600 }}>
                      {getSelectedClientUsage().llms_used} / 10
                    </span>
                  </div>
                  <div
                    className="plan-widget-progress-container"
                    style={{
                      height: "6px",
                      background: "rgba(255,255,255,0.12)",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="plan-widget-progress-bar"
                      style={{
                        width: `${Math.min(100, (getSelectedClientUsage().llms_used / 10) * 100)}%`,
                        height: "100%",
                        borderRadius: "3px",
                        background: "linear-gradient(90deg, #ec4899, #f59e0b)",
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                {getPlanScansLimit() !== Infinity ? (
                  <>
                    <div className="plan-widget-progress-container">
                      <div
                        className="plan-widget-progress-bar"
                        style={{ width: `${getScansPercent()}%` }}
                      />
                    </div>
                    <div className="plan-widget-usage-text">
                      <span>Scans used</span>
                      <span className="plan-widget-usage-count">
                        <strong>{userProfile?.scans_used || 0}</strong> /{" "}
                        {getPlanScansLimit()}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="plan-widget-usage-text">
                    <span>Scans used</span>
                    <span className="plan-widget-usage-count">
                      <strong>Unlimited</strong>
                    </span>
                  </div>
                )}
              </>
            )}

            <button
              className="plan-widget-upgrade-btn"
              onClick={() => navigate("/account")}
            >
              {hasPaidPlan ? "Manage Plan" : "Upgrade Plan"}
            </button>
          </div>

          {/* User Profile Dropup Widget */}
          <div className="sidebar-user-widget">
            <div
              className={`user-widget-trigger ${profileDropdownOpen ? "active" : ""}`}
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            >
              <div className="user-avatar-circle">{getUserInitials()}</div>
              <div className="user-info-stack">
                <div className="user-info-name">
                  {userData?.user_metadata?.full_name ||
                    userData?.email?.split("@")[0] ||
                    "User"}
                </div>
                <div className="user-info-org">
                  {userProfile?.company_name || "SeenByAI LLC"}
                </div>
              </div>
              <div className="user-chevron-arrow">
                <FiChevronRight
                  style={{
                    transform: profileDropdownOpen
                      ? "rotate(-90deg)"
                      : "rotate(90deg)",
                    transition: "transform 0.2s ease",
                  }}
                  size={16}
                />
              </div>
            </div>

            {/* Dropup Actions Menu */}
            {profileDropdownOpen && (
              <>
                <div
                  className="sidebar-user-dropup-backdrop"
                  onClick={() => setProfileDropdownOpen(false)}
                />
                <div className="sidebar-user-dropup-menu">
                  <a
                    href="#dashboard"
                    className="sidebar-user-dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("dashboard");
                      setProfileDropdownOpen(false);
                    }}
                  >
                    <FiGrid className="sidebar-user-item-icon" /> Dashboard
                  </a>
                  <a
                    href="#history"
                    className="sidebar-user-dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/history");
                      setProfileDropdownOpen(false);
                    }}
                  >
                    <FiClock className="sidebar-user-item-icon" /> Scan History
                  </a>
                  <a
                    href="#account"
                    className="sidebar-user-dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/account");
                      setProfileDropdownOpen(false);
                    }}
                  >
                    <FiUserCheck className="sidebar-user-item-icon" /> My
                    Account
                  </a>
                  <div className="sidebar-user-dropdown-divider" />
                  <button
                    className="sidebar-user-dropdown-item logout-btn"
                    onClick={async () => {
                      setProfileDropdownOpen(false);
                      try {
                        await Promise.race([
                          supabase.auth.signOut(),
                          new Promise((resolve) => setTimeout(resolve, 500)),
                        ]);
                      } catch (err) {
                        console.warn(
                          "Sidebar User Widget: error signing out:",
                          err,
                        );
                      } finally {
                        clearSupabaseAuthStorage();
                        window.location.href = "/auth";
                      }
                    }}
                  >
                    <FiLogOut className="sidebar-user-item-icon" /> Log Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* ── Main Content Area ── */}

      <main className="dashboard-main">
        {/* Top Header Bar */}
        <header className="main-header">
          <button
            className="header-sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={
              sidebarCollapsed
                ? "Expand sidebar drawer"
                : "Collapse sidebar drawer"
            }
            aria-label="Toggle sidebar drawer"
          >
            {sidebarCollapsed ? (
              <div className="toggle-btn-inner">
                <FiMenu size={18} />
                <FiChevronRight size={16} />
              </div>
            ) : (
              <div className="toggle-btn-inner">
                <FiChevronLeft size={18} />
              </div>
            )}
          </button>

          <div className="header-title-area">
            {activeTab === "search-console" ? (
              <>
                <h1 className="welcome-heading">
                  Welcome back, {userName}! 👋
                </h1>
                <p className="welcome-sub">
                  Here's your Google Search Console overview for{" "}
                  <span className="domain-highlight">{companyDomain}</span>
                </p>
              </>
            ) : activeTab === "knowledge" ? (
              <>
                <h1
                  className="welcome-heading"
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  AI Knowledge Engine
                  <FiInfo
                    className="info-icon"
                    style={{
                      verticalAlign: "middle",
                      fontSize: "16px",
                      cursor: "help",
                    }}
                    title="Generate and optimize the knowledge assets AI systems use to understand and recommend your business."
                  />
                </h1>
                <p className="welcome-sub">
                  Generate and optimize the knowledge assets AI systems use to
                  understand and recommend your business.
                </p>
              </>
            ) : activeTab === "linking" ? (
              <>
                <h1
                  className="welcome-heading"
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  Internal Linking Architecture
                  <FiInfo
                    className="info-icon"
                    style={{
                      verticalAlign: "middle",
                      fontSize: "16px",
                      cursor: "help",
                    }}
                    title="Audit crawl hierarchy, topic clusters, in-body contextual links, and anchor text distribution."
                  />
                </h1>
                <p className="welcome-sub">
                  Audit crawl hierarchy, topic clusters, in-body contextual links, and anchor text distribution for{" "}
                  <span className="domain-highlight">{companyDomain}</span>
                </p>
              </>
            ) : (
              <>
                <h1 className="welcome-heading">
                  Welcome back, {userName}! 👋
                </h1>
                <p className="welcome-sub">
                  Here's your AI Visibility overview for{" "}
                  <span className="domain-highlight">{companyDomain}</span>
                </p>
              </>
            )}
          </div>

          <div className="header-actions">
            {/* Website Selector Custom Dropdown (Admin, Agency, or multiple sites) */}
            {((isAdmin && uniqueWebsites.length > 0) ||
              (userPlanKey === "agency" && agencyClients.length > 0) ||
              uniqueWebsites.length > 1) &&
              (() => {
                const rawWebsiteOptions =
                  userPlanKey === "agency"
                    ? agencyClients
                        .filter((client) => client && client.website && !client.website.trim().startsWith("{"))
                        .map((client) => ({
                            value: client.website,
                            label: client.website
                              .replace(/^https?:\/\//, "")
                              .replace(/\/$/, ""),
                            isAll: false,
                          }))
                    : [
                        {
                          value: "all",
                          label: `All Websites (${uniqueWebsites.length})`,
                          isAll: true,
                        },
                        ...uniqueWebsites.map((site) => ({
                          value: site,
                          label: site
                            .replace(/^https?:\/\//, "")
                            .replace(/\/$/, ""),
                          isAll: false,
                        })),
                      ];

                const filteredOptions = rawWebsiteOptions.filter(
                  (opt) =>
                    opt.label
                      .toLowerCase()
                      .includes(websiteSearchQuery.toLowerCase()) ||
                    (opt.value &&
                      opt.value
                        .toLowerCase()
                        .includes(websiteSearchQuery.toLowerCase())),
                );

                const currentOption = rawWebsiteOptions.find(
                  (opt) => opt.value === selectedWebsite,
                );
                const displayLabel = currentOption
                  ? currentOption.label
                  : selectedWebsite === "all"
                    ? `All Websites (${uniqueWebsites.length})`
                    : selectedWebsite
                        .replace(/^https?:\/\//, "")
                        .replace(/\/$/, "");

                return (
                  <div
                    className="custom-dropdown-container"
                    ref={websiteDropdownRef}
                  >
                    <button
                      type="button"
                      className={`custom-picker-btn website-picker-trigger ${websiteDropdownOpen ? "active" : ""}`}
                      onClick={() => {
                        setWebsiteDropdownOpen(!websiteDropdownOpen);
                        setDateDropdownOpen(false);
                      }}
                      aria-haspopup="listbox"
                      aria-expanded={websiteDropdownOpen}
                      title="Select Website"
                    >
                      <FiGlobe className="custom-picker-icon" />
                      <span className="custom-picker-label">
                        {displayLabel}
                      </span>
                      <FiChevronDown className="custom-picker-chevron" />
                    </button>

                    {websiteDropdownOpen && (
                      <div
                        className="custom-dropdown-popover website-dropdown-popover"
                        role="listbox"
                      >
                        <div className="custom-dropdown-header">
                          <span className="custom-dropdown-header-title">
                            {userPlanKey === "agency"
                              ? "Agency Clients"
                              : "Select Property"}
                          </span>
                          <span className="custom-dropdown-badge">
                            {rawWebsiteOptions.length}{" "}
                            {rawWebsiteOptions.length === 1 ? "Site" : "Sites"}
                          </span>
                        </div>

                        {rawWebsiteOptions.length > 3 && (
                          <div className="custom-dropdown-search-wrap">
                            <FiSearch className="custom-dropdown-search-icon" />
                            <input
                              type="text"
                              className="custom-dropdown-search-input"
                              placeholder="Filter websites..."
                              value={websiteSearchQuery}
                              onChange={(e) =>
                                setWebsiteSearchQuery(e.target.value)
                              }
                              autoFocus
                            />
                            {websiteSearchQuery && (
                              <button
                                type="button"
                                className="custom-dropdown-search-clear"
                                onClick={() => setWebsiteSearchQuery("")}
                                aria-label="Clear search"
                              >
                                <FiX />
                              </button>
                            )}
                          </div>
                        )}

                        <div className="custom-dropdown-list">
                          {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt) => {
                              const isSelected = selectedWebsite === opt.value;
                              return (
                                <div
                                  key={opt.value}
                                  className={`custom-dropdown-item ${isSelected ? "selected" : ""}`}
                                  role="option"
                                  aria-selected={isSelected}
                                  onClick={() => {
                                    setSelectedWebsite(opt.value);
                                    setWebsiteDropdownOpen(false);
                                    setWebsiteSearchQuery("");
                                  }}
                                >
                                  <div className="custom-dropdown-item-left">
                                    <div className="custom-dropdown-item-icon">
                                      {opt.isAll ? (
                                        <FiLayers size={13} />
                                      ) : (
                                        <FiGlobe size={13} />
                                      )}
                                    </div>
                                    <span
                                      className="custom-dropdown-item-text"
                                      title={opt.label}
                                    >
                                      {opt.label}
                                    </span>
                                  </div>
                                  {isSelected && (
                                    <FiCheck className="custom-dropdown-item-check" />
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="custom-dropdown-empty">
                              No matching websites
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

            {/* Custom Date Picker Dropdown */}
            <div className="custom-dropdown-container" ref={dateDropdownRef}>
              {(() => {
                const currentPreset =
                  DATE_RANGE_PRESETS.find((p) => p.id === selectedPeriod) ||
                  DATE_RANGE_PRESETS[1];
                const buttonLabel =
                  currentPreset.id === "all"
                    ? "All Time"
                    : formatPresetDateRange(currentPreset.days);

                return (
                  <>
                    <button
                      type="button"
                      className={`custom-picker-btn date-picker-trigger ${dateDropdownOpen ? "active" : ""}`}
                      onClick={() => {
                        setDateDropdownOpen(!dateDropdownOpen);
                        setWebsiteDropdownOpen(false);
                      }}
                      aria-haspopup="listbox"
                      aria-expanded={dateDropdownOpen}
                      title={`${currentPreset.label}: ${formatPresetDateRange(currentPreset.days)}`}
                    >
                      <FiCalendar className="custom-picker-icon" />
                      <span className="custom-picker-label">{buttonLabel}</span>
                      <FiChevronDown className="custom-picker-chevron" />
                    </button>

                    {dateDropdownOpen && (
                      <div
                        className="custom-dropdown-popover date-dropdown-popover"
                        role="listbox"
                      >
                        <div className="custom-dropdown-header">
                          <span className="custom-dropdown-header-title">
                            Date Range
                          </span>
                        </div>
                        <div className="custom-dropdown-list">
                          {DATE_RANGE_PRESETS.map((preset) => {
                            const isSelected = selectedPeriod === preset.id;
                            const rangeSubtext = formatPresetDateRange(
                              preset.days,
                            );

                            return (
                              <div
                                key={preset.id}
                                className={`custom-dropdown-item ${isSelected ? "selected" : ""}`}
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => handleSelectDateRange(preset.id)}
                              >
                                <div className="custom-dropdown-item-left">
                                  <div className="custom-dropdown-item-icon">
                                    <FiCalendar size={13} />
                                  </div>
                                  <div className="custom-dropdown-item-text-wrap">
                                    <span className="custom-dropdown-item-title">
                                      {preset.label}
                                    </span>
                                    <span className="custom-dropdown-item-subtext">
                                      {rangeSubtext}
                                    </span>
                                  </div>
                                </div>
                                {isSelected && (
                                  <FiCheck className="custom-dropdown-item-check" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <button
              className="btn-run-scan"
              onClick={() =>
                navigate("/scanner", {
                  state: {
                    website:
                      selectedWebsite && selectedWebsite !== "all"
                        ? selectedWebsite
                        : "",
                    company: latestScan?.company || "",
                  },
                })
              }
            >
              <FiPlus className="me-1" /> Run New Scan
            </button>

            <button
              className="theme-toggle-btn"
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
              title={
                theme === "light"
                  ? "Switch to Dark Mode"
                  : "Switch to Light Mode"
              }
              aria-label="Toggle theme mode"
            >
              {theme === "light" ? <FiMoon size={18} /> : <FiSun size={18} />}
            </button>

            <Dropdown align="end">
              <Dropdown.Toggle
                as="div"
                className="user-avatar-badge"
                title="Account Options"
              >
                <FiUser size={18} />
              </Dropdown.Toggle>
              <Dropdown.Menu className="topbar-user-dropdown-menu mt-2">
                <Dropdown.Item
                  onClick={() => navigate("/user-dashboard")}
                  className="topbar-user-dropdown-item"
                >
                  <FiGrid className="topbar-dropdown-icon" /> Dashboard
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => navigate("/history")}
                  className="topbar-user-dropdown-item"
                >
                  <FiClock className="topbar-dropdown-icon" /> Scan History
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => navigate("/account")}
                  className="topbar-user-dropdown-item"
                >
                  <FiUserCheck className="topbar-dropdown-icon" /> My Account
                </Dropdown.Item>
                {isAdmin && (
                  <Dropdown.Item
                    onClick={() => navigate("/master-admin")}
                    className="topbar-user-dropdown-item"
                  >
                    <FiShield className="topbar-dropdown-icon" /> Master Admin
                  </Dropdown.Item>
                )}
                <Dropdown.Divider className="topbar-dropdown-divider" />
                <Dropdown.Item
                  onClick={handleLogout}
                  className="topbar-user-dropdown-item logout-item"
                >
                  <FiLogOut className="topbar-dropdown-icon" /> Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </header>

        {!hasPaidPlan && (
          <div className="guest-upgrade-banner mx-4 mt-3 p-3 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div className="upgrade-icon-wrap">
                <FiZap size={20} className="text-warning" />
              </div>
              <div>
                <strong
                  className="d-block text-dark-console"
                  style={{ fontSize: "14px" }}
                >
                  Guest Session — No Active Plan
                </strong>
                <span className="text-muted" style={{ fontSize: "12px" }}>
                  Upgrade your plan to unlock full AI readiness audits and
                  custom optimization tools.
                </span>
              </div>
            </div>
            <button
              className="btn btn-warning btn-sm fw-bold text-dark px-3 py-2 ms-3 flex-shrink-0"
              onClick={() => navigate("/pricing")}
            >
              Upgrade your plan
            </button>
          </div>
        )}

        {error && (
          <div className="premium-alert premium-alert-danger mx-4 mt-3 mb-0">
            <div className="premium-alert-body">
              <div className="premium-alert-icon-container">
                <FiAlertTriangle className="premium-alert-icon" />
              </div>
              <div className="premium-alert-message">{error}</div>
            </div>
            <button
              type="button"
              className="premium-alert-close"
              onClick={() => setError(null)}
              aria-label="Close alert"
            >
              <FiX />
            </button>
          </div>
        )}

        {successMessage && (
          <div className="premium-alert premium-alert-success mx-4 mt-3 mb-0">
            <div className="premium-alert-body">
              <div className="premium-alert-icon-container">
                <FiCheckCircle className="premium-alert-icon" />
              </div>
              <div className="premium-alert-message">
                {(() => {
                  if (successMessage.includes("Subscription confirmed!")) {
                    const parts = successMessage.split("SeenByAI");
                    if (parts.length > 1) {
                      return (
                        <>
                          {parts[0]}
                          <span className="fw-semibold premium-alert-highlight">
                            SeenByAI
                          </span>
                          <span className="premium-plan-badge ms-2">
                            {parts[1].trim()}
                          </span>
                        </>
                      );
                    }
                  }
                  return successMessage;
                })()}
              </div>
            </div>
            <button
              type="button"
              className="premium-alert-close"
              onClick={() => setSuccessMessage(null)}
              aria-label="Close alert"
            >
              <FiX />
            </button>
          </div>
        )}

        {!initialDataLoaded ? (
          <DashboardSkeleton showSidebar={false} showHeader={false} />
        ) : activeTab === "knowledge" ? (
          userPlanKey === "core" ||
          userPlanKey === "guest" ||
          userPlanKey === "" ? (
            <div
              className="audit-card p-5 text-center d-flex flex-column align-items-center justify-content-center"
              style={{
                minHeight: "400px",
                border: "1px solid rgba(255, 255, 255, 0.05)",
                borderRadius: "12px",
                background: "rgba(255, 255, 255, 0.02)",
              }}
            >
              <div
                className="premium-lock-icon-container mb-4"
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  boxShadow: "0 0 15px rgba(59, 130, 246, 0.2)",
                }}
              >
                <FiLock size={26} className="text-primary" />
              </div>
              <h3
                className="fw-bold text-white mb-3"
                style={{ fontSize: "22px" }}
              >
                Unlock AI Knowledge Engine
              </h3>
              <p
                className="text-muted mb-4"
                style={{
                  fontSize: "14px",
                  maxWidth: "520px",
                  lineHeight: "1.6",
                }}
              >
                Core plan users do not have access to the AI Knowledge Engine.
                Upgrade to SeenByAI Growth to automatically generate, deploy,
                and update structured Schema markup, robots.txt crawl
                directives, and llms.txt files that make your business readable
                and recommendable by LLMs.
              </p>
              <button
                className="btn btn-primary fw-semibold px-4 py-2.5"
                onClick={() => navigate("/pricing")}
                style={{ borderRadius: "8px", fontSize: "15px" }}
              >
                Upgrade to Growth for $79.99/mo
              </button>
            </div>
          ) : (
            <Suspense fallback={<TabSkeleton type="knowledge" />}>
              <AIKnowledgeEngine
                companyDomain={companyDomain}
                latestScan={latestScan}
                userProfile={getModifiedProfileForClient()}
                refreshProfile={fetchDashboardData}
              />
            </Suspense>
          )
        ) : activeTab === "report" ? (
          !latestScan ? (
            <div className="audit-report-container p-4">
              <div className="audit-card text-center py-5">
                <div className="mb-3">
                  <LuSparkle size={44} style={{ color: "#10b981" }} />
                </div>
                <h3 className="text-white mb-2">
                  No AI Readiness Report Found
                </h3>
                <p
                  className="text-muted mb-4"
                  style={{ maxWidth: 480, margin: "0 auto" }}
                >
                  {selectedWebsite && selectedWebsite !== "all"
                    ? `No scan has been performed yet for ${cleanUrlJS(selectedWebsite)}. Run an AI readiness scan to generate your comprehensive audit report, Schema analysis, and crawler recommendations.`
                    : "Please select a website or run your first AI readiness scan to generate an audit report."}
                </p>
                <Button
                  variant="primary"
                  onClick={() =>
                    navigate("/scanner", {
                      state: {
                        website:
                          selectedWebsite && selectedWebsite !== "all"
                            ? selectedWebsite
                            : "",
                      },
                    })
                  }
                >
                  Run First Scan Now
                </Button>
              </div>
            </div>
          ) : (
            <div className="audit-report-container p-4">
              {/* Audit Header Bar */}
              <div className="audit-header-bar">
                <div className="audit-header-left">
                  <h2 className="audit-page-title">AI Readiness Report</h2>
                  <div className="audit-breadcrumbs">
                    <span>Home</span> &gt; <span>Audit</span> &gt;{" "}
                    <span className="active-date">
                      {new Date(
                        latestScan?.created_at || Date.now(),
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                </div>
                <div className="audit-header-actions">
                  <button
                    className="btn-audit-outline"
                    onClick={handleDownloadPDF}
                    disabled={pdfLoading}
                    title="Download PDF Report"
                  >
                    <FiDownload className="me-1" />{" "}
                    {pdfLoading ? "Downloading..." : "Download Report"}
                  </button>
                  {/* <button
                  className="btn-audit-primary"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success("Report share link copied to clipboard!");
                  }}
                >
                  <FiShare2 className="me-1" /> Share
                </button> */}
                </div>
              </div>

              {/* Top Audit Summary Section (Gauge + 4 Metric Cards) */}
              <div className="audit-summary-grid mt-4">
                {/* Left Score Gauge Card */}
                <div className="audit-card audit-gauge-card">
                  <div className="gauge-wrap">
                    <div className="gauge-score-display">
                      <span className="big-score">{rawScore}</span>
                      <span className="score-denom">/100</span>
                    </div>
                    <div className="score-tier-badge">
                      {latestScan?.scan_data?.ai_readiness_tier || "Good"}
                    </div>
                    <div className="score-delta-sub green">
                      {scoreDeltaText} vs. last audit
                    </div>
                  </div>
                  <button
                    className="btn-historical-audits mt-3"
                    onClick={() => setActiveTab("dashboard")}
                  >
                    View Historical Audits
                  </button>
                </div>

                {/* Right 4 Stat Metric Cards */}
                <div className="audit-stats-grid">
                  {/* 1. Pages Crawled */}
                  <div className="stat-card">
                    <span className="stat-label">Pages Crawled</span>
                    <div
                      className="stat-val-row"
                      style={{ zIndex: 2, position: "relative" }}
                    >
                      <span className="stat-number">
                        {getCrawledPagesList(latestScan).length}
                      </span>
                      <span
                        className="stat-trend green"
                        style={{
                          color: "#3b82f6",
                          backgroundColor: "rgba(59, 130, 246, 0.1)",
                        }}
                      >
                        {getCrawledPagesList(latestScan).length > 1
                          ? "Multi-Page"
                          : getCrawledPagesList(latestScan).length === 1
                            ? "Single-Page"
                            : "No Data"}
                      </span>
                    </div>
                    {pagesSparkline && (
                      <div className="stat-card-sparkline-wrapper">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 120 30"
                          preserveAspectRatio="none"
                          style={{ overflow: "visible", display: "block" }}
                        >
                          <defs>
                            <linearGradient
                              id="pagesGrad"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#3b82f6"
                                stopOpacity="0.2"
                              />
                              <stop
                                offset="100%"
                                stopColor="#3b82f6"
                                stopOpacity="0"
                              />
                            </linearGradient>
                          </defs>
                          <path
                            d={pagesSparkline.areaD}
                            fill="url(#pagesGrad)"
                          />
                          <path
                            d={pagesSparkline.pathD}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* 2. Issues Found */}
                  <div className="stat-card">
                    <span className="stat-label">Issues Found</span>
                    <div
                      className="stat-val-row"
                      style={{ zIndex: 2, position: "relative" }}
                    >
                      <span className="stat-number">
                        {allReportIssues.length}
                      </span>
                      <span className="stat-trend red">Total</span>
                    </div>
                    {issuesSparkline && (
                      <div className="stat-card-sparkline-wrapper">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 120 30"
                          preserveAspectRatio="none"
                          style={{ overflow: "visible", display: "block" }}
                        >
                          <defs>
                            <linearGradient
                              id="issuesGrad"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#ef4444"
                                stopOpacity="0.2"
                              />
                              <stop
                                offset="100%"
                                stopColor="#ef4444"
                                stopOpacity="0"
                              />
                            </linearGradient>
                          </defs>
                          <path
                            d={issuesSparkline.areaD}
                            fill="url(#issuesGrad)"
                          />
                          <path
                            d={issuesSparkline.pathD}
                            fill="none"
                            stroke="#ef4444"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* 3. Critical Issues */}
                  <div className="stat-card">
                    <span className="stat-label">Critical Issues</span>
                    <div
                      className="stat-val-row"
                      style={{ zIndex: 2, position: "relative" }}
                    >
                      <span className="stat-number">
                        {
                          allReportIssues.filter((i) => i.type === "Critical")
                            .length
                        }
                      </span>
                      <span className="stat-trend red">Priority</span>
                    </div>
                    {/* No sparkline for Critical Issues as requested */}
                  </div>

                  {/* 4. Warnings */}
                  <div className="stat-card">
                    <span className="stat-label">Warnings</span>
                    <div
                      className="stat-val-row"
                      style={{ zIndex: 2, position: "relative" }}
                    >
                      <span className="stat-number">
                        {
                          allReportIssues.filter((i) => i.type === "Warning")
                            .length
                        }
                      </span>
                      <span className="stat-trend orange">Action Required</span>
                    </div>
                    {warningsSparkline && (
                      <div className="stat-card-sparkline-wrapper">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 120 30"
                          preserveAspectRatio="none"
                          style={{ overflow: "visible", display: "block" }}
                        >
                          <defs>
                            <linearGradient
                              id="warningsGrad"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#f97316"
                                stopOpacity="0.2"
                              />
                              <stop
                                offset="100%"
                                stopColor="#f97316"
                                stopOpacity="0"
                              />
                            </linearGradient>
                          </defs>
                          <path
                            d={warningsSparkline.areaD}
                            fill="url(#warningsGrad)"
                          />
                          <path
                            d={warningsSparkline.pathD}
                            fill="none"
                            stroke="#f97316"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Middle Audit Results Table */}
              <div className="audit-card table-card mt-4">
                <div className="table-controls-row">
                  <div className="filter-pills-wrap">
                    <button
                      className={`filter-pill ${issueFilter === "all" ? "active" : ""}`}
                      onClick={() => setIssueFilter("all")}
                    >
                      All Issues{" "}
                      <span className="pill-cnt">
                        ({allReportIssues.length})
                      </span>
                    </button>
                    <button
                      className={`filter-pill critical ${issueFilter === "critical" ? "active" : ""}`}
                      onClick={() => setIssueFilter("critical")}
                    >
                      Critical{" "}
                      <span className="pill-cnt red">
                        (
                        {
                          allReportIssues.filter((i) => i.type === "Critical")
                            .length
                        }
                        )
                      </span>
                    </button>
                    <button
                      className={`filter-pill warning ${issueFilter === "warning" ? "active" : ""}`}
                      onClick={() => setIssueFilter("warning")}
                    >
                      Warnings{" "}
                      <span className="pill-cnt orange">
                        (
                        {
                          allReportIssues.filter((i) => i.type === "Warning")
                            .length
                        }
                        )
                      </span>
                    </button>
                    <button
                      className={`filter-pill notice ${issueFilter === "notice" ? "active" : ""}`}
                      onClick={() => setIssueFilter("notice")}
                    >
                      Notices{" "}
                      <span className="pill-cnt blue">
                        (
                        {
                          allReportIssues.filter((i) => i.type === "Notice")
                            .length
                        }
                        )
                      </span>
                    </button>
                    <button
                      className={`filter-pill fixed ${issueFilter === "fixed" ? "active" : ""}`}
                      onClick={() => setIssueFilter("fixed")}
                    >
                      Fixed <span className="pill-cnt green">({fixedReportIssues.length})</span>
                    </button>
                  </div>

                  <div className="table-search-box">
                    <FiSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search issues..."
                      value={issueSearch}
                      onChange={(e) => setIssueSearch(e.target.value)}
                    />
                  </div>
                </div>

                {/* Issues Table */}
                <div className="table-responsive mt-3">
                  <table className="audit-table">
                    <thead>
                      <tr>
                        <th>Issue</th>
                        <th>Type</th>
                        <th>Pages Affected</th>
                        <th>{issueFilter === "fixed" ? "Impact & Status" : "Impact"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {issueFilter === "fixed" ? (
                        fixedReportIssues
                          .filter((item) => {
                            if (
                              issueSearch &&
                              !item.name
                                .toLowerCase()
                                .includes(issueSearch.toLowerCase())
                            )
                              return false;
                            return true;
                          })
                          .map((issueItem, idx) => (
                            <tr key={`fixed-${idx}`}>
                              <td className="issue-name-cell">
                                <span className="issue-icon-wrap" style={{ color: "#10b981" }}>
                                  <FiCheckCircle size={16} />
                                </span>
                                <span
                                  className="issue-title-text"
                                  style={{ color: "var(--text-light, #f1f5f9)" }}
                                >
                                  {issueItem.name}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge-type badge-${issueItem.type.toLowerCase()}`}
                                >
                                  {issueItem.type}
                                </span>
                              </td>
                              <td className="fw-semibold">
                                {issueItem.pagesAffected || 1}
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <span
                                    className={`badge-impact impact-${issueItem.impact.toLowerCase()}`}
                                  >
                                    {issueItem.impact}
                                  </span>
                                  <span
                                    className="badge-fixed-source"
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      padding: "3px 8px",
                                      borderRadius: "12px",
                                      backgroundColor: "rgba(16, 185, 129, 0.12)",
                                      color: "#10b981",
                                      border: "1px solid rgba(16, 185, 129, 0.25)",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    ✓ {issueItem.source || "Fixed"}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))
                      ) : (
                        allReportIssues
                          .filter((item) => {
                            if (
                              issueFilter !== "all" &&
                              item.type.toLowerCase() !== issueFilter
                            )
                              return false;
                            if (
                              issueSearch &&
                              !item.name
                                .toLowerCase()
                                .includes(issueSearch.toLowerCase())
                            )
                              return false;
                            return true;
                          })
                          .map((issueItem, idx) => (
                            <tr key={`open-${idx}`}>
                              <td className="issue-name-cell">
                                <span className="issue-icon-wrap">
                                  <FiAlertTriangle />
                                </span>
                                <span className="issue-title-text">
                                  {issueItem.name}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge-type badge-${issueItem.type.toLowerCase()}`}
                                >
                                  {issueItem.type}
                                </span>
                              </td>
                              <td className="fw-semibold">
                                {issueItem.pagesAffected}
                              </td>
                              <td>
                                <span
                                  className={`badge-impact impact-${issueItem.impact.toLowerCase()}`}
                                >
                                  {issueItem.impact}
                                </span>
                              </td>
                            </tr>
                          ))
                      )}

                      {/* Empty State for Fixed filter */}
                      {issueFilter === "fixed" && fixedReportIssues.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center py-5">
                            <div className="d-flex flex-column align-items-center justify-content-center py-3">
                              <FiCheckCircle
                                size={40}
                                style={{
                                  color: "#10b981",
                                  marginBottom: "12px",
                                  opacity: 0.85,
                                }}
                              />
                              <h5
                                style={{
                                  fontWeight: 600,
                                  marginBottom: "6px",
                                  color: "var(--text-light, #f1f5f9)",
                                }}
                              >
                                No Fixed Issues Yet
                              </h5>
                              <p
                                className="text-muted"
                                style={{
                                  fontSize: "13px",
                                  maxWidth: "420px",
                                  marginBottom: "16px",
                                  lineHeight: "1.5",
                                }}
                              >
                                When you scan a 2nd time and issues are resolved, or when you click issues in the AI Visibility Action Center, they will appear here.
                              </p>
                              <button
                                className="btn-action-outline"
                                onClick={() => setActiveTab("knowledge")}
                                style={{
                                  cursor: "pointer",
                                }}
                              >
                                Open AI Visibility Action Center
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bottom Recommendations Center */}
              <div className="audit-card recommendations-card mt-4 mb-5">
                <h3 className="section-heading mb-3">Recommendations Center</h3>

                <div className="recommendations-container">
                  {/* 1. Code / JSON / Schema Viewer Section (Top) */}
                  <div className="code-viewer-side">
                    <div className="code-tabs-header">
                      <button
                        className={`code-tab-btn ${codeTab === "json-ld" ? "active" : ""}`}
                        onClick={() => setCodeTab("json-ld")}
                      >
                        JSON-LD Schema
                      </button>
                      {isAdmin && (
                        <button
                          className={`code-tab-btn ${codeTab === "raw" ? "active" : ""}`}
                          onClick={() => setCodeTab("raw")}
                        >
                          Raw JSON Response
                        </button>
                      )}
                      <button
                        className={`code-tab-btn ${codeTab === "pages" ? "active" : ""}`}
                        onClick={() => setCodeTab("pages")}
                      >
                        Pages Crawled
                      </button>
                      {/* <button
                      className={`code-tab-btn ${codeTab === "howto" ? "active" : ""}`}
                      onClick={() => setCodeTab("howto")}
                    >
                      How to Implement
                    </button> */}
                    </div>

                    <div className="code-box-wrapper">
                      <pre className="code-content-box">
                        <code>
                          {codeTab === "json-ld" &&
                            (getRawResponseJSON(latestScan?.scan_data)?.schema
                              ? JSON.stringify(
                                  getRawResponseJSON(latestScan?.scan_data)
                                    .schema,
                                  null,
                                  2,
                                )
                              : "No data available")}
                          {codeTab === "raw" &&
                            isAdmin &&
                            (latestScan?.scan_data
                              ? JSON.stringify(
                                  getRawResponseJSON(latestScan?.scan_data),
                                  null,
                                  2,
                                )
                              : "No data available")}
                          {codeTab === "pages" &&
                            (getCrawledPagesList(latestScan).length > 0
                              ? getCrawledPagesList(latestScan)
                                  .map(
                                    (url, idx) =>
                                      `[${idx + 1}]  200 OK  =>  ${url}`,
                                  )
                                  .join("\n\n")
                              : "No data available")}
                          {/* {codeTab === "howto" && (
                          getRawResponseJSON(latestScan?.scan_data)?.schema
                            ? `<!-- Add the following JSON-LD script inside your <head> tag -->\n<script type="application/ld+json">\n${JSON.stringify(
                                getRawResponseJSON(latestScan?.scan_data).schema,
                                null,
                                2
                              )}\n</script>`
                            : "No data available"
                        )} */}
                        </code>
                      </pre>

                      <div className="code-action-buttons">
                        <button
                          className="btn-code-copy"
                          onClick={() => {
                            const rawObj = getRawResponseJSON(
                              latestScan?.scan_data,
                            );
                            const crawledList = getCrawledPagesList(latestScan);
                            const content =
                              codeTab === "raw" && isAdmin
                                ? latestScan?.scan_data
                                  ? JSON.stringify(rawObj, null, 2)
                                  : "No data available"
                                : codeTab === "pages"
                                  ? crawledList.length > 0
                                    ? crawledList.join("\n")
                                    : "No data available"
                                  : codeTab === "howto"
                                    ? rawObj?.schema
                                      ? `<script type="application/ld+json">\n${JSON.stringify(rawObj.schema, null, 2)}\n</script>`
                                      : "No data available"
                                    : rawObj?.schema
                                      ? JSON.stringify(rawObj.schema, null, 2)
                                      : "No data available";
                            safeCopyToClipboard(content).then((ok) => {
                              if (ok) {
                                setCopiedCode(true);
                                setTimeout(() => setCopiedCode(false), 2000);
                              }
                            });
                          }}
                        >
                          {copiedCode ? (
                            <FiCheck className="me-1 text-success" />
                          ) : (
                            <FiCopy className="me-1" />
                          )}
                          {copiedCode ? "Copied!" : "Copy Code"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* 2. Recommendations List Section (Below Code Viewer) */}
                  <div className="recommendations-list-side mt-3">
                    <div className="rec-pills-row mb-3">
                      <span className="rec-tab active">
                        Recommended ({dynamicRecommendations.length})
                      </span>
                    </div>

                    <div className="rec-items-stack">
                      {dynamicRecommendations.length > 0 ? (
                        dynamicRecommendations.map((rec, rIdx) => (
                          <div
                            key={rIdx}
                            className={`rec-item-card ${selectedRecIndex === rIdx ? "active" : ""}`}
                            onClick={() => setSelectedRecIndex(rIdx)}
                          >
                            <div className="rec-card-header">
                              <span className="rec-title">{rec.title}</span>
                              {rec.boost &&
                                rec.boost !== "No data available" && (
                                  <span className="rec-boost-tag">
                                    {rec.boost}
                                  </span>
                                )}
                            </div>
                            {rec.impact &&
                              rec.impact !== "No data available" && (
                                <span className="rec-impact-badge">
                                  {rec.impact}
                                </span>
                              )}
                            {rec.description &&
                              rec.description !== "No data available" && (
                                <p className="rec-desc">{rec.description}</p>
                              )}
                            {Array.isArray(rec.affectedPages) &&
                              rec.affectedPages.length > 0 && (
                                <div className="affected-pages-pills">
                                  {rec.affectedPages.map((pg, pIdx) => (
                                    <span key={pIdx} className="page-pill">
                                      {pg}
                                    </span>
                                  ))}
                                </div>
                              )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-muted">
                          <p className="mb-0 fw-medium">
                            No recommendation data available
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : activeTab === "monthly-report" ? (
          <Suspense fallback={<TabSkeleton type="default" />}>
            <MonthlyReportHub
              selectedWebsite={selectedWebsite}
              setSelectedWebsite={setSelectedWebsite}
              userProfile={getModifiedProfileForClient()}
              setActiveTab={setActiveTab}
              theme={theme}
              uniqueWebsites={uniqueWebsites}
              agencyClients={agencyClients}
              userPlanKey={userPlanKey}
              isAdmin={isAdmin}
              navigate={navigate}
            />
          </Suspense>
        ) : activeTab === "help-support" ? (
          <Suspense fallback={<TabSkeleton type="default" />}>
            <HelpSupport userProfile={userProfile} />
          </Suspense>
        ) : activeTab === "clients" ? (
          <div className="acm-container">
            <div className="acm-header">
              <div className="acm-header-info">
                <h2 className="acm-title font-title">
                  Agency Client Management
                </h2>
                <p className="acm-subtitle font-sm">
                  Manage up to 20 client websites, connect integrations, and
                  track visibility.
                </p>
              </div>
              <div className="acm-header-badge">
                <span className="acm-header-badge-text">
                  {agencyClients.length} / 20 Clients
                </span>
              </div>
            </div>

            {/* Dynamic Billing & Limits Summary Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                {(() => {
                  const compLimit = agencyAllowance
                    ? agencyAllowance.complimentary_limit
                    : 1 + (userProfile?.extra_clients || 0);
                  const totalWebsites = agencyClients.length;
                  const paidBusinessesCount = Math.max(0, totalWebsites - compLimit);
                  const paidAddonCost = paidBusinessesCount * 9.99;
                  const totalMonthlyCost = 79.99 + paidAddonCost;
                  const complimentaryQuota = compLimit > 1 ? compLimit - 1 : 0;
                  const complimentaryUsed = Math.max(
                    0,
                    Math.min(totalWebsites - 1, complimentaryQuota),
                  );

                  return (
                    <div className="acm-card acm-billing-card h-100">
                      <h5 className="acm-card-title">
                        SUBSCRIPTION & BILLING SUMMARY
                      </h5>
                      <div className="acm-price-wrap mb-3">
                        <span className="acm-price-val">
                          ${totalMonthlyCost.toFixed(2)}
                        </span>
                        <span className="acm-price-period ms-2">/ month</span>
                      </div>
                      <div className="acm-billing-list">
                        <div className="acm-billing-row mb-1">
                          <span className="acm-billing-label">
                            Base Plan (includes 1st business):
                          </span>
                          <span className="acm-billing-value">$79.99/mo</span>
                        </div>
                        {complimentaryQuota > 0 && (
                          <div className="acm-billing-row mb-1">
                            <span className="acm-billing-label">
                              Complimentary Businesses ({complimentaryUsed} of{" "}
                              {complimentaryQuota} used):
                            </span>
                            <span
                              className="acm-billing-value"
                              style={{ color: "#10b981", fontWeight: 600 }}
                            >
                              Included ($0.00/mo)
                            </span>
                          </div>
                        )}
                        <div className="acm-billing-row mb-2">
                          <span className="acm-billing-label">
                            Additional Paid Businesses ({paidBusinessesCount}):
                          </span>
                          <span className="acm-billing-value">
                            {paidBusinessesCount > 0
                              ? `+ $${paidAddonCost.toFixed(2)}/mo`
                              : "$0.00/mo"}
                          </span>
                        </div>
                        <hr className="acm-divider my-2" />
                        <div className="acm-billing-row acm-billing-total-row">
                          <span className="acm-billing-total-label">
                            Total Estimated Cost:
                          </span>
                          <span className="acm-billing-total-val">
                            ${totalMonthlyCost.toFixed(2)}/mo
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="col-md-6">
                <div className="acm-card acm-limits-card h-100">
                  {selectedWebsite && selectedWebsite !== "all" ? (
                    <>
                      <h5 className="acm-card-title mb-3">
                        Usage Details: {selectedWebsite}
                      </h5>
                      <div className="acm-usage-list">
                        <div className="acm-usage-item mb-2">
                          <div className="acm-usage-header mb-1">
                            <span className="acm-usage-label">
                              AI Readiness Scans:
                            </span>
                            <span className="acm-usage-count">
                              <strong>{getSelectedClientScansUsed()}</strong> /
                              20 used
                            </span>
                          </div>
                          <div className="acm-progress-track">
                            <div
                              className="acm-progress-bar acm-progress-scans"
                              style={{
                                width: `${Math.min(100, (getSelectedClientScansUsed() / 20) * 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                        <div className="acm-usage-item mb-2">
                          <div className="acm-usage-header mb-1">
                            <span className="acm-usage-label">
                              Schema Generations:
                            </span>
                            <span className="acm-usage-count">
                              <strong>
                                {getSelectedClientUsage().schema_used}
                              </strong>{" "}
                              / 10 used
                            </span>
                          </div>
                          <div className="acm-progress-track">
                            <div
                              className="acm-progress-bar acm-progress-schema"
                              style={{
                                width: `${Math.min(100, (getSelectedClientUsage().schema_used / 10) * 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                        <div className="acm-usage-item mb-2">
                          <div className="acm-usage-header mb-1">
                            <span className="acm-usage-label">
                              robots.txt Generations:
                            </span>
                            <span className="acm-usage-count">
                              <strong>
                                {getSelectedClientUsage().robots_used}
                              </strong>{" "}
                              / 10 used
                            </span>
                          </div>
                          <div className="acm-progress-track">
                            <div
                              className="acm-progress-bar acm-progress-robots"
                              style={{
                                width: `${Math.min(100, (getSelectedClientUsage().robots_used / 10) * 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                        <div className="acm-usage-item mb-1">
                          <div className="acm-usage-header mb-1">
                            <span className="acm-usage-label">
                              llms.txt Generations:
                            </span>
                            <span className="acm-usage-count">
                              <strong>
                                {getSelectedClientUsage().llms_used}
                              </strong>{" "}
                              / 10 used
                            </span>
                          </div>
                          <div className="acm-progress-track">
                            <div
                              className="acm-progress-bar acm-progress-llms"
                              style={{
                                width: `${Math.min(100, (getSelectedClientUsage().llms_used / 10) * 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <h5 className="acm-card-title mb-3">
                        MONTHLY ALLOWANCE PER BUSINESS
                      </h5>
                      <div className="row g-3 acm-allowance-grid">
                        <div className="col-6 d-flex align-items-center mb-1">
                          <span className="acm-allowance-bullet me-2">●</span>
                          <div className="acm-allowance-text">
                            <strong className="acm-allowance-strong">20</strong>{" "}
                            AI Readiness Scans
                          </div>
                        </div>
                        <div className="col-6 d-flex align-items-center mb-1">
                          <span className="acm-allowance-bullet me-2">●</span>
                          <div className="acm-allowance-text">
                            <strong className="acm-allowance-strong">10</strong>{" "}
                            Schema generations
                          </div>
                        </div>
                        <div className="col-6 d-flex align-items-center">
                          <span className="acm-allowance-bullet me-2">●</span>
                          <div className="acm-allowance-text">
                            <strong className="acm-allowance-strong">10</strong>{" "}
                            robots.txt generations
                          </div>
                        </div>
                        <div className="col-6 d-flex align-items-center">
                          <span className="acm-allowance-bullet me-2">●</span>
                          <div className="acm-allowance-text">
                            <strong className="acm-allowance-strong">10</strong>{" "}
                            llms.txt generations
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  <p className="acm-footnote font-xs mt-3 mb-0">
                    * Limits are automatically reset every monthly billing
                    cycle. Removing a business immediately stops its $9.99/mo
                    charge.
                  </p>
                </div>
              </div>
            </div>

            {/* Add Website Form Card */}
            {(() => {
              const compLimit = agencyAllowance
                ? agencyAllowance.complimentary_limit
                : 1 + (userProfile?.extra_clients || 0);
              const hasFreeSlot = agencyClients.length < compLimit;
              const remainingFree = agencyAllowance
                ? agencyAllowance.complimentary_remaining
                : Math.max(0, compLimit - agencyClients.length);

              return (
                <div className="acm-card acm-add-card mb-4">
                  <div className="acm-add-header mb-3">
                    <div>
                      <h5 className="acm-add-title mb-0">
                        {agencyClients.length === 0
                          ? "Add First Client Website"
                          : hasFreeSlot
                            ? "Add Complimentary Client Website"
                            : "Add Additional Client Website"}
                      </h5>
                      {compLimit > 1 && (
                        <span className="text-muted font-xs d-block mt-1">
                          Complimentary slots: {agencyClients.length} of {compLimit} used ({remainingFree} remaining at $0.00 without paying)
                        </span>
                      )}
                    </div>
                    <span
                      className={`acm-plan-badge ${hasFreeSlot ? "acm-plan-badge-included" : "acm-plan-badge-extra"}`}
                    >
                      {hasFreeSlot
                        ? "Included in Plan ($0.00)"
                        : "+$9.99 / month"}
                    </span>
                  </div>
                  <div className="acm-add-form">
                    <input
                      type="text"
                      className="acm-input"
                      placeholder="e.g. clientwebsite.com"
                      value={newClientUrl}
                      onChange={(e) => setNewClientUrl(e.target.value)}
                    />
                    <button
                      className="acm-btn-add"
                      onClick={handleAddAgencyClient}
                      disabled={agencyLoading}
                    >
                      {agencyLoading
                        ? "Adding..."
                        : hasFreeSlot
                          ? (agencyClients.length === 0 ? "Add First Website" : "Add Website (Included)")
                          : "Add Website (+$9.99/mo)"}
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Clients Table Card */}
            <div className="acm-card acm-list-card">
              {agencyClients.length === 0 ? (
                <div className="acm-empty-state text-center py-5">
                  <FiGlobe size={40} className="acm-empty-icon mb-3" />
                  <p className="acm-empty-text mb-0 font-sm">
                    No client websites added yet. Add a website above to get
                    started.
                  </p>
                </div>
              ) : (
                <div className="acm-table-responsive">
                  <table className="acm-table align-middle">
                    <thead>
                      <tr className="acm-thead-tr">
                        <th className="acm-th">Website Domain</th>
                        <th className="acm-th">Google Analytics</th>
                        <th className="acm-th">Search Console</th>
                        <th className="acm-th">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agencyClients.map((client) => {
                        const cleanUrl = client.website
                          .replace(/^https?:\/\//, "")
                          .replace(/\/$/, "");
                        return (
                          <tr key={client.website} className="acm-tr">
                            <td className="acm-td acm-domain-cell">
                              {cleanUrl}
                            </td>
                            <td className="acm-td">
                              {client.property_id ? (
                                <span className="acm-badge acm-badge-success">
                                  GA4 Connected:{" "}
                                  {client.ga4_property || client.property_id}
                                </span>
                              ) : (
                                <button
                                  className="acm-btn-connect acm-btn-connect-ga4"
                                  onClick={() => {
                                    setSelectedWebsite(client.website);
                                    setActiveTab("analytics");
                                  }}
                                >
                                  Connect GA4
                                </button>
                              )}
                            </td>
                            <td className="acm-td">
                              {client.gsc_site_url ? (
                                <span className="acm-badge acm-badge-info">
                                  GSC Connected:{" "}
                                  {client.gsc_site_url
                                    .replace(/^https?:\/\//, "")
                                    .replace(/\/$/, "")}
                                </span>
                              ) : (
                                <button
                                  className="acm-btn-connect acm-btn-connect-gsc"
                                  onClick={() => {
                                    setSelectedWebsite(client.website);
                                    setActiveTab("search-console");
                                  }}
                                >
                                  Connect GSC
                                </button>
                              )}
                            </td>
                            <td className="acm-td">
                              <button
                                className="acm-btn-delete"
                                onClick={() =>
                                  handleDeleteAgencyClient(client.website)
                                }
                                title="Remove Client Website"
                              >
                                <FiX size={16} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === "analytics" ? (
          <Suspense fallback={<TabSkeleton type="analytics" />}>
            <div className="analytics-view-wrapper w-100">
              {googleAnalyticsConnected && getModifiedProfileForClient()?.property_id && (
                <AnalyticsHeader
                  gaMode={gaMode}
                  setGaMode={handleSetGaMode}
                  userProfile={getModifiedProfileForClient()}
                  companyDomain={companyDomain}
                  selectedWebsite={selectedWebsite !== "all" ? selectedWebsite : companyDomain}
                  gaPeriodDays={gaPeriodDays}
                  setGaPeriodDays={setGaPeriodDays}
                  fetchGoogleAnalyticsData={fetchGoogleAnalyticsData}
                  handleDisconnectGoogle={handleDisconnectGoogle}
                  actionLoading={actionLoading}
                  analyticsLoading={analyticsLoading}
                />
              )}
              {gaMode === "legacy" ? (
                <LegacyGA4
                  hideHeader={googleAnalyticsConnected && !!getModifiedProfileForClient()?.property_id}
                  gaMode={gaMode}
                  setGaMode={handleSetGaMode}
                  analyticsData={analyticsData}
                  breakdownData={breakdownData}
                  analyticsLoading={analyticsLoading}
                  breakdownLoading={breakdownLoading}
                  googleAnalyticsConnected={googleAnalyticsConnected}
                  handleConnectGoogle={handleConnectGoogle}
                  handleDisconnectGoogle={handleDisconnectGoogle}
                  handleSelectGAProperty={handleSelectGAProperty}
                  propertiesList={propertiesList}
                  userProfile={getModifiedProfileForClient()}
                  companyDomain={companyDomain}
                  selectedWebsite={selectedWebsite !== "all" ? selectedWebsite : companyDomain}
                  gaPeriodDays={gaPeriodDays}
                  setGaPeriodDays={setGaPeriodDays}
                  fetchGoogleAnalyticsData={fetchGoogleAnalyticsData}
                />
              ) : (
                <GoogleAnalytics
                  hideHeader={googleAnalyticsConnected && !!getModifiedProfileForClient()?.property_id}
                  analyticsData={analyticsData}
                  analyticsLoading={analyticsLoading}
                  googleAnalyticsConnected={googleAnalyticsConnected}
                  propertiesList={propertiesList}
                  userProfile={getModifiedProfileForClient()}
                  handleConnectGoogle={handleConnectGoogle}
                  handleDisconnectGoogle={handleDisconnectGoogle}
                  handleSelectGAProperty={handleSelectGAProperty}
                  actionLoading={actionLoading}
                  fetchGoogleAnalyticsData={fetchGoogleAnalyticsData}
                  breakdownData={breakdownData}
                  breakdownLoading={breakdownLoading}
                  handleDisconnectGAProperty={handleDisconnectGAProperty}
                  showPropertySelect={showPropertySelect}
                  setShowPropertySelect={setShowPropertySelect}
                  gaPeriodDays={gaPeriodDays}
                  setGaPeriodDays={setGaPeriodDays}
                  userPlanKey={userPlanKey}
                  navigate={navigate}
                  gaMode={gaMode}
                  setGaMode={handleSetGaMode}
                />
              )}
            </div>
          </Suspense>
        ) : activeTab === "search-console" ? (
          <Suspense fallback={<TabSkeleton type="search-console" />}>
            <div className="search-console-view-wrapper w-100">
              {gscActive && (
                <SearchConsoleHeader
                  gscMode={gscMode}
                  setGscMode={handleSetGscMode}
                  userProfile={getModifiedProfileForClient()}
                  gscSiteUrl={getModifiedProfileForClient()?.gsc_site_url}
                  gscSitesList={gscSitesList}
                  handleSelectGSCSite={handleSelectGSCSite}
                  gscPeriodDays={gscPeriodDays}
                  setGscPeriodDays={setGscPeriodDays}
                  fetchGoogleSearchConsoleData={fetchGoogleSearchConsoleData}
                  handleDisconnectGSC={handleDisconnectGSC}
                  actionLoading={actionLoading}
                  gscLoading={gscLoading}
                />
              )}
              {gscMode === "legacy" ? (
                <LegacyGSC
                  hideHeader={gscActive}
                  gscMode={gscMode}
                  setGscMode={handleSetGscMode}
                  gscData={gscData}
                  gscLoading={gscLoading}
                  gscError={gscError}
                  gscActive={gscActive}
                  googleAnalyticsConnected={googleAnalyticsConnected}
                  handleConnectGoogle={handleConnectGoogle}
                  handleDisconnectGSC={handleDisconnectGSC}
                  handleSelectGSCSite={handleSelectGSCSite}
                  gscSitesList={gscSitesList}
                  userProfile={getModifiedProfileForClient()}
                  companyDomain={companyDomain}
                  selectedWebsite={selectedWebsite !== "all" ? selectedWebsite : companyDomain}
                  gscPeriodDays={gscPeriodDays}
                  setGscPeriodDays={setGscPeriodDays}
                  fetchGoogleSearchConsoleData={fetchGoogleSearchConsoleData}
                />
              ) : (
                <GoogleSearchConsole
                  hideHeader={gscActive}
                  gscData={gscData}
                  gscLoading={gscLoading}
                  gscError={gscError}
                  gscActive={gscActive}
                  googleAnalyticsConnected={googleAnalyticsConnected}
                  gscSitesList={gscSitesList}
                  userProfile={getModifiedProfileForClient()}
                  gscSiteUrl={getModifiedProfileForClient()?.gsc_site_url}
                  handleSelectGSCSite={handleSelectGSCSite}
                  handleConnectGoogle={handleConnectGoogle}
                  fetchGoogleSearchConsoleData={fetchGoogleSearchConsoleData}
                  gscPeriodDays={gscPeriodDays}
                  setGscPeriodDays={setGscPeriodDays}
                  userPlanKey={userPlanKey}
                  navigate={navigate}
                  handleDisconnectGSC={handleDisconnectGSC}
                  handleDisconnectGoogle={handleDisconnectGoogle}
                  actionLoading={actionLoading}
                  showGscSiteSelect={showGscSiteSelect}
                  setShowGscSiteSelect={setShowGscSiteSelect}
                  gscMode={gscMode}
                  setGscMode={handleSetGscMode}
                />
              )}
            </div>
          </Suspense>
        ) : activeTab === "linking" ? (
          <Suspense fallback={<TabSkeleton type="default" />}>
            <InternalLinkingHub
              latestScan={latestScan}
              companyDomain={companyDomain}
              onNavigateToScan={() =>
                navigate("/scanner", {
                  state: {
                    website:
                      selectedWebsite && selectedWebsite !== "all"
                        ? selectedWebsite
                        : "",
                  },
                })
              }
              onSelectTab={setActiveTab}
            />
          </Suspense>
        ) : (
          <>
            {/* ── Top Row (AI Readiness Score & Top Priority Actions) ── */}
            <section className="dashboard-section top-row-grid">
              {/* AI Readiness Score Card */}
              <div className="dashboard-card score-card">
                <div className="card-header-row">
                  <div className="card-title-wrap">
                    <h3 className="card-title-dashboard">AI Readiness Score</h3>
                    <FiInfo
                      className="info-icon"
                      title="Evaluates structured data, technical SEO, and AI accessibility"
                    />
                  </div>
                </div>

                <div className="score-content-body-flex">
                  <div className="score-top-row">
                    <div className="score-left-info">
                      <div className="score-value-row">
                        <span className="big-score-number">
                          {latestScan ? rawScore : "—"}
                        </span>
                        <span className="score-total-denom">/100</span>
                      </div>

                      <div
                        className={`score-tag-badge status-${!latestScan ? "no-data" : rawScore >= 80 ? "excellent" : rawScore >= 60 ? "good" : rawScore > 0 ? "needs-work" : "no-data"}`}
                      >
                        {scoreTier}
                      </div>

                      <div className={`score-delta-text ${getDeltaClass()}`}>
                        {latestScan ? (
                          <>
                            {scoreDeltaText}{" "}
                            <span className="vs-label">{vsLabel}</span>
                          </>
                        ) : (
                          <span className="vs-label">
                            No scans recorded yet
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="score-right-gauge">
                      <div className="donut-gauge-container">
                        <svg className="donut-svg" viewBox="0 0 120 120">
                          <circle
                            className="donut-track"
                            cx="60"
                            cy="60"
                            r="50"
                          />
                          <circle
                            className="donut-progress"
                            cx="60"
                            cy="60"
                            r="50"
                            style={{
                              strokeDasharray: "314.16",
                              strokeDashoffset: `${314.16 - (314.16 * rawScore) / 100}`,
                            }}
                          />
                        </svg>
                        <div className="donut-inner-content">
                          <div className="donut-sparkle-container">
                            <LuSparkle className="donut-sparkle-icon" />
                          </div>
                          <div className="donut-status-text">
                            {getGaugeStatusText(scoreTier)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="score-divider"></div>

                  <div className="score-bottom-section">
                    <div className="what-means-section">
                      <div className="sparkle-icon-container">
                        <LuSparkle className="sparkle-icon" />
                      </div>
                      <div className="what-means-content">
                        <h4 className="what-means-title">What this means</h4>
                        <p className="what-means-text">
                          {getReadinessDescription(scoreTier)}
                        </p>
                      </div>
                    </div>

                    <button
                      className="btn-view-details-link"
                      onClick={() => {
                        if (!latestScan) {
                          navigate("/scanner", {
                            state: {
                              website:
                                selectedWebsite && selectedWebsite !== "all"
                                  ? selectedWebsite
                                  : "",
                            },
                          });
                        } else {
                          handleViewDetails(latestScan);
                        }
                      }}
                    >
                      {latestScan ? "View details" : "Run scan"}{" "}
                      <FiChevronRight className="arrow-icon" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Top Priority Actions Card */}
              <div className="dashboard-card priority-actions-card">
                <div className="card-header-row mb-3">
                  <h3 className="card-title-dashboard">Top Priority Actions</h3>
                </div>

                <div className="actions-list">
                  {topPriorityActions.length === 0 ? (
                    <div className="text-muted text-center py-4 fs-6">
                      Run a scan to generate dynamic priority actions for your
                      site.
                    </div>
                  ) : (
                    topPriorityActions.map((action, idx) => (
                      <div
                        key={idx}
                        className="action-item-row"
                        onClick={() => handleViewDetails(latestScan)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="action-left-info">
                          <div className="action-icon-pill">
                            <FiZap className="zap-icon" />
                          </div>
                          <div className="action-title-wrap">
                            <span className="action-title-text">
                              {action.title}
                            </span>
                            <div className="action-meta-line">
                              {action.impact &&
                                action.impact !== "No data available" && (
                                  <span
                                    className={`impact-badge impact-${action.impactLevel}`}
                                  >
                                    {action.impact}
                                  </span>
                                )}
                              {action.boost &&
                                action.boost !== "No data available" && (
                                  <span className="boost-pill">
                                    {action.boost}
                                  </span>
                                )}
                            </div>
                          </div>
                        </div>

                        <FiChevronRight className="action-arrow" />
                      </div>
                    ))
                  )}
                </div>

                <div className="card-footer-link-row">
                  <button
                    className="btn-footer-link"
                    onClick={() => {
                      if (!latestScan) {
                        navigate("/scanner", {
                          state: {
                            website:
                              selectedWebsite && selectedWebsite !== "all"
                                ? selectedWebsite
                                : "",
                          },
                        });
                      } else {
                        handleViewDetails(latestScan);
                      }
                    }}
                  >
                    {latestScan
                      ? "View all recommendations"
                      : "Run scan for recommendations"}{" "}
                    <FiChevronRight />
                  </button>
                </div>
              </div>

              {/* AI Authority Scanner Coming Soon Card */}
              <div className="dashboard-card authority-scanner-card">
                <div className="authority-card-header-row mb-3">
                  <div className="authority-card-title-wrap">
                    <FiFileText
                      style={{ fontSize: "19px", color: "#6366f1" }}
                    />
                    <h3
                      className="authority-card-title-dashboard"
                      style={{ marginLeft: "8px" }}
                    >
                      AI Authority Scanner
                    </h3>
                    <span className="phase-badge">PHASE 2</span>
                  </div>
                </div>

                <div className="authority-scanner-content">
                  <h4 className="coming-soon-title">Coming Soon!</h4>
                  <p className="coming-soon-desc">
                    Understand how trustworthy and authoritative your business
                    appears to AI systems.
                  </p>

                  <ul className="coming-soon-list">
                    <li>
                      <FiCheckCircle className="check-icon" />
                      <span>Measure your AI Authority Score</span>
                    </li>
                    <li>
                      <FiCheckCircle className="check-icon" />
                      <span>Analyze trust & authority signals</span>
                    </li>
                    <li>
                      <FiCheckCircle className="check-icon" />
                      <span>Evaluate entity and brand consistency</span>
                    </li>
                    <li>
                      <FiCheckCircle className="check-icon" />
                      <span>Benchmark your authority over time</span>
                    </li>
                    <li>
                      <FiCheckCircle className="check-icon" />
                      <span>
                        Discover opportunities to strengthen your AI presence
                      </span>
                    </li>
                  </ul>

                  <div className="development-status-wrapper">
                    <button className="btn-in-development" disabled>
                      <FiLock
                        style={{ marginRight: "6px", fontSize: "14px" }}
                      />
                      In Development
                    </button>
                  </div>

                  <div className="powered-by-footer">
                    Powered by the SeenByAI Intelligence Engine™
                  </div>
                </div>
              </div>
            </section>

            {/* ── Middle Row (AI Visibility Trend Chart) ── */}
            <section
              className="dashboard-section middle-row-section"
              style={{ position: "relative" }}
            >
              <div className="dashboard-card trend-card">
                <div className="card-header-row align-items-center mb-4">
                  <div className="card-title-wrap">
                    <h3 className="card-title-dashboard">
                      AI Visibility Trend
                    </h3>
                  </div>

                  <div className="trend-controls">
                    <div className="trend-legend-item">
                      <span className="legend-dot green-dot" />
                      <span className="legend-label">AI Readiness</span>
                    </div>

                    <div className="trend-time-segmented">
                      {["7 Days", "30 Days", "90 Days", "All Time"].map(
                        (range) => (
                          <button
                            key={range}
                            type="button"
                            className={`trend-time-pill ${timeRange === range ? "active" : ""}`}
                            onClick={() => {
                              const mappedPreset = DATE_RANGE_PRESETS.find(
                                (p) => p.trendKey === range,
                              );
                              if (mappedPreset) {
                                handleSelectDateRange(mappedPreset.id);
                              } else {
                                setTimeRange(range);
                              }
                            }}
                          >
                            {range}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                </div>

                {/* Dynamic SVG Line Chart */}
                <div className="trend-chart-container">
                  <div className="chart-main-area">
                    {/* HTML Y-Axis (0% distortion) */}
                    <div className="chart-y-axis-html">
                      <span className="chart-y-label-item">100</span>
                      <span className="chart-y-label-item">75</span>
                      <span className="chart-y-label-item">50</span>
                      <span className="chart-y-label-item">25</span>
                      <span className="chart-y-label-item">0</span>
                    </div>

                    {/* Plot Area */}
                    <div className="chart-plot-area">
                      <svg
                        className="chart-svg-layer"
                        viewBox="0 0 1000 100"
                        preserveAspectRatio="none"
                      >
                        <defs>
                          <linearGradient
                            id="readinessGrad"
                            x1="0%"
                            y1="0%"
                            x2="0%"
                            y2="100%"
                          >
                            <stop
                              offset="0%"
                              stopColor="#10b981"
                              stopOpacity="0.28"
                            />
                            <stop
                              offset="100%"
                              stopColor="#10b981"
                              stopOpacity="0.0"
                            />
                          </linearGradient>
                        </defs>

                        {/* Horizontal Grid lines */}
                        <line
                          x1="0"
                          y1="8"
                          x2="1000"
                          y2="8"
                          className="chart-grid-line"
                          vectorEffect="non-scaling-stroke"
                        />
                        <line
                          x1="0"
                          y1="29"
                          x2="1000"
                          y2="29"
                          className="chart-grid-line"
                          vectorEffect="non-scaling-stroke"
                        />
                        <line
                          x1="0"
                          y1="50"
                          x2="1000"
                          y2="50"
                          className="chart-grid-line"
                          vectorEffect="non-scaling-stroke"
                        />
                        <line
                          x1="0"
                          y1="71"
                          x2="1000"
                          y2="71"
                          className="chart-grid-line"
                          vectorEffect="non-scaling-stroke"
                        />
                        <line
                          x1="0"
                          y1="92"
                          x2="1000"
                          y2="92"
                          className="chart-grid-line axis-bottom"
                          vectorEffect="non-scaling-stroke"
                        />

                        {/* Gradient Area Fill */}
                        {trendData.areaD && (
                          <path
                            d={trendData.areaD}
                            fill="url(#readinessGrad)"
                          />
                        )}

                        {/* Readiness Trend Line (Green) */}
                        {trendData.pathD && (
                          <path
                            d={trendData.pathD}
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            vectorEffect="non-scaling-stroke"
                          />
                        )}
                      </svg>

                      {/* Pixel-Perfect HTML Data Dots (No Elliptical Distortion) */}
                      {trendData.points.map((pt, idx) => (
                        <div
                          key={`trend-dot-${idx}`}
                          className="chart-data-dot"
                          style={{
                            left: `${pt.pctX}%`,
                            top: `${pt.pctY}%`,
                            backgroundColor: "#10b981",
                            border: "2px solid #ffffff",
                            boxShadow: "0 0 10px rgba(16, 185, 129, 0.7)",
                          }}
                          onMouseEnter={() => setHoveredPoint(pt)}
                          onMouseLeave={() => setHoveredPoint(null)}
                        />
                      ))}

                      {/* Clean HTML Tooltip Bubble */}
                      {hoveredPoint && (
                        <div
                          className="chart-tooltip-bubble"
                          style={{
                            left: `${hoveredPoint.pctX}%`,
                            top: `${hoveredPoint.pctY}%`,
                          }}
                        >
                          <div style={{ color: "#34d399", fontWeight: 700 }}>
                            Score: {hoveredPoint.score}
                          </div>
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#94a3b8",
                              marginTop: "1px",
                            }}
                          >
                            {hoveredPoint.dateStr}
                          </div>
                        </div>
                      )}

                      {/* Clean Empty State Overlay when 0 scans exist in the selected time range */}
                      {trendData.totalScansInRange === 0 && (
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            textAlign: "center",
                            background: "rgba(15, 23, 42, 0.8)",
                            backdropFilter: "blur(6px)",
                            border: "1px solid rgba(255, 255, 255, 0.08)",
                            padding: "12px 24px",
                            borderRadius: "10px",
                            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.35)",
                            pointerEvents: "auto",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 600,
                              color: "#f8fafc",
                              fontSize: "12px",
                              marginBottom: "3px",
                            }}
                          >
                            {timeRange === "All Time"
                              ? "No scans recorded yet"
                              : `No scans in the last ${timeRange}`}
                          </div>
                          {trendData.latestOverallScan ? (
                            <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                              Latest score:{" "}
                              <strong style={{ color: "#10b981" }}>
                                {trendData.latestOverallScan.scan_data?.score}
                              </strong>{" "}
                              on{" "}
                              {new Date(
                                trendData.latestOverallScan.created_at,
                              ).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                          ) : (
                            <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                              Run a scan to begin tracking your AI Visibility
                              trend
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* HTML X-Axis Date Labels (0% distortion) */}
                  <div className="chart-x-axis-html">
                    {trendData.xTicks.map((tick, idx) => (
                      <span
                        key={`xtick-${idx}`}
                        className="chart-x-label-item"
                        style={
                          idx === 0
                            ? { textAlign: "left" }
                            : idx === trendData.xTicks.length - 1
                              ? { textAlign: "right" }
                              : { textAlign: "center" }
                        }
                      >
                        {tick.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Score Distribution Card */}
              <div className="dashboard-card score-distribution-card">
                <div className="card-header-row align-items-center mb-4">
                  <div className="card-title-wrap">
                    <h3 className="card-title-dashboard">Score Distribution</h3>
                  </div>
                </div>

                <div className="score-distribution-body">
                  <div className="distribution-chart-wrapper">
                    <svg
                      viewBox="0 0 100 100"
                      className="distribution-donut-svg"
                    >
                      {/* Background track */}
                      <circle
                        cx="50"
                        cy="50"
                        r={distRadius}
                        stroke="rgba(255, 255, 255, 0.08)"
                        strokeWidth="10"
                        fill="none"
                      />
                      {/* Segment 1: Excellent */}
                      {scoreDist.excellentPct > 0 && (
                        <circle
                          cx="50"
                          cy="50"
                          r={distRadius}
                          className="donut-segment"
                          stroke="#10b981"
                          strokeDasharray={`${excellentDash} ${distCirc}`}
                          strokeDashoffset={excellentOffset}
                        />
                      )}
                      {/* Segment 2: Good */}
                      {scoreDist.goodPct > 0 && (
                        <circle
                          cx="50"
                          cy="50"
                          r={distRadius}
                          className="donut-segment"
                          stroke="#3b82f6"
                          strokeDasharray={`${goodDash} ${distCirc}`}
                          strokeDashoffset={goodOffset}
                        />
                      )}
                      {/* Segment 3: Average */}
                      {scoreDist.averagePct > 0 && (
                        <circle
                          cx="50"
                          cy="50"
                          r={distRadius}
                          className="donut-segment"
                          stroke="#fbbf24"
                          strokeDasharray={`${averageDash} ${distCirc}`}
                          strokeDashoffset={averageOffset}
                        />
                      )}
                      {/* Segment 4: Poor */}
                      {scoreDist.poorPct > 0 && (
                        <circle
                          cx="50"
                          cy="50"
                          r={distRadius}
                          className="donut-segment"
                          stroke="#ef4444"
                          strokeDasharray={`${poorDash} ${distCirc}`}
                          strokeDashoffset={poorOffset}
                        />
                      )}
                    </svg>
                    <div className="donut-center-text">
                      <span className="donut-total-scans">
                        {scoreDist.total}
                      </span>
                      <span className="donut-total-label">Categories</span>
                    </div>
                  </div>

                  <div className="distribution-legend">
                    <div className="legend-item-row">
                      <div className="legend-item-left">
                        <span className="legend-item-dot dot-excellent" />
                        <span>Excellent (80-100)</span>
                      </div>
                      <div className="legend-item-right">
                        <span className="legend-pct">
                          {scoreDist.excellentPct}%
                        </span>
                        <span className="legend-count">
                          ({scoreDist.excellent})
                        </span>
                      </div>
                    </div>

                    <div className="legend-item-row">
                      <div className="legend-item-left">
                        <span className="legend-item-dot dot-good" />
                        <span>Good (60-79)</span>
                      </div>
                      <div className="legend-item-right">
                        <span className="legend-pct">{scoreDist.goodPct}%</span>
                        <span className="legend-count">({scoreDist.good})</span>
                      </div>
                    </div>

                    <div className="legend-item-row">
                      <div className="legend-item-left">
                        <span className="legend-item-dot dot-average" />
                        <span>Average (40-59)</span>
                      </div>
                      <div className="legend-item-right">
                        <span className="legend-pct">
                          {scoreDist.averagePct}%
                        </span>
                        <span className="legend-count">
                          ({scoreDist.average})
                        </span>
                      </div>
                    </div>

                    <div className="legend-item-row">
                      <div className="legend-item-left">
                        <span className="legend-item-dot dot-poor" />
                        <span>Poor (0-39)</span>
                      </div>
                      <div className="legend-item-right">
                        <span className="legend-pct">{scoreDist.poorPct}%</span>
                        <span className="legend-count">({scoreDist.poor})</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Premium Lock Overlay for Trend Chart */}
              {(userPlanKey === "core" ||
                userPlanKey === "guest" ||
                userPlanKey === "") && (
                <div
                  className="glass-premium-overlay d-flex flex-column align-items-center justify-content-center"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(23, 23, 23, 0.85)",
                    backdropFilter: "blur(6px)",
                    borderRadius: "12px",
                    zIndex: 10,
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  <div
                    className="premium-lock-icon-container mb-3"
                    style={{
                      width: "54px",
                      height: "54px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(59, 130, 246, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                    }}
                  >
                    <FiLock size={22} className="text-primary" />
                  </div>
                  <h4
                    className="fw-bold text-white mb-2"
                    style={{ fontSize: "18px" }}
                  >
                    Historical Score Tracking
                  </h4>
                  <p
                    className="text-muted mb-4"
                    style={{
                      fontSize: "13px",
                      maxWidth: "420px",
                      lineHeight: "1.5",
                    }}
                  >
                    Track your website's AI readiness score fluctuations and
                    optimize progress over time. Available exclusively on the
                    Growth plan.
                  </p>
                  <button
                    className="btn btn-primary fw-semibold px-4 py-2"
                    onClick={() => navigate("/pricing")}
                    style={{ borderRadius: "8px" }}
                  >
                    Upgrade to Growth
                  </button>
                </div>
              )}
            </section>

            {/* ── Bottom Row (3-Column Layout: Readiness Breakdown, Top Issues, Recent Scans) ── */}
            <section className="dashboard-section bottom-three-col-grid">
              {/* Column 1: AI Readiness Breakdown */}
              <div className="dashboard-card breakdown-card">
                <div className="card-header-row mb-3">
                  <div className="card-title-wrap">
                    <h3 className="card-title-dashboard">
                      AI Readiness Breakdown
                    </h3>
                    <FiInfo
                      className="info-icon"
                      title="Full score breakdown across all 6 readiness categories"
                    />
                  </div>
                </div>

                <div className="categories-list">
                  {categoriesBreakdown.map((cat, idx) => (
                    <div
                      key={idx}
                      className="category-row-item"
                      style={cat.name === "Internal Linking" ? { cursor: "pointer" } : {}}
                      onClick={() => {
                        if (cat.name === "Internal Linking") setActiveTab("linking");
                      }}
                      title={cat.name === "Internal Linking" ? "Click to view full Internal Linking audit" : undefined}
                    >
                      <div className="category-label-wrap">
                        <span className="category-icon">{cat.icon}</span>
                        <span className="category-name-text">{cat.name}</span>
                      </div>

                      <div className="category-progress-col">
                        <div className="bar-track">
                          <div
                            className={
                              cat.score >= 80
                                ? "bar-fill-green"
                                : cat.score >= 60
                                  ? "bar-fill-blue"
                                  : cat.score >= 40
                                    ? "bar-fill-yellow"
                                    : "bar-fill-red"
                            }
                            style={{
                              width: `${Math.min(100, Math.max(0, cat.score))}%`,
                            }}
                          />
                        </div>
                        <span className="cat-score-text">{cat.score}/100</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="card-footer-link-row">
                  <button
                    className="btn-footer-link"
                    onClick={() => {
                      if (!latestScan) {
                        navigate("/scanner", {
                          state: {
                            website:
                              selectedWebsite && selectedWebsite !== "all"
                                ? selectedWebsite
                                : "",
                          },
                        });
                      } else {
                        handleViewDetails(latestScan);
                      }
                    }}
                  >
                    {latestScan
                      ? "View full AI Readiness report"
                      : "Run scan for breakdown"}{" "}
                    <FiChevronRight />
                  </button>
                </div>
              </div>

              {/* Column 2: Scanner Findings */}
              {(() => {
                const rawObj =
                  latestScan?.scan_data?.raw || latestScan?.scan_data;
                const isSchemaFound =
                  rawObj?.raw_score > 0 || latestScan?.scan_data?.score > 0;
                const isLlmsFound =
                  !!rawObj?.llms_present || rawObj?.llms_txt_score > 0;
                const isRobotsFound =
                  !!rawObj?.robots_present ||
                  rawObj?.technical_seo_score_breakdown?.robots_txt_sitemap > 0;
                const isSitemapFound =
                  !!rawObj?.sitemap_present ||
                  rawObj?.technical_seo_score_breakdown?.robots_txt_sitemap >
                    50;
                const isHttpsFound =
                  !!rawObj?.https_present ||
                  rawObj?.technical_seo_score_breakdown?.https > 0;
                const isOgFound = rawObj?.og_score > 0;
                const isTwitterFound = rawObj?.twitter_score > 0;

                return (
                  <div className="dashboard-card issues-card">
                    <div className="card-header-row mb-3">
                      <div className="card-title-wrap">
                        <h3 className="card-title-dashboard">
                          Scanner Findings
                        </h3>
                        <FiInfo
                          className="info-icon"
                          title="Detected assets and configurations found by the scan"
                        />
                      </div>
                    </div>

                    <div
                      className="findings-list"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {!latestScan ? (
                        <div className="text-muted text-center py-4 fs-6">
                          Run a scan to detect robots.txt, sitemaps, schema, and
                          AI readiness assets.
                        </div>
                      ) : (
                        [
                          {
                            label: "robots.txt File",
                            found: isRobotsFound,
                            desc: "Crawler access policy",
                          },
                          {
                            label: "llms.txt File",
                            found: isLlmsFound,
                            desc: "AI engine summary file",
                          },
                          {
                            label: "Sitemap.xml File",
                            found: isSitemapFound,
                            desc: "Site structure directory",
                          },
                          {
                            label: "Schema JSON-LD",
                            found: isSchemaFound,
                            desc: "Structured business entities",
                          },
                          {
                            label: "HTTPS Encryption",
                            found: isHttpsFound,
                            desc: "Secure SSL active",
                          },
                          {
                            label: "Open Graph Tags",
                            found: isOgFound,
                            desc: "Social sharing tags",
                          },
                          {
                            label: "Twitter Card Tags",
                            found: isTwitterFound,
                            desc: "Twitter discovery cards",
                          },
                        ].map((item, idx) => (
                          <div key={idx} className="finding-row-item">
                            <div
                              className="finding-left"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              {item.found ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "22px",
                                    height: "22px",
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(16, 185, 129, 0.12)",
                                    color: "#10b981",
                                  }}
                                >
                                  <FiCheck size={12} />
                                </div>
                              ) : (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "22px",
                                    height: "22px",
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(239, 68, 68, 0.12)",
                                    color: "#ef4444",
                                  }}
                                >
                                  <FiAlertTriangle size={12} />
                                </div>
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <span
                                  className="finding-title"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  {item.label}
                                  {item.label === "llms.txt File" && (
                                    <FiInfo
                                      className="info-icon"
                                      style={{
                                        cursor: "help",
                                        fontSize: "12px",
                                      }}
                                      title="llms.txt is an emerging AI standard that helps platforms like ChatGPT, Perplexity, and Gemini understand your business. Adoption is growing but not yet universal, and no AI platform has officially confirmed it as a ranking signal. That said — the GEO community consensus is clear: it costs nothing to implement, carries no downside risk, and puts you ahead of businesses that are still waiting for 100% proof. Better to have it than not."
                                    />
                                  )}
                                </span>
                                <span className="finding-desc">
                                  {item.desc}
                                </span>
                              </div>
                            </div>

                            <span
                              className={`badge-status ${item.found ? "deployed" : "draft"}`}
                              style={{
                                fontSize: "10px",
                                fontWeight: "700",
                                padding: "2px 8px",
                                borderRadius: "99px",
                                backgroundColor: item.found
                                  ? "rgba(16, 185, 129, 0.12)"
                                  : "rgba(239, 68, 68, 0.12)",
                                color: item.found ? "#10b981" : "#ef4444",
                              }}
                            >
                              {item.found ? "Found" : "Missing"}
                            </span>
                          </div>
                        ))
                      )}
                    </div>

                    <div
                      className="card-footer-link-row"
                      style={{ marginTop: "14px" }}
                    >
                      <button
                        className="btn-footer-link"
                        onClick={() => {
                          if (!latestScan) {
                            navigate("/scanner", {
                              state: {
                                website:
                                  selectedWebsite && selectedWebsite !== "all"
                                    ? selectedWebsite
                                    : "",
                              },
                            });
                          } else {
                            handleViewDetails(latestScan);
                          }
                        }}
                      >
                        {latestScan
                          ? "View all issues & recommendations"
                          : "Run scan to detect assets"}{" "}
                        <FiChevronRight />
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Column 3: Recent Scans */}
              <div className="dashboard-card recent-scans-card">
                <div className="card-header-row mb-3">
                  <div className="card-title-wrap d-flex align-items-center justify-content-between w-100">
                    <h3 className="card-title-dashboard mb-0">Recent Scans</h3>
                    <span
                      className="badge bg-secondary-subtle text-secondary rounded-pill px-2 py-0.5"
                      style={{ fontSize: "10.5px" }}
                    >
                      {DATE_RANGE_PRESETS.find((p) => p.id === selectedPeriod)
                        ?.label || "Last 30 Days"}
                    </span>
                  </div>
                </div>

                <div className="scans-list">
                  {activeHistory.length === 0 ? (
                    <div className="no-history-placeholder text-center py-4">
                      <p className="text-muted">
                        No scan history recorded yet.
                      </p>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() =>
                          navigate("/scanner", {
                            state: {
                              website:
                                selectedWebsite && selectedWebsite !== "all"
                                  ? selectedWebsite
                                  : "",
                            },
                          })
                        }
                      >
                        Run First Scan
                      </Button>
                    </div>
                  ) : (
                    (() => {
                      const preset = DATE_RANGE_PRESETS.find(
                        (p) => p.id === selectedPeriod,
                      );
                      const cutoff = preset?.days
                        ? Date.now() - preset.days * 24 * 60 * 60 * 1000
                        : null;
                      const periodScans = cutoff
                        ? activeHistory.filter(
                            (row) =>
                              new Date(row.created_at).getTime() >= cutoff,
                          )
                        : activeHistory;

                      if (periodScans.length === 0) {
                        return (
                          <div className="py-2 text-center">
                            <div
                              className="text-muted"
                              style={{ fontSize: "12px" }}
                            >
                              No scans in{" "}
                              {preset?.label?.toLowerCase() ||
                                "selected period"}
                              .
                            </div>
                            <div
                              className="my-1"
                              style={{ fontSize: "11px", color: "#94a3b8" }}
                            >
                              Showing earlier scans:
                            </div>
                            <div className="text-start mt-2">
                              {activeHistory.slice(0, 3).map((row, idx) => {
                                const formattedDate = new Date(
                                  row.created_at,
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                });
                                return (
                                  <div
                                    key={row.id || idx}
                                    className="scan-history-item-row"
                                    onClick={() => handleViewDetails(row)}
                                  >
                                    <div className="scan-item-left">
                                      <FiClock className="scan-clock-icon" />
                                      <div className="scan-date-wrap">
                                        <span className="scan-date-text">
                                          {formattedDate}
                                        </span>
                                        <span className="scan-tag-sub">
                                          Full Scan
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }

                      return periodScans.slice(0, 4).map((row, idx) => {
                        const formattedDate = new Date(
                          row.created_at,
                        ).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        });
                        return (
                          <div
                            key={row.id || idx}
                            className="scan-history-item-row"
                            onClick={() => handleViewDetails(row)}
                          >
                            <div className="scan-item-left">
                              <FiClock className="scan-clock-icon" />
                              <div className="scan-date-wrap">
                                <span className="scan-date-text">
                                  {formattedDate}
                                </span>
                                <span className="scan-tag-sub">Full Scan</span>
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()
                  )}
                </div>

                <div className="card-footer-link-row">
                  <button
                    className="btn-footer-link"
                    onClick={() => navigate("/history")}
                  >
                    View all scans <FiChevronRight />
                  </button>
                </div>
              </div>
            </section>
            {/* ── Integrations Row (Google Analytics & Search Console Connection) ── */}
            <section
              className="dashboard-section integrations-section ga-metrics-section my-4"
              style={{ position: "relative" }}
            >
              <div className="dashboard-card integrations-card">
                <div
                  className="card-header-row align-items-center mb-3"
                  style={{
                    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                    paddingBottom: "10px",
                  }}
                >
                  <div className="card-title-wrap d-flex gap-3 align-items-center">
                    <button
                      className={`btn btn-link p-0 text-decoration-none fw-bold ${activeIntegrationTab === "analytics" ? "text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{
                        fontSize: "15px",
                        borderRadius: "0",
                        paddingBottom: "4px",
                      }}
                      onClick={() => setActiveIntegrationTab("analytics")}
                    >
                      Google Analytics
                    </button>
                    <button
                      className={`btn btn-link p-0 text-decoration-none fw-bold ${activeIntegrationTab === "search-console" ? "text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{
                        fontSize: "15px",
                        borderRadius: "0",
                        paddingBottom: "4px",
                      }}
                      onClick={() => setActiveIntegrationTab("search-console")}
                    >
                      Google Search Console
                    </button>
                  </div>
                  {activeIntegrationTab === "analytics" ? (
                    googleAnalyticsConnected ? (
                      <span
                        className="badge bg-success-subtle text-success border border-success-subtle px-3 py-1.5 rounded-pill fw-semibold d-inline-flex align-items-center gap-1.5"
                        style={{ fontSize: "12px" }}
                      >
                        <span
                          style={{
                            width: "6px",
                            height: "6px",
                            backgroundColor: "#10b981",
                            borderRadius: "50%",
                            display: "inline-block",
                          }}
                        ></span>
                        Connected:{" "}
                        {activeProfile?.ga4_property || ga4Property
                          ? activeProfile?.ga4_property || ga4Property
                          : activeProfile?.property_id || ga4PropertyId
                            ? `Property #${activeProfile?.property_id || ga4PropertyId}`
                            : "Select Property"}
                      </span>
                    ) : (
                      <span
                        className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-3 py-1.5 rounded-pill fw-semibold d-inline-flex align-items-center gap-1.5"
                        style={{ fontSize: "12px" }}
                      >
                        <span
                          style={{
                            width: "6px",
                            height: "6px",
                            backgroundColor: "#6b7280",
                            borderRadius: "50%",
                            display: "inline-block",
                          }}
                        ></span>
                        Disconnected
                      </span>
                    )
                  ) : activeProfile?.google_search_console_connected ||
                    activeProfile?.gsc_site_url ||
                    gscSiteUrl ? (
                    <span
                      className="badge bg-success-subtle text-success border border-success-subtle px-3 py-1.5 rounded-pill fw-semibold d-inline-flex align-items-center gap-1.5"
                      style={{ fontSize: "12px" }}
                    >
                      <span
                        style={{
                          width: "6px",
                          height: "6px",
                          backgroundColor: "#10b981",
                          borderRadius: "50%",
                          display: "inline-block",
                        }}
                      ></span>
                      Connected: {activeProfile?.gsc_site_url || gscSiteUrl}
                    </span>
                  ) : (
                    <span
                      className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-3 py-1.5 rounded-pill fw-semibold d-inline-flex align-items-center gap-1.5"
                      style={{ fontSize: "12px" }}
                    >
                      <span
                        style={{
                          width: "6px",
                          height: "6px",
                          backgroundColor: "#6b7280",
                          borderRadius: "50%",
                          display: "inline-block",
                        }}
                      ></span>
                      Disconnected
                    </span>
                  )}
                </div>

                {activeIntegrationTab === "analytics" ? (
                  <div className="card-body-content d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                    <div>
                      <p
                        className="text-muted mb-0"
                        style={{
                          fontSize: "14px",
                          maxWidth: "650px",
                          lineHeight: "1.5",
                        }}
                      >
                        {!googleAnalyticsConnected &&
                          "Connect Google Analytics to overlay actual visitor traffic data with your AI visibility readiness. This helps measure how your visibility optimizations affect real-world traffic."}
                        {googleAnalyticsConnected &&
                          (activeProfile?.property_id || ga4PropertyId) &&
                          !showPropertySelect && (
                            <>
                              Your Google Analytics account is connected.
                              Property selected:{" "}
                              <strong className="text-console">
                                {activeProfile?.ga4_property ||
                                  ga4Property ||
                                  `Property #${activeProfile?.property_id || ga4PropertyId}`}
                              </strong>
                            </>
                          )}
                        {googleAnalyticsConnected &&
                          (!(activeProfile?.property_id || ga4PropertyId) ||
                            showPropertySelect) && (
                            <>
                              Select which Google Analytics 4 (GA4) property to
                              import traffic metrics from
                              {selectedWebsite && selectedWebsite !== "all" ? (
                                <>
                                  {" "}
                                  for{" "}
                                  <strong className="text-console">
                                    {selectedWebsite
                                      .replace(/^https?:\/\//, "")
                                      .replace(/\/$/, "")}
                                  </strong>
                                  :
                                </>
                              ) : (
                                ":"
                              )}
                            </>
                          )}
                      </p>
                    </div>
                    <div>
                      {!googleAnalyticsConnected && (
                        <button
                          className="btn btn-primary fw-bold px-4 py-2 d-inline-flex align-items-center gap-2"
                          style={{
                            borderRadius: "8px",
                            fontSize: "14px",
                            transition: "all 0.2s",
                          }}
                          onClick={handleConnectGoogle}
                          disabled={actionLoading}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill="currentColor"
                          >
                            <path
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              fill="#4285F4"
                            />
                            <path
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              fill="#34A853"
                            />
                            <path
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                              fill="#FBBC05"
                            />
                            <path
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                              fill="#EA4335"
                            />
                          </svg>
                          {actionLoading
                            ? "Redirecting..."
                            : "Connect Google Account"}
                        </button>
                      )}

                      {googleAnalyticsConnected &&
                        (activeProfile?.property_id || ga4PropertyId) &&
                        !showPropertySelect && (
                          <div className="d-flex align-items-center gap-2">
                            <button
                              className="btn btn-outline-primary fw-semibold px-3 py-2"
                              style={{ borderRadius: "8px", fontSize: "13px" }}
                              onClick={() => {
                                setShowPropertySelect(true);
                                if (propertiesList.length === 0) {
                                  fetchGoogleAnalyticsData(activeProfile);
                                }
                              }}
                              disabled={actionLoading}
                            >
                              Change Property
                            </button>
                            <button
                              className="btn btn-outline-danger fw-semibold px-3 py-2"
                              style={{ borderRadius: "8px", fontSize: "13px" }}
                              onClick={handleDisconnectGoogle}
                              disabled={actionLoading}
                            >
                              {actionLoading
                                ? "Disconnecting..."
                                : "Disconnect"}
                            </button>
                          </div>
                        )}

                      {googleAnalyticsConnected &&
                        (!(activeProfile?.property_id || ga4PropertyId) ||
                          showPropertySelect) && (
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {analyticsLoading ? (
                              <div
                                className="d-flex align-items-center gap-2 text-muted"
                                style={{ fontSize: "13px" }}
                              >
                                <Spinner animation="border" size="sm" />{" "}
                                Fetching properties...
                              </div>
                            ) : propertiesList.length === 0 ? (
                              <div
                                className="text-muted d-flex align-items-center gap-2"
                                style={{ fontSize: "13px" }}
                              >
                                No properties found.
                                <button
                                  className="btn btn-sm btn-link p-0 text-decoration-none"
                                  onClick={() =>
                                    fetchGoogleAnalyticsData(activeProfile)
                                  }
                                >
                                  Retry
                                </button>
                              </div>
                            ) : (
                              <>
                                <select
                                  className="form-select form-select-sm"
                                  defaultValue=""
                                  id="ga4-property-select"
                                  style={{
                                    maxWidth: "280px",
                                    fontSize: "13px",
                                    borderRadius: "6px",
                                    minWidth: "180px",
                                  }}
                                >
                                  <option value="" disabled>
                                    -- Select GA4 Property --
                                  </option>
                                  {propertiesList.map((p) => (
                                    <option
                                      key={p.property_id}
                                      value={JSON.stringify(p)}
                                    >
                                      {p.display_name} ({p.account_name})
                                    </option>
                                  ))}
                                </select>
                                <button
                                  className="btn btn-primary btn-sm fw-bold px-3 py-2 text-white"
                                  style={{
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                  }}
                                  onClick={() => {
                                    const selectEl = document.getElementById(
                                      "ga4-property-select",
                                    );
                                    if (selectEl && selectEl.value) {
                                      const selected = JSON.parse(
                                        selectEl.value,
                                      );
                                      handleSelectGAProperty(
                                        selected.property_id,
                                        selected.display_name,
                                      );
                                    } else {
                                      toast.warning(
                                        "Please select a property first.",
                                      );
                                    }
                                  }}
                                  disabled={actionLoading}
                                >
                                  Save
                                </button>
                                {(activeProfile?.property_id ||
                                  ga4PropertyId) && (
                                  <button
                                    className="btn btn-outline-secondary btn-sm px-3 py-2"
                                    style={{
                                      borderRadius: "6px",
                                      fontSize: "13px",
                                    }}
                                    onClick={() => setShowPropertySelect(false)}
                                  >
                                    Cancel
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                ) : (
                  <div className="card-body-content d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                    <div>
                      <p
                        className="text-muted mb-0"
                        style={{
                          fontSize: "14px",
                          maxWidth: "650px",
                          lineHeight: "1.5",
                        }}
                      >
                        {!(activeProfile?.gsc_site_url || gscSiteUrl) ||
                        showGscSiteSelect ? (
                          <>
                            {gscSitesList.length > 0 ? (
                              "Select which verified website to import search analytics from:"
                            ) : (
                              <>
                                Connect Google Search Console to track organic
                                search keywords, click-through rates, and
                                average index rankings.
                                {googleAnalyticsConnected &&
                                  " If you previously connected Google Analytics, you may need to click 'Connect Search Console' to authorize Search Console permissions."}
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            Your Google Search Console site is connected. Site
                            URL:{" "}
                            <strong className="text-console">
                              {activeProfile?.gsc_site_url || gscSiteUrl}
                            </strong>
                          </>
                        )}
                      </p>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {/* 1. If not connected, or in edit mode, show Connect and Load buttons */}
                      {(showGscSiteSelect ||
                        !(activeProfile?.gsc_site_url || gscSiteUrl)) && (
                        <>
                          <button
                            className="btn btn-primary fw-bold px-4 py-2 d-inline-flex align-items-center gap-2"
                            style={{
                              borderRadius: "8px",
                              fontSize: "14px",
                              transition: "all 0.2s",
                            }}
                            onClick={handleConnectGoogle}
                            disabled={actionLoading}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              width="16"
                              height="16"
                              fill="currentColor"
                            >
                              <path
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                fill="#4285F4"
                              />
                              <path
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                fill="#34A853"
                              />
                              <path
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                                fill="#FBBC05"
                              />
                              <path
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                                fill="#EA4335"
                              />
                            </svg>
                            {actionLoading
                              ? "Redirecting..."
                              : "Connect Search Console"}
                          </button>

                          {gscSitesList.length === 0 &&
                            googleAnalyticsConnected && (
                              <button
                                className="btn btn-outline-primary fw-semibold px-3 py-2"
                                style={{
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                }}
                                onClick={() =>
                                  fetchGoogleSearchConsoleData(
                                    getModifiedProfileForClient(),
                                  )
                                }
                                disabled={gscLoading}
                              >
                                {gscLoading
                                  ? "Loading..."
                                  : "Load Verified Sites"}
                              </button>
                            )}
                        </>
                      )}

                      {/* 2. If GSC sites list is fetched, show dropdown */}
                      {(showGscSiteSelect ||
                        !(activeProfile?.gsc_site_url || gscSiteUrl)) &&
                        gscSitesList.length > 0 && (
                          <div className="d-flex align-items-center gap-2">
                            <select
                              className="form-select form-select-sm"
                              defaultValue=""
                              id="gsc-site-select"
                              style={{
                                maxWidth: "280px",
                                fontSize: "13px",
                                borderRadius: "6px",
                                minWidth: "180px",
                              }}
                            >
                              <option value="" disabled>
                                -- Select Site URL --
                              </option>
                              {gscSitesList.map((s) => (
                                <option key={s.site_url} value={s.site_url}>
                                  {s.site_url} ({s.permission_level})
                                </option>
                              ))}
                            </select>
                            <button
                              className="btn btn-primary btn-sm fw-bold px-3 py-2 text-white"
                              style={{ borderRadius: "6px", fontSize: "13px" }}
                              onClick={() => {
                                const selectEl =
                                  document.getElementById("gsc-site-select");
                                if (selectEl && selectEl.value) {
                                  handleSelectGSCSite(selectEl.value);
                                } else {
                                  toast.warning(
                                    "Please select a site URL first.",
                                  );
                                }
                              }}
                              disabled={actionLoading}
                            >
                              Save
                            </button>
                          </div>
                        )}

                      {/* 3. Cancel button to leave edit mode if they already have a site URL */}
                      {showGscSiteSelect &&
                        (activeProfile?.gsc_site_url || gscSiteUrl) && (
                          <button
                            className="btn btn-outline-secondary fw-semibold px-3 py-2"
                            style={{ borderRadius: "8px", fontSize: "13px" }}
                            onClick={() => setShowGscSiteSelect(false)}
                          >
                            Cancel
                          </button>
                        )}

                      {/* 4. Connected state actions */}
                      {!(
                        showGscSiteSelect ||
                        !(activeProfile?.gsc_site_url || gscSiteUrl)
                      ) && (
                        <div className="d-flex align-items-center gap-2">
                          <button
                            className="btn btn-outline-primary fw-semibold px-3 py-2"
                            style={{ borderRadius: "8px", fontSize: "13px" }}
                            onClick={() => {
                              setShowGscSiteSelect(true);
                              if (gscSitesList.length === 0) {
                                fetchGoogleSearchConsoleData(
                                  getModifiedProfileForClient(),
                                );
                              }
                            }}
                            disabled={actionLoading}
                          >
                            Change Site
                          </button>
                          <button
                            className="btn btn-outline-danger fw-semibold px-3 py-2"
                            style={{ borderRadius: "8px", fontSize: "13px" }}
                            onClick={handleDisconnectGSC}
                            disabled={actionLoading}
                          >
                            {actionLoading ? "Disconnecting..." : "Disconnect"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {(showGA || showGSC) && (
                  <>
                    <hr
                      className="my-4"
                      style={{
                        borderTop: "1px solid rgba(120, 120, 120, 0.15)",
                      }}
                    />
                    <div
                      className="card-header-row align-items-center mb-3 mt-2"
                      style={{
                        borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                        paddingBottom: "10px",
                      }}
                    >
                      <div className="card-title-wrap">
                        <h3
                          className="welcome-heading fw-bold mb-0 text-dark-console"
                          style={{ fontSize: "18px" }}
                        >
                          {showGA
                            ? "Google Analytics Overview"
                            : "Search Console Overview"}
                        </h3>
                      </div>
                      <span className="text-muted" style={{ fontSize: "12px" }}>
                        {showGA ? (
                          <>
                            Data imported from GA4 Property:{" "}
                            <strong>
                              {activeProfile?.ga4_property ||
                                ga4Property ||
                                (activeProfile?.property_id || ga4PropertyId
                                  ? `Property #${activeProfile?.property_id || ga4PropertyId}`
                                  : "Not Connected")}
                            </strong>
                          </>
                        ) : (
                          <>
                            Data imported from Site:{" "}
                            <strong>
                              {activeProfile?.gsc_site_url || gscSiteUrl}
                            </strong>
                          </>
                        )}
                      </span>
                    </div>

                    {showGA && (
                      <>
                        {/* 1. GA Summary Metric Cards */}
                        <div className="ga-summary-grid mb-4">
                          {/* Card 1: Active Users */}
                          <div className="dashboard-card ga-metric-card shadow-sm border border-light">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span
                                className="text-secondary fw-semibold"
                                style={{
                                  fontSize: "12px",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                ACTIVE USERS
                              </span>
                              <span
                                className="badge bg-primary-subtle text-primary rounded-pill px-2 py-0.5"
                                style={{ fontSize: "10px" }}
                              >
                                {gaPeriodDays
                                  ? `${gaPeriodDays} Days`
                                  : "30 Days"}
                              </span>
                            </div>
                            <div className="d-flex align-items-baseline gap-2">
                              <span className="fs-3 fw-extrabold text-dark-console">
                                {analyticsData.totals.users.toLocaleString()}
                              </span>
                              <span
                                className="text-success fw-bold"
                                style={{ fontSize: "11px" }}
                              >
                                ● Live
                              </span>
                            </div>
                            <p
                              className="text-muted mb-0 mt-2"
                              style={{ fontSize: "11px" }}
                            >
                              Total unique active visitors on your site.
                            </p>
                          </div>

                          {/* Card 2: Sessions */}
                          <div className="dashboard-card ga-metric-card shadow-sm border border-light">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span
                                className="text-secondary fw-semibold"
                                style={{
                                  fontSize: "12px",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                TOTAL SESSIONS
                              </span>
                              <span
                                className="badge bg-info-subtle text-info rounded-pill px-2 py-0.5"
                                style={{ fontSize: "10px" }}
                              >
                                {gaPeriodDays
                                  ? `${gaPeriodDays} Days`
                                  : "30 Days"}
                              </span>
                            </div>
                            <div className="d-flex align-items-baseline gap-2">
                              <span className="fs-3 fw-extrabold text-dark-console">
                                {analyticsData.totals.sessions.toLocaleString()}
                              </span>
                            </div>
                            <p
                              className="text-muted mb-0 mt-2"
                              style={{ fontSize: "11px" }}
                            >
                              Total visitor sessions initiated on your site.
                            </p>
                          </div>

                          {/* Card 3: Screen Page Views */}
                          <div className="dashboard-card ga-metric-card shadow-sm border border-light">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span
                                className="text-secondary fw-semibold"
                                style={{
                                  fontSize: "12px",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                PAGE VIEWS
                              </span>
                              <span
                                className="badge bg-success-subtle text-success rounded-pill px-2 py-0.5"
                                style={{ fontSize: "10px" }}
                              >
                                {gaPeriodDays
                                  ? `${gaPeriodDays} Days`
                                  : "30 Days"}
                              </span>
                            </div>
                            <div className="d-flex align-items-baseline gap-2">
                              <span className="fs-3 fw-extrabold text-dark-console">
                                {analyticsData.totals.views.toLocaleString()}
                              </span>
                            </div>
                            <p
                              className="text-muted mb-0 mt-2"
                              style={{ fontSize: "11px" }}
                            >
                              Total number of individual pages viewed.
                            </p>
                          </div>
                        </div>

                        <div className="ga-charts-row">
                          {/* 2. GA Traffic Trend Chart Card (2/3 width) */}
                          <div className="dashboard-card trend-card ga-main-trend-card">
                            <div className="card-header-row align-items-center mb-4">
                              <div className="card-title-wrap">
                                <h3
                                  className="card-title-dashboard"
                                  style={{ fontSize: "14px" }}
                                >
                                  GA4 Traffic Trend
                                </h3>
                              </div>

                              <div className="trend-controls flex-wrap gap-3">
                                <div className="trend-legend-item">
                                  <span
                                    className="legend-dot"
                                    style={{ backgroundColor: "#4f46e5" }}
                                  />
                                  <span className="legend-label">Users</span>
                                </div>
                                <div className="trend-legend-item">
                                  <span
                                    className="legend-dot"
                                    style={{ backgroundColor: "#06b6d4" }}
                                  />
                                  <span className="legend-label">Sessions</span>
                                </div>
                                <div className="trend-legend-item">
                                  <span
                                    className="legend-dot"
                                    style={{ backgroundColor: "#10b981" }}
                                  />
                                  <span className="legend-label">Views</span>
                                </div>
                              </div>
                            </div>

                            {/* Dynamic Line Chart */}
                            <div className="trend-chart-container">
                              <div className="chart-main-area">
                                {/* HTML Y-Axis (0% distortion) */}
                                <div className="chart-y-axis-html">
                                  <span className="chart-y-label-item">
                                    {gaMaxVal.toLocaleString()}
                                  </span>
                                  <span className="chart-y-label-item">
                                    {Math.round(gaMaxVal / 2).toLocaleString()}
                                  </span>
                                  <span className="chart-y-label-item">0</span>
                                </div>

                                {/* Plot Area */}
                                <div className="chart-plot-area">
                                  <svg
                                    className="chart-svg-layer"
                                    viewBox="0 0 1000 100"
                                    preserveAspectRatio="none"
                                  >
                                    {/* Horizontal Grid lines */}
                                    <line
                                      x1="0"
                                      y1="8"
                                      x2="1000"
                                      y2="8"
                                      className="chart-grid-line"
                                      vectorEffect="non-scaling-stroke"
                                    />
                                    <line
                                      x1="0"
                                      y1="50"
                                      x2="1000"
                                      y2="50"
                                      className="chart-grid-line"
                                      vectorEffect="non-scaling-stroke"
                                    />
                                    <line
                                      x1="0"
                                      y1="92"
                                      x2="1000"
                                      y2="92"
                                      className="chart-grid-line axis-bottom"
                                      vectorEffect="non-scaling-stroke"
                                    />

                                    {/* Users Path */}
                                    {analyticsData.daily.length >= 2 && (
                                      <path
                                        d={getGAPathD("users")}
                                        fill="none"
                                        stroke="#4f46e5"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                      />
                                    )}

                                    {/* Sessions Path */}
                                    {analyticsData.daily.length >= 2 && (
                                      <path
                                        d={getGAPathD("sessions")}
                                        fill="none"
                                        stroke="#06b6d4"
                                        strokeWidth="2"
                                        strokeDasharray="4 3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                      />
                                    )}

                                    {/* Page Views Path */}
                                    {analyticsData.daily.length >= 2 && (
                                      <path
                                        d={getGAPathD("views")}
                                        fill="none"
                                        stroke="#10b981"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                      />
                                    )}
                                  </svg>

                                  {/* Data dots for final points */}
                                  {analyticsData.daily.length > 0 && (
                                    <>
                                      <div
                                        className="chart-data-dot"
                                        style={{
                                          left: "100%",
                                          top: `${92 - ((analyticsData.daily[analyticsData.daily.length - 1].users || 0) * 84) / gaMaxVal}%`,
                                          backgroundColor: "#4f46e5",
                                          border: "2px solid #ffffff",
                                          boxShadow:
                                            "0 0 8px rgba(79, 70, 229, 0.6)",
                                        }}
                                      />
                                      <div
                                        className="chart-data-dot"
                                        style={{
                                          left: "100%",
                                          top: `${92 - ((analyticsData.daily[analyticsData.daily.length - 1].views || 0) * 84) / gaMaxVal}%`,
                                          backgroundColor: "#10b981",
                                          border: "2px solid #ffffff",
                                          boxShadow:
                                            "0 0 8px rgba(16, 185, 129, 0.6)",
                                        }}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* HTML X-Axis Date Labels */}
                              <div className="chart-x-axis-html">
                                {analyticsData.daily.map((dp, idx) => {
                                  if (
                                    idx % 5 !== 0 &&
                                    idx !== analyticsData.daily.length - 1
                                  )
                                    return null;
                                  const dateParts = dp.date.split("-");
                                  const dateStr =
                                    dateParts.length === 3
                                      ? new Date(
                                          dateParts[0],
                                          dateParts[1] - 1,
                                          dateParts[2],
                                        ).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                        })
                                      : dp.date;
                                  return (
                                    <span
                                      key={`ga-xlabel-${idx}`}
                                      className="chart-x-label-item"
                                    >
                                      {dateStr}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          {/* AI Traffic Chart Card (1/3 width) */}
                          <div className="dashboard-card trend-card ga-ai-trend-card">
                            <div className="d-flex flex-column mb-3">
                              <div className="d-flex align-items-center gap-1">
                                <span
                                  className="text-secondary fw-semibold"
                                  style={{
                                    fontSize: "12px",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  AI Traffic
                                </span>
                                <i
                                  className="bi bi-info-circle text-muted"
                                  style={{ fontSize: "11px", cursor: "help" }}
                                  title="Traffic initiated from AI search engines and assistants (like ChatGPT, Claude, Perplexity, etc.)"
                                />
                              </div>
                              <div className="d-flex align-items-baseline gap-2 mt-1">
                                <span className="fs-3 fw-extrabold text-dark-console">
                                  {(
                                    analyticsData.ai_traffic?.total_sessions ||
                                    0
                                  ).toLocaleString()}
                                </span>
                                {analyticsData.ai_traffic?.percentage_change !==
                                  undefined && (
                                  <span
                                    className={`fw-bold ${analyticsData.ai_traffic.percentage_change >= 0 ? "text-success" : "text-danger"}`}
                                    style={{ fontSize: "12px" }}
                                  >
                                    {analyticsData.ai_traffic
                                      .percentage_change >= 0
                                      ? "+"
                                      : ""}
                                    {analyticsData.ai_traffic.percentage_change}
                                    %
                                  </span>
                                )}
                              </div>
                              <span
                                className="text-muted"
                                style={{ fontSize: "11px" }}
                              >
                                {analyticsData.ai_traffic?.comparison_label ||
                                  `vs previous ${gaPeriodDays} days`}
                              </span>
                            </div>

                            {/* Chart for AI Traffic */}
                            <div
                              className="trend-chart-container"
                              style={{ height: "140px", marginTop: "15px" }}
                            >
                              <div className="chart-main-area">
                                <div
                                  className="chart-y-axis-html"
                                  style={{ width: "32px" }}
                                >
                                  <span className="chart-y-label-item">
                                    {aiMaxVal.toLocaleString()}
                                  </span>
                                  <span className="chart-y-label-item">
                                    {Math.round(aiMaxVal / 2).toLocaleString()}
                                  </span>
                                  <span className="chart-y-label-item">0</span>
                                </div>

                                <div className="chart-plot-area">
                                  <svg
                                    className="chart-svg-layer"
                                    viewBox="0 0 1000 100"
                                    preserveAspectRatio="none"
                                  >
                                    <defs>
                                      <linearGradient
                                        id="aiChartGrad"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        <stop
                                          offset="0%"
                                          stopColor="#8b5cf6"
                                          stopOpacity="0.25"
                                        />
                                        <stop
                                          offset="100%"
                                          stopColor="#8b5cf6"
                                          stopOpacity="0.0"
                                        />
                                      </linearGradient>
                                    </defs>

                                    {/* Horizontal Grid lines */}
                                    <line
                                      x1="0"
                                      y1="8"
                                      x2="1000"
                                      y2="8"
                                      className="chart-grid-line"
                                      vectorEffect="non-scaling-stroke"
                                    />
                                    <line
                                      x1="0"
                                      y1="50"
                                      x2="1000"
                                      y2="50"
                                      className="chart-grid-line"
                                      vectorEffect="non-scaling-stroke"
                                    />
                                    <line
                                      x1="0"
                                      y1="92"
                                      x2="1000"
                                      y2="92"
                                      className="chart-grid-line axis-bottom"
                                      vectorEffect="non-scaling-stroke"
                                    />

                                    {/* Filled Area */}
                                    {aiDailyPoints.length >= 2 && (
                                      <path
                                        d={getAIFillPathD()}
                                        fill="url(#aiChartGrad)"
                                      />
                                    )}

                                    {/* Line Path */}
                                    {aiDailyPoints.length >= 2 && (
                                      <path
                                        d={getAIPathD()}
                                        fill="none"
                                        stroke="#8b5cf6"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                      />
                                    )}
                                  </svg>

                                  {/* Data dot for final point */}
                                  {aiDailyPoints.length > 0 && (
                                    <div
                                      className="chart-data-dot"
                                      style={{
                                        left: "100%",
                                        top: `${92 - ((aiDailyPoints[aiDailyPoints.length - 1].sessions || 0) * 84) / aiMaxVal}%`,
                                        backgroundColor: "#8b5cf6",
                                        border: "2px solid #ffffff",
                                        boxShadow:
                                          "0 0 8px rgba(139, 92, 246, 0.6)",
                                      }}
                                    />
                                  )}
                                </div>
                              </div>

                              {/* X-axis date labels */}
                              {aiDailyPoints.length > 0 &&
                                (() => {
                                  const getXDateLabel = (idx) => {
                                    const dp = aiDailyPoints[idx];
                                    if (!dp) return "";
                                    const dateParts = dp.date.split("-");
                                    return dateParts.length === 3
                                      ? new Date(
                                          dateParts[0],
                                          dateParts[1] - 1,
                                          dateParts[2],
                                        ).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                        })
                                      : dp.date;
                                  };
                                  return (
                                    <div
                                      className="chart-x-axis-html"
                                      style={{ marginLeft: "32px" }}
                                    >
                                      <span className="chart-x-label-item">
                                        {getXDateLabel(0)}
                                      </span>
                                      <span className="chart-x-label-item">
                                        {getXDateLabel(
                                          Math.floor(
                                            (aiDailyPoints.length - 1) / 2,
                                          ),
                                        )}
                                      </span>
                                      <span className="chart-x-label-item">
                                        {getXDateLabel(
                                          aiDailyPoints.length - 1,
                                        )}
                                      </span>
                                    </div>
                                  );
                                })()}
                            </div>
                          </div>
                        </div>

                        {/* 3. GA Traffic Breakdowns Grid */}
                        <div className="ga-breakdowns-grid mt-4">
                          {/* Card 1: Active Users by Country */}
                          <div className="dashboard-card breakdown-card shadow-sm border border-light">
                            <div className="card-header-row mb-3">
                              <h4
                                className="card-title-dashboard"
                                style={{
                                  fontSize: "13px",
                                  margin: 0,
                                  fontWeight: 700,
                                }}
                              >
                                Active users by Country
                              </h4>
                            </div>
                            {breakdownLoading ? (
                              <div className="d-flex justify-content-center py-4">
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  variant="secondary"
                                />
                              </div>
                            ) : !breakdownData?.countries ||
                              breakdownData.countries.length === 0 ? (
                              <div
                                className="text-center py-4 text-muted"
                                style={{ fontSize: "12px" }}
                              >
                                No country data available.
                              </div>
                            ) : (
                              <div className="breakdown-list-container">
                                <table className="breakdown-table mb-0">
                                  <thead>
                                    <tr>
                                      <th>COUNTRY</th>
                                      <th className="text-center">
                                        ACTIVE USERS
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {breakdownData.countries.map((c, idx) => {
                                      const maxVal =
                                        breakdownData.countries[0]?.value || 1;
                                      const pct = Math.min(
                                        (c.value / maxVal) * 100,
                                        100,
                                      );
                                      return (
                                        <tr key={`country-${idx}`}>
                                          <td
                                            style={{
                                              position: "relative",
                                              overflow: "hidden",
                                            }}
                                          >
                                            <div
                                              className="breakdown-bar"
                                              style={{ width: `${pct}%` }}
                                            />
                                            <span
                                              className="breakdown-name"
                                              style={{
                                                position: "relative",
                                                zIndex: 2,
                                              }}
                                            >
                                              {c.name}
                                            </span>
                                          </td>
                                          <td
                                            className="text-end fw-semibold text-dark-console"
                                            style={{
                                              position: "relative",
                                              zIndex: 2,
                                            }}
                                          >
                                            {c.value.toLocaleString()}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>

                          {/* Card 2: Views by Page Title  */}
                          <div className="dashboard-card breakdown-card shadow-sm border border-light">
                            <div className="card-header-row mb-3">
                              <h4
                                className="card-title-dashboard"
                                style={{
                                  fontSize: "13px",
                                  margin: 0,
                                  fontWeight: 700,
                                }}
                              >
                                Views by Page title
                              </h4>
                            </div>
                            {breakdownLoading ? (
                              <div className="d-flex justify-content-center py-4">
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  variant="secondary"
                                />
                              </div>
                            ) : !breakdownData?.pages ||
                              breakdownData.pages.length === 0 ? (
                              <div
                                className="text-center py-4 text-muted"
                                style={{ fontSize: "12px" }}
                              >
                                No page view data available.
                              </div>
                            ) : (
                              <div className="breakdown-list-container">
                                <table className="breakdown-table mb-0">
                                  <thead>
                                    <tr>
                                      <th>PAGE TITLE</th>
                                      <th className="text-center">VIEWS</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {breakdownData.pages.map((p, idx) => {
                                      const maxVal =
                                        breakdownData.pages[0]?.value || 1;
                                      const pct = Math.min(
                                        (p.value / maxVal) * 100,
                                        100,
                                      );
                                      return (
                                        <tr key={`page-${idx}`}>
                                          <td
                                            style={{
                                              position: "relative",
                                              overflow: "hidden",
                                            }}
                                          >
                                            <div
                                              className="breakdown-bar"
                                              style={{ width: `${pct}%` }}
                                            />
                                            <span
                                              className="breakdown-name"
                                              title={p.name}
                                              style={{
                                                position: "relative",
                                                zIndex: 2,
                                                display: "inline-block",
                                                maxWidth: "170px",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {p.name}
                                            </span>
                                          </td>
                                          <td
                                            className="text-end fw-semibold text-dark-console"
                                            style={{
                                              position: "relative",
                                              zIndex: 2,
                                            }}
                                          >
                                            {p.value.toLocaleString()}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>

                          {/* Card 3: Top AI Agents */}
                          <div className="dashboard-card breakdown-card shadow-sm border border-light">
                            <div className="card-header-row mb-3">
                              <h4
                                className="card-title-dashboard"
                                style={{
                                  fontSize: "13px",
                                  margin: 0,
                                  fontWeight: 700,
                                }}
                              >
                                Top AI Agents
                              </h4>
                            </div>
                            {analyticsLoading ? (
                              <div className="d-flex justify-content-center py-4">
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  variant="secondary"
                                />
                              </div>
                            ) : !agentData || agentData.length === 0 ? (
                              <div
                                className="text-center py-4 text-muted"
                                style={{ fontSize: "12px" }}
                              >
                                No AI agent traffic detected.
                              </div>
                            ) : (
                              <div className="breakdown-list-container">
                                <table className="breakdown-table mb-0">
                                  <thead>
                                    <tr>
                                      <th>AGENT / SOURCE</th>
                                      <th className="text-end">SESSIONS</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {agentData.map((agent, idx) => {
                                      const maxVal = agentData[0]?.value || 1;
                                      const pct =
                                        maxVal > 0
                                          ? Math.min(
                                              (agent.value / maxVal) * 100,
                                              100,
                                            )
                                          : 0;
                                      return (
                                        <tr key={`agent-${idx}`}>
                                          <td
                                            style={{
                                              position: "relative",
                                              overflow: "hidden",
                                            }}
                                          >
                                            <div
                                              className="breakdown-bar agent-bar"
                                              style={{ width: `${pct}%` }}
                                            />
                                            <span
                                              className="breakdown-name"
                                              style={{
                                                position: "relative",
                                                zIndex: 2,
                                              }}
                                            >
                                              {agent.name}
                                            </span>
                                          </td>
                                          <td
                                            className="text-end fw-semibold text-dark-console"
                                            style={{
                                              position: "relative",
                                              zIndex: 2,
                                            }}
                                          >
                                            {agent.value.toLocaleString()}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {showGSC &&
                      (!gscData ? (
                        <div className="dashboard-card p-5 text-center shadow-sm border border-light">
                          <div className="py-4">
                            <div
                              className="mb-3 text-muted"
                              style={{ fontSize: "32px" }}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                width="48"
                                height="48"
                                fill="currentColor"
                                style={{ opacity: 0.5 }}
                              >
                                <path
                                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                                  fill="#94a3b8"
                                />
                              </svg>
                            </div>
                            <h5 className="fw-bold text-dark-console mb-2">
                              No Search Console Data Available
                            </h5>
                            <p
                              className="text-muted mx-auto mb-4"
                              style={{
                                maxWidth: "420px",
                                fontSize: "14px",
                                lineHeight: "1.5",
                              }}
                            >
                              We couldn't fetch Google Search Console data for{" "}
                              <strong>
                                {activeProfile?.gsc_site_url || gscSiteUrl}
                              </strong>
                              . Please ensure the connected Google account has
                              access to this verified property, or connect your
                              account again.
                            </p>
                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                              <button
                                className="btn btn-outline-primary fw-semibold px-4 py-2"
                                style={{
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                }}
                                onClick={() => fetchDashboardData()}
                              >
                                Retry Loading
                              </button>
                              <button
                                className="btn btn-primary fw-bold px-4 py-2 d-inline-flex align-items-center gap-2"
                                style={{
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                }}
                                onClick={handleConnectGoogle}
                              >
                                <svg
                                  viewBox="0 0 24 24"
                                  width="14"
                                  height="14"
                                  fill="currentColor"
                                >
                                  <path
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                    fill="#FFFFFF"
                                  />
                                  <path
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                    fill="#FFFFFF"
                                  />
                                  <path
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                                    fill="#FFFFFF"
                                  />
                                  <path
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                                    fill="#FFFFFF"
                                  />
                                </svg>
                                Re-connect Google Account
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* 1. GSC Summary Metric Cards */}
                          <div
                            className="ga-summary-grid mb-4"
                            style={{
                              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                            }}
                          >
                            {/* Card 1: Clicks */}
                            <div className="dashboard-card ga-metric-card shadow-sm border border-light">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span
                                  className="text-secondary fw-semibold"
                                  style={{
                                    fontSize: "12px",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  TOTAL CLICKS
                                </span>
                                <span
                                  className="badge bg-primary-subtle text-primary rounded-pill px-2 py-0.5"
                                  style={{ fontSize: "10px" }}
                                >
                                  {gscPeriodDays
                                    ? `${gscPeriodDays} Days`
                                    : "30 Days"}
                                </span>
                              </div>
                              <div className="d-flex align-items-baseline gap-2">
                                <span className="fs-3 fw-extrabold text-dark-console">
                                  {gscData.totals.clicks.toLocaleString()}
                                </span>
                              </div>
                              <p
                                className="text-muted mb-0 mt-2"
                                style={{ fontSize: "11px" }}
                              >
                                Total click-throughs from Google organic
                                results.
                              </p>
                            </div>

                            {/* Card 2: Impressions */}
                            <div className="dashboard-card ga-metric-card shadow-sm border border-light">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span
                                  className="text-secondary fw-semibold"
                                  style={{
                                    fontSize: "12px",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  TOTAL IMPRESSIONS
                                </span>
                                <span
                                  className="badge bg-info-subtle text-info rounded-pill px-2 py-0.5"
                                  style={{ fontSize: "10px" }}
                                >
                                  {gscPeriodDays
                                    ? `${gscPeriodDays} Days`
                                    : "30 Days"}
                                </span>
                              </div>
                              <div className="d-flex align-items-baseline gap-2">
                                <span className="fs-3 fw-extrabold text-dark-console">
                                  {gscData.totals.impressions.toLocaleString()}
                                </span>
                              </div>
                              <p
                                className="text-muted mb-0 mt-2"
                                style={{ fontSize: "11px" }}
                              >
                                Total times site was viewed in Google searches.
                              </p>
                            </div>

                            {/* Card 3: Avg CTR */}
                            <div className="dashboard-card ga-metric-card shadow-sm border border-light">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span
                                  className="text-secondary fw-semibold"
                                  style={{
                                    fontSize: "12px",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  AVERAGE CTR
                                </span>
                                <span
                                  className="badge bg-success-subtle text-success rounded-pill px-2 py-0.5"
                                  style={{ fontSize: "10px" }}
                                >
                                  {gscPeriodDays
                                    ? `${gscPeriodDays} Days`
                                    : "30 Days"}
                                </span>
                              </div>
                              <div className="d-flex align-items-baseline gap-2">
                                <span className="fs-3 fw-extrabold text-dark-console">
                                  {typeof gscData.totals?.ctr === "number"
                                    ? `${gscData.totals.ctr.toFixed(1)}%`
                                    : (gscData.totals?.ctr ? `${gscData.totals.ctr}%` : "0.0%")}
                                </span>
                              </div>
                              <p
                                className="text-muted mb-0 mt-2"
                                style={{ fontSize: "11px" }}
                              >
                                Percentage of views that clicked the link.
                              </p>
                            </div>

                            {/* Card 4: Avg Position */}
                            <div className="dashboard-card ga-metric-card shadow-sm border border-light">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span
                                  className="text-secondary fw-semibold"
                                  style={{
                                    fontSize: "12px",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  AVERAGE POSITION
                                </span>
                                <span
                                  className="badge bg-warning-subtle text-warning rounded-pill px-2 py-0.5"
                                  style={{ fontSize: "10px" }}
                                >
                                  {gscPeriodDays
                                    ? `${gscPeriodDays} Days`
                                    : "30 Days"}
                                </span>
                              </div>
                              <div className="d-flex align-items-baseline gap-2">
                                <span className="fs-3 fw-extrabold text-dark-console">
                                  {gscData.totals.position}
                                </span>
                              </div>
                              <p
                                className="text-muted mb-0 mt-2"
                                style={{ fontSize: "11px" }}
                              >
                                Average ranking index across google search
                                results.
                              </p>
                            </div>
                          </div>

                          <div className="ga-charts-row">
                            {/* 2. GSC Clicks & Impressions Trend */}
                            <div
                              className="dashboard-card trend-card ga-main-trend-card"
                              style={{ flex: "1 1 100%" }}
                            >
                              <div className="card-header-row align-items-center mb-4">
                                <div className="card-title-wrap">
                                  <h3
                                    className="card-title-dashboard"
                                    style={{ fontSize: "14px" }}
                                  >
                                    Search Console Clicks & Impressions Trend
                                  </h3>
                                </div>

                                <div className="trend-controls flex-wrap gap-3">
                                  <div className="trend-legend-item">
                                    <span
                                      className="legend-dot"
                                      style={{ backgroundColor: "#6366f1" }}
                                    />
                                    <span className="legend-label">Clicks</span>
                                  </div>
                                  <div className="trend-legend-item">
                                    <span
                                      className="legend-dot"
                                      style={{ backgroundColor: "#fb7185" }}
                                    />
                                    <span className="legend-label">
                                      Impressions
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Dynamic Line Chart */}
                              <div className="trend-chart-container">
                                <div className="chart-main-area">
                                  {/* HTML Y-Axis */}
                                  <div className="chart-y-axis-html">
                                    <span className="chart-y-label-item">
                                      {gscMaxImpressions.toLocaleString()}
                                    </span>
                                    <span className="chart-y-label-item">
                                      {Math.round(
                                        gscMaxImpressions / 2,
                                      ).toLocaleString()}
                                    </span>
                                    <span className="chart-y-label-item">
                                      0
                                    </span>
                                  </div>

                                  {/* Plot Area */}
                                  <div className="chart-plot-area">
                                    <svg
                                      className="chart-svg-layer"
                                      viewBox="0 0 1000 100"
                                      preserveAspectRatio="none"
                                    >
                                      {/* Horizontal Grid lines */}
                                      <line
                                        x1="0"
                                        y1="8"
                                        x2="1000"
                                        y2="8"
                                        className="chart-grid-line"
                                        vectorEffect="non-scaling-stroke"
                                      />
                                      <line
                                        x1="0"
                                        y1="50"
                                        x2="1000"
                                        y2="50"
                                        className="chart-grid-line"
                                        vectorEffect="non-scaling-stroke"
                                      />
                                      <line
                                        x1="0"
                                        y1="92"
                                        x2="1000"
                                        y2="92"
                                        className="chart-grid-line axis-bottom"
                                        vectorEffect="non-scaling-stroke"
                                      />

                                      {/* Clicks Path (Indigo) */}
                                      {gscDailyPoints.length >= 2 && (
                                        <path
                                          d={getGSCPathD(
                                            "clicks",
                                            gscMaxClicks,
                                          )}
                                          fill="none"
                                          stroke="#6366f1"
                                          strokeWidth="2.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          vectorEffect="non-scaling-stroke"
                                        />
                                      )}

                                      {/* Impressions Path (Rose/Pink) */}
                                      {gscDailyPoints.length >= 2 && (
                                        <path
                                          d={getGSCPathD(
                                            "impressions",
                                            gscMaxImpressions,
                                          )}
                                          fill="none"
                                          stroke="#fb7185"
                                          strokeWidth="2.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          vectorEffect="non-scaling-stroke"
                                        />
                                      )}
                                    </svg>

                                    {/* Data dots for final points */}
                                    {gscDailyPoints.length > 0 && (
                                      <>
                                        <div
                                          className="chart-data-dot"
                                          style={{
                                            left: "100%",
                                            top: `${92 - ((gscDailyPoints[gscDailyPoints.length - 1].clicks || 0) * 84) / gscMaxClicks}%`,
                                            backgroundColor: "#6366f1",
                                            border: "2px solid #ffffff",
                                            boxShadow:
                                              "0 0 8px rgba(99, 102, 241, 0.6)",
                                          }}
                                        />
                                        <div
                                          className="chart-data-dot"
                                          style={{
                                            left: "100%",
                                            top: `${92 - ((gscDailyPoints[gscDailyPoints.length - 1].impressions || 0) * 84) / gscMaxImpressions}%`,
                                            backgroundColor: "#fb7185",
                                            border: "2px solid #ffffff",
                                            boxShadow:
                                              "0 0 8px rgba(251, 113, 133, 0.6)",
                                          }}
                                        />
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* HTML X-Axis Date Labels */}
                                <div className="chart-x-axis-html">
                                  {gscDailyPoints.map((dp, idx) => {
                                    if (
                                      idx % 5 !== 0 &&
                                      idx !== gscDailyPoints.length - 1
                                    )
                                      return null;
                                    const dateParts = dp.date.split("-");
                                    const dateStr =
                                      dateParts.length === 3
                                        ? new Date(
                                            dateParts[0],
                                            dateParts[1] - 1,
                                            dateParts[2],
                                          ).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                          })
                                        : dp.date;
                                    return (
                                      <span
                                        key={`gsc-xlabel-${idx}`}
                                        className="chart-x-label-item"
                                      >
                                        {dateStr}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 3. GSC Breakdowns Grid */}
                          <div
                            className="ga-breakdowns-grid mt-4"
                            style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
                          >
                            {/* Card 1: Top Search Queries */}
                            <div className="dashboard-card breakdown-card shadow-sm border border-light">
                              <div className="card-header-row mb-3">
                                <h4
                                  className="card-title-dashboard"
                                  style={{
                                    fontSize: "13px",
                                    margin: 0,
                                    fontWeight: 700,
                                  }}
                                >
                                  Top Search Queries (Keywords)
                                </h4>
                              </div>
                              {!gscData?.queries ||
                              gscData.queries.length === 0 ? (
                                <div
                                  className="text-center py-4 text-muted"
                                  style={{ fontSize: "12px" }}
                                >
                                  No search queries available.
                                </div>
                              ) : (
                                <div className="breakdown-list-container">
                                  <table className="breakdown-table mb-0">
                                    <thead>
                                      <tr>
                                        <th>QUERY</th>
                                        <th className="text-end">CLICKS</th>
                                        <th className="text-end">
                                          IMPRESSIONS
                                        </th>
                                        <th className="text-end">AVG POS</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {gscData.queries.map((q, idx) => {
                                        const maxVal =
                                          gscData.queries[0]?.clicks || 1;
                                        const pct = Math.min(
                                          (q.clicks / maxVal) * 100,
                                          100,
                                        );
                                        return (
                                          <tr key={`query-${idx}`}>
                                            <td
                                              style={{
                                                position: "relative",
                                                overflow: "hidden",
                                              }}
                                            >
                                              <div
                                                className="breakdown-bar"
                                                style={{ width: `${pct}%` }}
                                              />
                                              <span
                                                className="breakdown-name"
                                                style={{
                                                  position: "relative",
                                                  zIndex: 2,
                                                }}
                                              >
                                                {q.query}
                                              </span>
                                            </td>
                                            <td
                                              className="text-end fw-semibold text-dark-console"
                                              style={{
                                                position: "relative",
                                                zIndex: 2,
                                              }}
                                            >
                                              {q.clicks.toLocaleString()}
                                            </td>
                                            <td
                                              className="text-end text-muted"
                                              style={{
                                                position: "relative",
                                                zIndex: 2,
                                              }}
                                            >
                                              {q.impressions.toLocaleString()}
                                            </td>
                                            <td
                                              className="text-end fw-semibold text-primary"
                                              style={{
                                                position: "relative",
                                                zIndex: 2,
                                              }}
                                            >
                                              {q.position}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            {/* Card 2: Top Landing Pages */}
                            <div className="dashboard-card breakdown-card shadow-sm border border-light">
                              <div className="card-header-row mb-3">
                                <h4
                                  className="card-title-dashboard"
                                  style={{
                                    fontSize: "13px",
                                    margin: 0,
                                    fontWeight: 700,
                                  }}
                                >
                                  Top Landing Pages
                                </h4>
                              </div>
                              {!gscData?.pages || gscData.pages.length === 0 ? (
                                <div
                                  className="text-center py-4 text-muted"
                                  style={{ fontSize: "12px" }}
                                >
                                  No page data available.
                                </div>
                              ) : (
                                <div className="breakdown-list-container">
                                  <table className="breakdown-table mb-0">
                                    <thead>
                                      <tr>
                                        <th>PAGE PATH</th>
                                        <th className="text-end">CLICKS</th>
                                        <th className="text-end">
                                          IMPRESSIONS
                                        </th>
                                        <th className="text-end">AVG POS</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {gscData.pages.map((p, idx) => {
                                        const maxVal =
                                          gscData.pages[0]?.clicks || 1;
                                        const pct = Math.min(
                                          (p.clicks / maxVal) * 100,
                                          100,
                                        );
                                        return (
                                          <tr key={`gsc-page-${idx}`}>
                                            <td
                                              style={{
                                                position: "relative",
                                                overflow: "hidden",
                                              }}
                                            >
                                              <div
                                                className="breakdown-bar"
                                                style={{
                                                  width: `${pct}%`,
                                                  backgroundColor:
                                                    "rgba(251, 113, 133, 0.08)",
                                                }}
                                              />
                                              <span
                                                className="breakdown-name"
                                                title={p.page}
                                                style={{
                                                  position: "relative",
                                                  zIndex: 2,
                                                  display: "inline-block",
                                                  maxWidth: "240px",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                {p.page}
                                              </span>
                                            </td>
                                            <td
                                              className="text-end fw-semibold text-dark-console"
                                              style={{
                                                position: "relative",
                                                zIndex: 2,
                                              }}
                                            >
                                              {p.clicks.toLocaleString()}
                                            </td>
                                            <td
                                              className="text-end text-muted"
                                              style={{
                                                position: "relative",
                                                zIndex: 2,
                                              }}
                                            >
                                              {p.impressions.toLocaleString()}
                                            </td>
                                            <td
                                              className="text-end fw-semibold text-primary"
                                              style={{
                                                position: "relative",
                                                zIndex: 2,
                                              }}
                                            >
                                              {p.position}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      ))}
                  </>
                )}
              </div>
              {/* Premium Lock Overlay for Google Integrations */}
              {(userPlanKey === "core" ||
                userPlanKey === "guest" ||
                userPlanKey === "") && (
                <div
                  className="glass-premium-overlay d-flex flex-column align-items-center justify-content-center"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(23, 23, 23, 0.85)",
                    backdropFilter: "blur(6px)",
                    borderRadius: "12px",
                    zIndex: 10,
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  <div
                    className="premium-lock-icon-container mb-3"
                    style={{
                      width: "54px",
                      height: "54px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(59, 130, 246, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                    }}
                  >
                    <FiLock size={22} className="text-primary" />
                  </div>
                  <h4
                    className="fw-bold text-white mb-2"
                    style={{ fontSize: "18px" }}
                  >
                    Google Integrations (GA4 & GSC)
                  </h4>
                  <p
                    className="text-muted mb-4"
                    style={{
                      fontSize: "13px",
                      maxWidth: "420px",
                      lineHeight: "1.5",
                    }}
                  >
                    Connect Google Analytics 4 and Google Search Console to
                    overlay organic search metrics with your AI visibility
                    readiness. Available on the Growth plan.
                  </p>
                  <button
                    className="btn btn-primary fw-semibold px-4 py-2"
                    onClick={() => navigate("/pricing")}
                    style={{ borderRadius: "8px" }}
                  >
                    Upgrade to Growth
                  </button>
                </div>
              )}
            </section>
          </>
        )}
      </main>

      {/* ── Scan Detail Modal ── */}
      <Modal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-dark text-white">
          <Modal.Title>
            Scan Details: {selectedScan?.website || companyDomain}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#0b0e1e", color: "white" }}>
          {selectedScan && (
            <>
              <AIReadinessGauge
                score={selectedScan.scan_data?.score}
                tier={selectedScan.scan_data?.raw?.ai_readiness_tier}
              />

              <Row className="mt-4">
                <Col md={6}>
                  <h5 className="text-danger mb-3">Why this score?</h5>
                  <ListGroup variant="flush">
                    {selectedScan.scan_data?.reasons?.map((reason, idx) => (
                      <ListGroup.Item
                        key={`reason-${idx}`}
                        style={{
                          background: "rgba(239, 68, 68, 0.1)",
                          color: "#fca5a5",
                        }}
                      >
                        {typeof reason === "string" ? (
                          reason
                        ) : (
                          <>
                            <strong>{reason?.issue || "Issue"}</strong>
                            <br />
                            <span className="text-muted">{reason?.detail}</span>
                          </>
                        )}
                      </ListGroup.Item>
                    )) || (
                      <ListGroup.Item>
                        No explicit issues logged.
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Col>

                <Col md={6}>
                  <h5 className="text-success mb-3">
                    Recommended Improvements
                  </h5>
                  <ListGroup variant="flush">
                    {(
                      selectedScan.scan_data?.reasons ||
                      selectedScan.scan_data?.improvements
                    )?.map((imp, idx) => {
                      const text =
                        typeof imp === "string"
                          ? imp
                          : imp.fix || imp.reason || "";
                      if (!text) return null;
                      return (
                        <ListGroup.Item
                          key={`imp-${idx}`}
                          style={{
                            background: "rgba(16, 185, 129, 0.1)",
                            color: "#10b981",
                          }}
                        >
                          {text}
                        </ListGroup.Item>
                      );
                    }) || (
                      <ListGroup.Item>
                        No improvements recommended.
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: "#11162d", borderColor: "#1e293b" }}>
          <Button variant="secondary" onClick={() => setShowDetails(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Custom Confirmation Modal ── */}
      <Modal
        show={confirmModal.show}
        onHide={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
        dialogClassName="top-confirm-modal"
        centered
      >
        <Modal.Header closeButton>
          <div className="confirm-modal-header-content">
            <div
              className={`confirm-modal-icon-badge ${
                confirmModal.confirmVariant === "danger"
                  ? "badge-danger"
                  : "badge-primary"
              }`}
            >
              <FiAlertTriangle size={20} />
            </div>
            <Modal.Title className="confirm-modal-title">
              {confirmModal.title}
            </Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="confirm-modal-body">
          <p className="confirm-modal-message">{confirmModal.message}</p>
        </Modal.Body>
        <Modal.Footer className="confirm-modal-footer">
          <button
            type="button"
            className="btn-cancel"
            onClick={() =>
              setConfirmModal((prev) => ({ ...prev, show: false }))
            }
          >
            Cancel
          </button>
          <button
            type="button"
            className={
              confirmModal.confirmVariant === "danger"
                ? "btn-confirm-danger"
                : "btn-confirm-primary"
            }
            onClick={() => {
              if (confirmModal.onConfirm) confirmModal.onConfirm();
              setConfirmModal((prev) => ({ ...prev, show: false }));
            }}
          >
            {confirmModal.confirmText}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
